import React, { useState, useEffect } from "react";
import {
  Container,
  Button,
  Modal,
  Accordion,
  Form,
  Table,
  Row,
  Col,
  Nav,
  Tab,
  Dropdown,
  CardHeader,
  Card,
  CardBody,
  FloatingLabel,
  Spinner,
  Alert

} from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import Navbar from "../Navbar";
import NavigationWarningModal from '../components/NavigationWarningModal';
import axios from "axios";
import moment from "moment";
import "bootstrap-icons/font/bootstrap-icons.css";
import { API_ENDPOINTS } from '../config/api';
import ProgramPGYUtils from '../utils/programPGYUtils';
import { 
  SPECIALTY_ROTATION_TYPES, 
  DEFAULT_ROTATION_TYPES, 
  SPECIALTY_ROTATION_COLORS, 
  DEFAULT_ROTATION_COLORS,
  getRotationTypesForSpecialty,
  getRotationLabel
} from '../utils/rotationTypesConfig';

// Import SPECIALTY_ROTATION_COLORS for direct access
import { SPECIALTY_ROTATION_COLORS as SPECIALTY_COLORS } from '../utils/rotationTypesConfig';
import PageTitle from '../components/PageTitle';
import DailyScheduleTab from './components/DailyScheduleTab';
import WeekendCallTab from './components/WeekendCallTab';
import RotationScheduleTab from './components/RotationScheduleTab';
// Import schedule generators
import { generateMonthlySchedule as generateMonthlyScheduleUtil } from '../utils/scheduleGenerators/monthlyScheduleGenerator';
import { generateWeeklySchedule as generateWeeklyScheduleUtil } from '../utils/scheduleGenerators/weeklyScheduleGenerator';
import { generateBlockSchedule as generateBlockScheduleUtil } from '../utils/scheduleGenerators/blockScheduleGenerator';
// Import date helpers
import {
  getMonthsBetween,
  getWeeksBetween,
  getWeeklyGroups,
  getWeekStartForDate,
  getGroupWeekStarts,
  getAllMonths,
  getWeekendsForMonth,
  getAllDates
} from '../utils/scheduleGenerators/dateHelpers';
// Import weekend schedule generator
import { 
  generateAndStoreWeekendSchedules as generateAndStoreWeekendSchedulesUtil,
  buildBlockRotationRanges,
  weekendFallsWithinBlockRange
} from '../utils/weekendSchedule/weekendScheduleGenerator';
// Import daily schedule helpers
import { getResidentsForDate as getResidentsForDateHelper } from '../utils/dailySchedule/dailyScheduleHelpers';
// Import print utility
import {
  buildPrintDocument as buildPrintDocumentUtil,
  openPrintWindow,
  buildPrintHeader,
  PRINT_STYLES as SHARED_PRINT_STYLES
} from '../utils/printUtility';

// Helper function to get current academic year
const getCurrentAcademicYear = () => {
  const now = new Date();
  const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    year,
    start: `${year}-07-01`,
    end: `${year + 1}-06-30`,
    display: `${year}-${year + 1}`
  };
};

// Helper function to get academic year for a specific year
const getAcademicYearForYear = (year) => {
  return {
    year: parseInt(year),
    start: `${year}-07-01`,
    end: `${parseInt(year) + 1}-06-30`,
    display: `${year}-${parseInt(year) + 1}`
  };
};

// Date helper functions are now imported from '../utils/scheduleGenerators/dateHelpers'

const RotationSchedulerPage = (props) => {
  const createScheduleConfig = (id = Date.now()) => ({
    id: id.toString(),
    pgyLevel: "",
    selectedRotations: [],
    rotationFrequency: {},
    recurrentRotations: [],
    blockRotations: {},
    blockStep: 1,
  });

  const navigate = useNavigate();
  const [residents, setResidents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [scheduleType, setScheduleType] = useState("monthly");
  const [activeTab, setActiveTab] = useState(() => {
    if (props && props.initialTab) {
      return props.initialTab;
    }
    return localStorage.getItem("rotationSchedulerTab") || "rotations";
  });
  const [schedules, setSchedules] = useState({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");
  const [weekendCallSchedules, setWeekendCallSchedules] = useState({});
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isEditMode, setIsEditMode] = useState(false);
  const [draggedResident, setDraggedResident] = useState(null);
  const [editedWeekendSchedules, setEditedWeekendSchedules] = useState({});
  const [recentlyMovedResident, setRecentlyMovedResident] = useState(null);
  const [movedResidents, setMovedResidents] = useState(new Map());
  const [affectedCells, setAffectedCells] = useState(new Set());
  const [originalWeekendSchedules, setOriginalWeekendSchedules] = useState({});
  const [showWeekendGenModal, setShowWeekendGenModal] = useState(false);
  const [weekendGenMode, setWeekendGenMode] = useState('one-day-off'); // 'one-day-off' | 'gold-weekend'
  const [excludedRotationsForWeekend, setExcludedRotationsForWeekend] = useState([]);
  const [allowRotationMovement, setAllowRotationMovement] = useState(false);
  const [currentMonthUnassignedVersion, setCurrentMonthUnassignedVersion] = useState(0);
  // Map: `${rotation}-${monthStr}` -> { [residentId]: resident }
  const [manuallyUnassignedByRotationMonth, setManuallyUnassignedByRotationMonth] = useState({});
  // Confirmation modal state for clearing weekend calls
  const [showWeekendClearConfirmModal, setShowWeekendClearConfirmModal] = useState(false);
  const [clearConfirmData, setClearConfirmData] = useState(null); // { type: 'day' | 'month', rotation, date?: string }
  // Navigation warning state
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Vacation request state
  const [approvedVacationRequests, setApprovedVacationRequests] = useState([]);
  const [fetchingVacationRequests, setFetchingVacationRequests] = useState(false);

  // New state for academic year functionality
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(getCurrentAcademicYear().year);
  // Viewing filter for academic year (affects which schedules are fetched/displayed)
  const [viewAcademicYear, setViewAcademicYear] = useState(getCurrentAcademicYear().year);
  const [weekDuration, setWeekDuration] = useState(4);
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);

  // Block schedule configuration
  const [blockDuration, setBlockDuration] = useState(5); // weeks per block
  const [blockPattern, setBlockPattern] = useState("4-1"); // e.g., "4-1" means 4 weeks major + 1 week minor
  const initialScheduleConfig = createScheduleConfig();
  const [scheduleConfigs, setScheduleConfigs] = useState([initialScheduleConfig]);
  const [activeScheduleConfigId, setActiveScheduleConfigId] = useState(initialScheduleConfig.id);
  const [blockPageIndices, setBlockPageIndices] = useState({}); // { pgyLevel: currentPageIndex } for pagination (4 blocks per page)
  const [isBlockViewExpanded, setIsBlockViewExpanded] = useState(false); // Expanded view to show all blocks at once

  // New state for rotation editing functionality
  const [isRotationEditMode, setIsRotationEditMode] = useState(false);
  const [editedRotationSchedules, setEditedRotationSchedules] = useState({});
  const [originalRotationSchedules, setOriginalRotationSchedules] = useState({});
  const [draggedRotation, setDraggedRotation] = useState(null);
  const [movedRotations, setMovedRotations] = useState(new Map());
  const [affectedRotationCells, setAffectedRotationCells] = useState(new Set());
  const [loading, setLoading] = useState(true);
  
  // PGY level configuration
  const [pgyLevelOptions, setPgyLevelOptions] = useState([]);
  const [maxPGYLevel, setMaxPGYLevel] = useState(5);
  const [programDetails, setProgramDetails] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);
  
  // Specialty-specific rotation types
  const [rotationTypes, setRotationTypes] = useState(DEFAULT_ROTATION_TYPES);
  const [rotationColors, setRotationColors] = useState(DEFAULT_ROTATION_COLORS);
  
  // Custom rotation management
  const [customRotations, setCustomRotations] = useState([]);
  const [newCustomRotation, setNewCustomRotation] = useState("");
  const [showCustomRotationInput, setShowCustomRotationInput] = useState(false);
  const [isCustomRotationEditMode, setIsCustomRotationEditMode] = useState(false);
  const [customRotationLoading, setCustomRotationLoading] = useState(false);

  // Pager number management
  const [showPagerModal, setShowPagerModal] = useState(false);
  const [pagerNumbers, setPagerNumbers] = useState({});
  const [pagerModalLoading, setPagerModalLoading] = useState(false);
  const [profilePicUrls, setProfilePicUrls] = useState({});
  const [residentPagerNumbers, setResidentPagerNumbers] = useState({});
  const [generatingSchedule, setGeneratingSchedule] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Off-service residents state
  const [offServiceResidents, setOffServiceResidents] = useState([]);
  const [showOffServiceModal, setShowOffServiceModal] = useState(false);
  const [selectedPgyForOffService, setSelectedPgyForOffService] = useState(null);
  const [offServiceEntries, setOffServiceEntries] = useState([{ residentName: '', month: '', rotationName: '', weekBlock: '', blockIndex: '', rotations: { major: '', minor: '' } }]);
  const [editingOffServiceResident, setEditingOffServiceResident] = useState(null);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    // Auto-clear after 4 seconds
    window.clearTimeout(showSuccess._t);
    showSuccess._t = window.setTimeout(() => setSuccessMessage(""), 4000);
  };

  const activeScheduleConfig =
    scheduleConfigs.find((cfg) => cfg.id === activeScheduleConfigId) || scheduleConfigs[0];

  const selectedPGY = activeScheduleConfig?.pgyLevel || "";
  const selectedRotations = activeScheduleConfig?.selectedRotations || [];
  const rotationFrequency = activeScheduleConfig?.rotationFrequency || {};
  const recurrentRotations = activeScheduleConfig?.recurrentRotations || [];
  const blockRotations = activeScheduleConfig?.blockRotations || {};
  const blockStep = activeScheduleConfig?.blockStep || 1;

  const updateActiveScheduleConfig = (updater) => {
    const currentId = activeScheduleConfig?.id;
    if (!currentId) return;
    setScheduleConfigs((prev) =>
      prev.map((cfg) => {
        if (cfg.id !== currentId) return cfg;
        return typeof updater === "function" ? updater(cfg) : { ...cfg, ...updater };
      })
    );
  };

  const setSelectedPGY = (value) => updateActiveScheduleConfig({ pgyLevel: value });
  const setSelectedRotations = (updater) =>
    updateActiveScheduleConfig((cfg) => ({
      ...cfg,
      selectedRotations:
        typeof updater === "function" ? updater(cfg.selectedRotations || []) : updater,
    }));
  const setRotationFrequency = (updater) =>
    updateActiveScheduleConfig((cfg) => ({
      ...cfg,
      rotationFrequency:
        typeof updater === "function" ? updater(cfg.rotationFrequency || {}) : updater,
    }));
  const setRecurrentRotations = (updater) =>
    updateActiveScheduleConfig((cfg) => ({
      ...cfg,
      recurrentRotations:
        typeof updater === "function" ? updater(cfg.recurrentRotations || []) : updater,
    }));
  const setBlockRotations = (updater) =>
    updateActiveScheduleConfig((cfg) => ({
      ...cfg,
      blockRotations:
        typeof updater === "function" ? updater(cfg.blockRotations || {}) : updater,
    }));
  const setBlockStep = (value) =>
    updateActiveScheduleConfig({ blockStep: typeof value === "number" ? value : 1 });

  const handleAddScheduleLevel = () => {
    if (scheduleConfigs.length >= maxPGYLevel) {
      return;
    }
    const nextId = Date.now().toString();
    const newConfig = createScheduleConfig(nextId);
    setScheduleConfigs((prev) => [...prev, newConfig]);
    setActiveScheduleConfigId(nextId);
  };

  const handleRemoveScheduleLevel = (id) => {
    setScheduleConfigs((prev) => {
      const next = prev.filter((cfg) => cfg.id !== id);
      if (next.length === 0) {
        const replacement = createScheduleConfig();
        setActiveScheduleConfigId(replacement.id);
        return [replacement];
      }
      if (activeScheduleConfigId === id) {
        setActiveScheduleConfigId(next[0].id);
      }
      return next;
    });
  };

  const resetScheduleConfigState = () => {
    const resetConfig = createScheduleConfig();
    setScheduleConfigs([resetConfig]);
    setActiveScheduleConfigId(resetConfig.id);
  };

  // Function to get rotation color
  const getRotationColor = (rotation) => {
    if (!rotation) return 'transparent';
    
    // Try exact match first
    if (rotationColors[rotation]) {
      return rotationColors[rotation];
    }
    
    // Try case-insensitive match
    const rotationLower = rotation.toLowerCase();
    for (const [key, color] of Object.entries(rotationColors)) {
      if (key.toLowerCase() === rotationLower) {
        return color;
      }
    }
    
    // Try partial match
    for (const [key, color] of Object.entries(rotationColors)) {
      if (rotationLower.includes(key.toLowerCase()) || key.toLowerCase().includes(rotationLower)) {
        return color;
      }
    }
    
    // Debug: log if color not found (for any specialty)
    if (programDetails?.specialty) {
      console.warn(`Color not found for rotation: "${rotation}" (specialty: ${programDetails.specialty}). Available colors:`, Object.keys(rotationColors).slice(0, 10));
    }
    
    return '#6B7280';
  };

  // Function to abbreviate rotation names
  const abbreviateRotation = (rotation) => {
    if (!rotation) return '-';
    
    // Use the imported getRotationLabel function
    return getRotationLabel(rotation);
  };

  // Function to load profile picture with authentication
  const loadProfilePicture = async (profilePicPath) => {
    if (!profilePicPath) return null;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ENDPOINTS.PROFILE_PICS}${profilePicPath}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        console.warn('Failed to load profile picture:', response.status);
        return null;
      }
      
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error loading profile picture:', error);
      return null;
    }
  };

  // Function to render resident name with profile picture
  const renderResidentNameWithPic = (residentId, residentName, profilePic) => {
    // Create default avatar function similar to UsersPage
    const defaultAvatar = (name) => {
      const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
      return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' fill='%23e9ecef'/%3E%3Ctext x='16' y='20' font-family='Arial' font-size='14' fill='%236c757d' text-anchor='middle'%3E${initials}%3C/text%3E%3C/svg%3E`;
    };

    // Capitalize first letter of each name
    const capitalizeName = (name) => {
      return name.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };

    return (
      <div className="d-flex align-items-center gap-2">
        <img 
          src={profilePicUrls[residentId] || defaultAvatar(residentName)} 
          alt="avatar" 
          style={{width:32,height:32,borderRadius:'50%'}} 
        />
        <span>{capitalizeName(residentName)}</span>
      </div>
    );
  };



  // Helper function to capitalize names
  const capitalizeName = (name) => {
    return name.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };



  // Fetch program details and PGY levels
  const fetchProgramDetails = async () => {
    setProgramLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/program-details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Program details received:', data);
        setProgramDetails(data);
        
        // Don't overwrite rotation types here - they're set by fetchAllRotationTypes
        // which includes custom rotations. Only set colors if not already set.
        if (data.specialty) {
          const { colors } = getRotationTypesForSpecialty(data.specialty);
          console.log(`Loading rotation colors for specialty: "${data.specialty}"`, colors);
          console.log('Available specialty keys in SPECIALTY_ROTATION_COLORS:', Object.keys(SPECIALTY_ROTATION_COLORS || {}));
          if (colors && Object.keys(colors).length > 0) {
            setRotationColors(prev => {
              // Merge specialty colors, giving priority to specialty colors (specialty colors override defaults)
              const merged = { ...prev, ...colors };
              console.log(`Loading colors for specialty: "${data.specialty}"`);
              console.log('Previous colors:', prev);
              console.log('Specialty colors to add:', colors);
              console.log('Merged rotation colors (specialty colors override):', merged);
              // Verify some sample colors are present for any specialty
              const sampleRotations = Object.keys(colors).slice(0, 3);
              const sampleColors = {};
              sampleRotations.forEach(rot => {
                sampleColors[rot] = merged[rot];
              });
              console.log(`Sample colors for ${data.specialty}:`, sampleColors);
              return merged;
            });
          } else {
            console.warn(`No colors found for specialty: "${data.specialty}"`);
            console.warn('Available specialties:', Object.keys(SPECIALTY_ROTATION_COLORS || {}));
            console.warn('Falling back to default colors');
          }
        }
        
        // Fetch PGY levels for this program
        if (data.id) {
          console.log('Fetching PGY levels for programId:', data.id);
          await fetchPGYLevels(data.id);
        } else {
          console.log('No programId found in program details');
        }
      }
    } catch (err) {
      console.error("Failed to fetch program details", err);
    } finally {
      setProgramLoading(false);
    }
  };

  // Fetch all rotation types (standard + custom) from database
  const fetchAllRotationTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_ENDPOINTS.CUSTOM_ROTATION_TYPES}/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRotationTypes(response.data.rotationTypes);
      // Merge rotation colors instead of overwriting - preserve specialty colors
      // Only add custom rotation colors, don't overwrite specialty colors
      setRotationColors(prev => {
        const apiColors = response.data.rotationColors || {};
        // Preserve all existing colors (including specialty colors), only add new ones from API
        const merged = { ...prev };
        Object.keys(apiColors).forEach(key => {
          // Only add if it's a new color or if it's not a specialty color
          // This preserves specialty colors that were set earlier
          if (!prev[key] || (programDetails?.specialty && !SPECIALTY_ROTATION_COLORS[programDetails.specialty]?.[key])) {
            merged[key] = apiColors[key];
          }
        });
        console.log('Merged rotation colors from API (preserving specialty colors):', merged);
        return merged;
      });
      setCustomRotations(response.data.customRotationTypes);
      
    } catch (error) {
      console.error('Error fetching rotation types:', error);
      // Fallback to specialty-specific types if API fails
      fetchProgramDetails();
    }
  };

  // Fetch PGY level options for the program
  const fetchPGYLevels = async (programId) => {
    try {
      console.log('Fetching PGY levels for program:', programId);
      const options = await ProgramPGYUtils.getPGYLevelOptions(programId);
      console.log('Received PGY options:', options);
      setPgyLevelOptions(options);
      setMaxPGYLevel(options.length > 0 ? options[options.length - 1].value : 5);
    } catch (error) {
      console.error('Error fetching PGY levels:', error);
      // Fallback to default 5 levels
      const fallbackOptions = Array.from({ length: 5 }, (_, i) => ({
        value: i + 1,
        label: `PGY-${i + 1}`
      }));
      console.log('Using fallback PGY options:', fallbackOptions);
      setPgyLevelOptions(fallbackOptions);
      setMaxPGYLevel(5);
    }
  };

  // Fetch approved vacation requests
  const fetchApprovedVacationRequests = async () => {
    try {
      // Prevent multiple simultaneous calls
      if (fetchingVacationRequests) {
        console.log('Already fetching vacation requests, skipping...');
        return;
      }
      
      const token = localStorage.getItem('token');
      if (!token) return;

      // Get current month for the request
      const months = getAllMonths(schedules);
      const currentMonthStr = months && months[currentMonthIndex];
      if (!currentMonthStr) {
        console.log('No current month found, skipping vacation request fetch');
        return;
      }
      
      const [year, month] = currentMonthStr.split('-').map(str => parseInt(str, 10));
      
      console.log('Fetching approved vacation requests for:', { year, month });
      setFetchingVacationRequests(true);
      
      // Use director endpoint to fetch all residents' vacation requests
      try {
        console.log('Using director endpoint to fetch all residents\' vacation requests');
        const response = await axios.get(`${API_ENDPOINTS.DIRECTOR}/vacation-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('Director vacation requests response:', response.data);
        const allRequests = response.data || [];
        
        // Filter for approved requests that overlap with the current month
        const monthStart = new Date(year, month - 1, 1);
        const monthEnd = new Date(year, month, 0, 23, 59, 59);
        
        const approvedRequests = allRequests.filter(request => {
          if (request.status !== 'Approved') return false;
          
          const requestStart = new Date(request.startDate);
          const requestEnd = new Date(request.endDate);
          
          // Check if the request overlaps with the current month
          return requestStart <= monthEnd && requestEnd >= monthStart;
        });
        
        console.log('Filtered approved requests for current month:', approvedRequests);
        
        // Add resident names from schedule data
        const approvedRequestsWithNames = approvedRequests.map(request => {
          // If the request already has residentName (from new endpoint), use it
          if (request.residentName) {
            return {
              ...request,
              resident: {
                firstName: request.residentName.split(' ')[0] || 'Unknown',
                lastName: request.residentName.split(' ').slice(1).join(' ') || 'Resident'
              }
            };
          }
          
          // Otherwise, find the resident in the schedule data (fallback for old endpoint)
          let residentName = 'Unknown Resident';
          Object.values(schedules).forEach(pgyData => {
            if (pgyData && pgyData.schedule) {
              Object.entries(pgyData.schedule).forEach(([residentId, residentData]) => {
                if (parseInt(residentId) === request.residentId && residentData.name) {
                  residentName = residentData.name;
                }
              });
            }
          });
          
          return {
            ...request,
            resident: {
              firstName: residentName.split(' ')[0] || 'Unknown',
              lastName: residentName.split(' ').slice(1).join(' ') || 'Resident'
            }
          };
        });
        
        console.log('Approved requests with names:', approvedRequestsWithNames);
        setApprovedVacationRequests(approvedRequestsWithNames);
      } catch (apiError) {
        console.log('Error processing vacation requests:', apiError.message);
        setApprovedVacationRequests([]);
      }
    } catch (error) {
      console.error('Error fetching vacation requests:', error);
      setApprovedVacationRequests([]);
    } finally {
      setFetchingVacationRequests(false);
    }
  };

  // Initialize available academic years
  useEffect(() => {
    const currentYear = getCurrentAcademicYear().year;
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      years.push(i);
    }
    setAvailableAcademicYears(years);
  }, []);

  // Update start and end dates when academic year changes
  useEffect(() => {
    if (scheduleType === "monthly") {
      const academicYear = getAcademicYearForYear(selectedAcademicYear);
      setStartDate(academicYear.start);
      setEndDate(academicYear.end);
    }
  }, [selectedAcademicYear, scheduleType]);

  // Fetch vacation requests when schedules or current month changes
  useEffect(() => {
    if (Object.keys(schedules).length > 0) {
      fetchApprovedVacationRequests();
    }
  }, [schedules, currentMonthIndex]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        // Fetch program details first to get specialty and set specialty colors
        await fetchProgramDetails();
        // Small delay to ensure specialty colors are set before fetching rotation types
        await new Promise(resolve => setTimeout(resolve, 100));
        // Then fetch all rotation types (which includes custom rotations)
        // This will preserve specialty colors set in fetchProgramDetails
        await fetchAllRotationTypes();
        // Then fetch residents and schedules
        await Promise.all([
          fetchResidents(),
          fetchSchedules(),
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    initializeData();
  }, []);

  // Refetch schedules when viewAcademicYear changes
  useEffect(() => {
    fetchSchedules();
  }, [viewAcademicYear]);

  // Load pager numbers from database
  useEffect(() => {
    const fetchPagerNumbers = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axios.get(`${API_ENDPOINTS.SCHEDULES}/pager-numbers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPagerNumbers(response.data);
      } catch (error) {
        console.error("Error loading pager numbers:", error);
        // Fallback to localStorage if database fails
        const savedPagerNumbers = localStorage.getItem("rotationPagerNumbers");
        if (savedPagerNumbers) {
          try {
            setPagerNumbers(JSON.parse(savedPagerNumbers));
          } catch (localError) {
            console.error("Error loading pager numbers from localStorage:", localError);
          }
        }
      }
    };

    fetchPagerNumbers();
  }, []);

  const fetchResidents = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_ENDPOINTS.DIRECTOR}/users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Fetched residents:", response.data); // Debug log
      setResidents(response.data);
      
      // Extract pager numbers from residents
      const pagerNumbers = {};
      response.data.forEach(resident => {
        if (resident.pagerNumber) {
          pagerNumbers[resident.id] = resident.pagerNumber;
        }
      });
      setResidentPagerNumbers(pagerNumbers);
      
      // Load profile pictures for all residents
      const picUrls = {};
      for (const resident of response.data) {
        if (resident.profilePic) {
          const picUrl = await loadProfilePicture(resident.profilePic);
          if (picUrl) {
            picUrls[resident.id] = picUrl;
          }
        }
      }
      setProfilePicUrls(picUrls);
    } catch (error) {
      console.error("Error fetching residents:", error);
      setError("Failed to fetch residents. Please try again.");
    }
  };

  const fetchSchedules = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Please log in to view schedules");
        return;
      }

      // Fetch rotation schedules
      const rotationResponse = await axios.get(`${API_ENDPOINTS.SCHEDULES}/rotations`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { academicYear: viewAcademicYear }
      });
      setSchedules(rotationResponse.data);

      // Fetch weekend call schedules (filtered by academic year)
      const weekendResponse = await axios.get(`${API_ENDPOINTS.SCHEDULES}/weekend-call`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { academicYear: viewAcademicYear }
      });
      setWeekendCallSchedules(weekendResponse.data);

      // Fetch off-service residents
      await fetchOffServiceResidents();

      // Update current month index based on fetched schedules
      const months = getAllMonths(rotationResponse.data);
      if (months.length > 0) {
        const now = new Date();
        const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const idx = months.indexOf(currentMonthStr);
        setCurrentMonthIndex(idx >= 0 ? idx : 0);
      } else {
        setCurrentMonthIndex(-1);
      }

    } catch (error) {
      console.error("Error fetching schedules:", error);
      setError("Failed to load schedules. Please try again later.");
    }
  };

  // Fetch off-service residents
  const fetchOffServiceResidents = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(`${API_ENDPOINTS.SCHEDULES}/off-service-residents`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { academicYear: viewAcademicYear }
      });
      setOffServiceResidents(response.data);
    } catch (error) {
      console.error("Error fetching off-service residents:", error);
    }
  };

  // Save off-service residents
  const handleSaveOffServiceResidents = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Please log in to save off-service residents");
        return;
      }

      const scheduleData = schedules[selectedPgyForOffService];
      const scheduleType = scheduleData?.scheduleType || 'monthly';

      // Validate entries based on schedule type
      let validEntries;
      if (scheduleType === 'monthly') {
        validEntries = offServiceEntries.filter(
        entry => entry.residentName.trim() && entry.month && entry.rotationName.trim()
      );
      if (validEntries.length === 0) {
        setError("Please add at least one valid entry with resident name, month, and rotation");
          return;
        }
      } else if (scheduleType === 'weekly') {
        validEntries = offServiceEntries.filter(
          entry => entry.residentName.trim() && entry.weekBlock && entry.rotationName.trim()
        );
        if (validEntries.length === 0) {
          setError("Please add at least one valid entry with resident name, week block, and rotation");
          return;
        }
      } else if (scheduleType === 'blocks') {
        validEntries = offServiceEntries.filter(
          entry => entry.residentName.trim() && entry.blockIndex && 
          (entry.rotations?.major || entry.rotations?.minor)
        );
        if (validEntries.length === 0) {
          setError("Please add at least one valid entry with resident name, block, and at least one rotation");
          return;
        }
      } else {
        setError("Unknown schedule type");
        return;
      }

      // Prepare entries with pgyLevel and academicYear
      const entriesToSave = validEntries.map(entry => {
        const baseEntry = {
        pgyLevel: parseInt(selectedPgyForOffService, 10),
        residentName: entry.residentName.trim(),
          academicYear: viewAcademicYear,
          scheduleType: scheduleType
        };

        if (scheduleType === 'monthly') {
          baseEntry.month = entry.month;
          baseEntry.rotationName = entry.rotationName.trim();
        } else if (scheduleType === 'weekly') {
          baseEntry.weekBlock = entry.weekBlock;
          baseEntry.rotationName = entry.rotationName.trim();
        } else if (scheduleType === 'blocks') {
          baseEntry.blockIndex = entry.blockIndex;
          if (entry.rotations?.major) {
            baseEntry.majorRotation = entry.rotations.major;
          }
          if (entry.rotations?.minor) {
            baseEntry.minorRotation = entry.rotations.minor;
          }
        }

        return baseEntry;
      });

      await axios.post(`${API_ENDPOINTS.SCHEDULES}/off-service-residents`, {
        entries: entriesToSave
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh off-service residents
      await fetchOffServiceResidents();

      // Reset modal
      setShowOffServiceModal(false);
      const defaultEntry = {
        residentName: '',
        month: '',
        rotationName: '',
        weekBlock: '',
        blockIndex: '',
        rotations: { major: '', minor: '' }
      };
      setOffServiceEntries([defaultEntry]);
      setSelectedPgyForOffService(null);
      setError("");
      showSuccess(`Added ${validEntries.length} off-service resident(s)`);
    } catch (error) {
      console.error("Error saving off-service residents:", error);
      setError(error.response?.data?.error || "Failed to save off-service residents");
    }
  };

  // Delete off-service resident
  const handleDeleteOffServiceResident = async (id) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      await axios.delete(`${API_ENDPOINTS.SCHEDULES}/off-service-residents/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh off-service residents
      await fetchOffServiceResidents();
      showSuccess("Off-service resident deleted successfully");
    } catch (error) {
      console.error("Error deleting off-service resident:", error);
      setError(error.response?.data?.error || "Failed to delete off-service resident");
    }
  };

  // Open off-service modal for a specific PGY level
  const handleOpenOffServiceModal = (pgyLevel) => {
    setSelectedPgyForOffService(pgyLevel);
    const scheduleData = schedules[pgyLevel];
    const scheduleType = scheduleData?.scheduleType || 'monthly';
    const defaultEntry = {
      residentName: '',
      month: '',
      rotationName: '',
      weekBlock: '',
      blockIndex: '',
      rotations: { major: '', minor: '' }
    };
    setOffServiceEntries([defaultEntry]);
    setEditingOffServiceResident(null); // Not editing, adding new
    setShowOffServiceModal(true);
    setError("");
  };

  // Edit all off-service residents for a PGY level
  const handleEditOffServiceResidents = (pgyLevel) => {
    setSelectedPgyForOffService(pgyLevel);
    // Load all existing residents for this PGY level
    const existingResidents = offServiceResidents
      .filter(osr => osr.pgyLevel.toString() === pgyLevel.toString())
      .map(osr => ({
        id: osr.id,
        residentName: osr.residentName,
        month: osr.month || '',
        rotationName: osr.rotationName || '',
        weekBlock: osr.weekBlock || '',
        blockIndex: osr.blockIndex || '',
        rotations: osr.rotations || { major: osr.majorRotation || '', minor: osr.minorRotation || '' }
      }));
    
    if (existingResidents.length > 0) {
      setOffServiceEntries(existingResidents);
      setEditingOffServiceResident('all'); // Mark that we're editing all residents
    } else {
      const scheduleData = schedules[pgyLevel];
      const scheduleType = scheduleData?.scheduleType || 'monthly';
      const defaultEntry = {
        residentName: '',
        month: '',
        rotationName: '',
        weekBlock: '',
        blockIndex: '',
        rotations: { major: '', minor: '' }
      };
      setOffServiceEntries([defaultEntry]);
      setEditingOffServiceResident(null);
    }
    setShowOffServiceModal(true);
    setError("");
  };

  // Save all off-service residents (handles both new and updates)
  const handleSaveAllOffServiceResidents = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Please log in to save off-service residents");
        return;
      }

      const scheduleData = schedules[selectedPgyForOffService];
      const scheduleType = scheduleData?.scheduleType || 'monthly';

      // Validate entries based on schedule type
      // When editing, allow 0 entries (to delete all)
      const isEditing = editingOffServiceResident === 'all';
      let validEntries;
      if (scheduleType === 'monthly') {
        validEntries = offServiceEntries.filter(
        entry => entry.residentName.trim() && entry.month && entry.rotationName.trim()
      );
      if (validEntries.length === 0 && !isEditing) {
        setError("Please add at least one valid entry with resident name, month, and rotation");
          return;
        }
      } else if (scheduleType === 'weekly') {
        validEntries = offServiceEntries.filter(
          entry => entry.residentName.trim() && entry.weekBlock && entry.rotationName.trim()
        );
        if (validEntries.length === 0 && !isEditing) {
          setError("Please add at least one valid entry with resident name, week block, and rotation");
          return;
        }
      } else if (scheduleType === 'blocks') {
        validEntries = offServiceEntries.filter(
          entry => entry.residentName.trim() && entry.blockIndex && 
          (entry.rotations?.major || entry.rotations?.minor)
        );
        if (validEntries.length === 0 && !isEditing) {
          setError("Please add at least one valid entry with resident name, block, and at least one rotation");
          return;
        }
      } else {
        setError("Unknown schedule type");
        return;
      }

      // Get existing residents for this PGY level
      const existingResidents = offServiceResidents.filter(
        osr => osr.pgyLevel.toString() === selectedPgyForOffService.toString()
      );

      // Delete all existing residents for this PGY level
      for (const existing of existingResidents) {
        await axios.delete(`${API_ENDPOINTS.SCHEDULES}/off-service-residents/${existing.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Create new entries
      const entriesToSave = validEntries.map(entry => {
        const baseEntry = {
        pgyLevel: parseInt(selectedPgyForOffService, 10),
        residentName: entry.residentName.trim(),
          academicYear: viewAcademicYear,
          scheduleType: scheduleType
        };

        if (scheduleType === 'monthly') {
          baseEntry.month = entry.month;
          baseEntry.rotationName = entry.rotationName.trim();
        } else if (scheduleType === 'weekly') {
          baseEntry.weekBlock = entry.weekBlock;
          baseEntry.rotationName = entry.rotationName.trim();
        } else if (scheduleType === 'blocks') {
          baseEntry.blockIndex = entry.blockIndex;
          if (entry.rotations?.major) {
            baseEntry.majorRotation = entry.rotations.major;
          }
          if (entry.rotations?.minor) {
            baseEntry.minorRotation = entry.rotations.minor;
          }
        }

        return baseEntry;
      });

      if (entriesToSave.length > 0) {
        await axios.post(`${API_ENDPOINTS.SCHEDULES}/off-service-residents`, {
          entries: entriesToSave
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Refresh off-service residents
      await fetchOffServiceResidents();

      // Reset modal
      setShowOffServiceModal(false);
      const defaultEntry = {
        residentName: '',
        month: '',
        rotationName: '',
        weekBlock: '',
        blockIndex: '',
        rotations: { major: '', minor: '' }
      };
      setOffServiceEntries([defaultEntry]);
      setSelectedPgyForOffService(null);
      setEditingOffServiceResident(null);
      setError("");
      const deletedCount = existingResidents.length - validEntries.length;
      if (validEntries.length === 0 && existingResidents.length > 0) {
        showSuccess(`Deleted all ${existingResidents.length} off-service resident(s)`);
      } else if (deletedCount > 0) {
        showSuccess(`Updated ${validEntries.length} and deleted ${deletedCount} off-service resident(s)`);
      } else {
      showSuccess(`Updated ${validEntries.length} off-service resident(s)`);
      }
    } catch (error) {
      console.error("Error saving off-service residents:", error);
      setError(error.response?.data?.error || "Failed to save off-service residents");
    }
  };

  const handleAddRotation = () => {
    setShowModal(true);
    setNewCustomRotation("");
    setShowCustomRotationInput(false);
    setIsCustomRotationEditMode(false);
  };

  const handleRotationSelect = (rotation) => {
    setSelectedRotations((prev) => {
      if (prev.includes(rotation)) {
        return prev.filter((r) => r !== rotation);
      }
      // Initialize block rotation config when rotation is first selected (if blocks schedule type)
      if (scheduleType === "blocks" && !blockRotations[rotation]) {
        const [majorWeeks, minorWeeks] = blockPattern.split('-').map(Number);
        setBlockRotations((prevBlockRotations) => ({
          ...prevBlockRotations,
          [rotation]: {
            column: 'major',
            totalWeeks: 0,
            minBlockSize: 2, // Major: min = 2
            maxBlockSize: majorWeeks, // Major: max = majorWeeks
          }
        }));
      }
      return [...prev, rotation];
    });
  };

  const handleFrequencyChange = (rotation, frequency) => {
    setRotationFrequency((prev) => ({
      ...prev,
      [rotation]: frequency,
    }));
  };

  const handleRecurrentRotationChange = (rotation) => {
    setRecurrentRotations((prev) => {
      if (prev.includes(rotation)) {
        return prev.filter((r) => r !== rotation);
      }
      return [...prev, rotation];
    });
  };

  // Custom rotation management functions
  const handleAddCustomRotation = async () => {
    const trimmedRotation = newCustomRotation.trim();
    if (!trimmedRotation) {
      return;
    }
    
    if (rotationTypes.includes(trimmedRotation)) {
      setError(`Rotation "${trimmedRotation}" already exists.`);
      return;
    }
    
    try {
      setCustomRotationLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_ENDPOINTS.CUSTOM_ROTATION_TYPES}`, {
        name: trimmedRotation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh rotation types from database
      await fetchAllRotationTypes();
      
      setNewCustomRotation("");
      setShowCustomRotationInput(false);
      setError(""); // Clear any previous errors
    } catch (error) {
      console.error('Error adding custom rotation:', error);
      setError(error.response?.data?.error || 'Failed to add custom rotation');
    } finally {
      setCustomRotationLoading(false);
    }
  };

  const handleRemoveCustomRotation = async (rotationToRemove) => {
    try {
      setCustomRotationLoading(true);
      const token = localStorage.getItem('token');
      
      // Find the custom rotation type in the database
      const customRotation = customRotations.find(cr => cr.name === rotationToRemove);
      if (!customRotation) {
        setError('Custom rotation not found');
        return;
      }
      
      await axios.delete(`${API_ENDPOINTS.CUSTOM_ROTATION_TYPES}/${customRotation.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh rotation types from database
      await fetchAllRotationTypes();
      
      // Remove from selected and recurrent rotations
      setSelectedRotations(prev => prev.filter(r => r !== rotationToRemove));
      setRecurrentRotations(prev => prev.filter(r => r !== rotationToRemove));
      
    } catch (error) {
      console.error('Error removing custom rotation:', error);
      setError(error.response?.data?.error || 'Failed to remove custom rotation');
    } finally {
      setCustomRotationLoading(false);
    }
  };

  const handleShowCustomRotationInput = () => {
    setShowCustomRotationInput(true);
  };

  const handleCancelCustomRotation = () => {
    setShowCustomRotationInput(false);
    setNewCustomRotation("");
  };

  const handleToggleCustomRotationEditMode = () => {
    setIsCustomRotationEditMode(!isCustomRotationEditMode);
  };

  // Pager number management functions
  const handleShowPagerModal = () => {
    // Get all unique rotations from schedules
    const allRotations = new Set();
    Object.values(schedules).forEach((scheduleData) => {
      Object.values(scheduleData.schedule).forEach((residentData) => {
        Object.values(residentData.rotations).forEach((rotation) => {
          if (rotation) allRotations.add(rotation);
        });
      });
    });
    
    // Load saved pager numbers from localStorage as fallback
    let savedPagerNumbers = {};
    const savedPagerNumbersStr = localStorage.getItem("rotationPagerNumbers");
    if (savedPagerNumbersStr) {
      try {
        savedPagerNumbers = JSON.parse(savedPagerNumbersStr);
      } catch (error) {
        console.error("Error parsing saved pager numbers:", error);
      }
    }
    
    // Initialize pager numbers with existing values from state, localStorage, or empty strings
    const initialPagerNumbers = {};
    Array.from(allRotations).forEach((rotation) => {
      initialPagerNumbers[rotation] = pagerNumbers[rotation] || savedPagerNumbers[rotation] || "";
    });
    
    setPagerNumbers(initialPagerNumbers);
    setShowPagerModal(true);
  };

  // Function to format phone numbers as (###) ###-####
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all non-digits from input
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    
    // Format as (###) ###-####
    if (digitsOnly.length <= 3) {
      return digitsOnly;
    } else if (digitsOnly.length <= 6) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }
  };

  const handlePagerNumberChange = (rotation, value) => {
    // Remove all non-digits from input
    const digitsOnly = value.replace(/\D/g, '');
    
    // Format as (###) ###-####
    let formattedValue = '';
    if (digitsOnly.length <= 3) {
      formattedValue = digitsOnly;
    } else if (digitsOnly.length <= 6) {
      formattedValue = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else {
      formattedValue = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }
    
    // Limit to 10 digits maximum
    if (digitsOnly.length <= 10) {
      setPagerNumbers(prev => ({
        ...prev,
        [rotation]: formattedValue
      }));
    }
  };

  const handleSavePagerNumbers = async () => {
    try {
      setPagerModalLoading(true);
      const token = localStorage.getItem("token");
      
      if (!token) {
        setError("Please log in to save pager numbers");
        setPagerModalLoading(false);
        return;
      }

      // Save pager numbers to database
      const response = await axios.post(`${API_ENDPOINTS.SCHEDULES}/pager-numbers`, {
        pagerNumbers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log("Pager numbers saved successfully:", response.data);
      
      // Also save to localStorage as backup
      localStorage.setItem("rotationPagerNumbers", JSON.stringify(pagerNumbers));
      
      setShowPagerModal(false);
      setPagerModalLoading(false);
      setError(""); // Clear any previous errors
      
      // Show success message
      showSuccess("Pager numbers saved successfully.");
    } catch (error) {
      console.error("Error saving pager numbers:", error);
      setError(error.response?.data?.error || "Failed to save pager numbers. Please try again.");
      setPagerModalLoading(false);
    }
  };

  const handleCancelPagerModal = () => {
    setShowPagerModal(false);
    setPagerNumbers({});
  };

  const generateSchedule = async () => {
    try {
      console.log('[Generate Schedule] Starting schedule generation...');
      setGeneratingSchedule(true);
      console.log('[Generate Schedule] Validation check...', { configuredLevels: scheduleConfigs.length, startDate, endDate, scheduleType });
      
      if (!startDate || !endDate || scheduleConfigs.length === 0) {
        console.log('[Generate Schedule] Validation failed - missing required fields');
        setError("Please fill in all required fields");
        setGeneratingSchedule(false);
        return;
      }

      const validConfigs = scheduleConfigs.filter(
        (cfg) => cfg.pgyLevel && (cfg.selectedRotations || []).length > 0
      );
      if (validConfigs.length === 0) {
        setError("Add at least one PGY level with selected rotations");
        setGeneratingSchedule(false);
        return;
      }

      const pgyValues = validConfigs.map((cfg) => String(cfg.pgyLevel));
      if (new Set(pgyValues).size !== pgyValues.length) {
        setError("Each PGY level can only be configured once");
        setGeneratingSchedule(false);
        return;
      }

      // Validate dates
      const startDateObj = new Date(startDate + "T00:00:00");
      const endDateObj = new Date(endDate + "T00:00:00");
      
      console.log('[Generate Schedule] Date validation...', { startDateObj, endDateObj });
      
      if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
        console.log('[Generate Schedule] Invalid dates');
        setError("Please enter valid start and end dates");
        setGeneratingSchedule(false);
        return;
      }

      if (startDateObj >= endDateObj) {
        console.log('[Generate Schedule] End date before start date');
        setError("End date must be after start date");
        setGeneratingSchedule(false);
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        console.log('[Generate Schedule] No token found');
        setError("Please log in to save schedules");
        return;
      }

      try {
        const generatedPgyLevels = [];
        for (const config of validConfigs) {
          console.log('[Generate Schedule] Filtering residents...', { totalResidents: residents.length, selectedPGY: config.pgyLevel });
          const filteredResidents = residents.filter(
            (resident) => resident.year === parseInt(config.pgyLevel, 10)
          );

          if (filteredResidents.length === 0) {
            setError(`No residents found for PGY-${config.pgyLevel}`);
            setGeneratingSchedule(false);
            return;
          }

          let newSchedule = {};
          if (scheduleType === "monthly") {
            newSchedule = await generateMonthlySchedule(filteredResidents, config);
          } else if (scheduleType === "blocks") {
            const hasValidConfig = (config.selectedRotations || []).some(rotation => {
              const rotationConfig = config.blockRotations?.[rotation];
              return rotationConfig && rotationConfig.totalWeeks > 0;
            });
            if (!hasValidConfig) {
              setError(`Please configure block rotations for PGY-${config.pgyLevel}`);
              setGeneratingSchedule(false);
              return;
            }

            const totalWeeksInSchedule = getWeeksBetween(startDate, endDate).length;
            const totalConfiguredWeeks = (config.selectedRotations || []).reduce((sum, rotation) => {
              const rotationConfig = config.blockRotations?.[rotation];
              return sum + (rotationConfig?.totalWeeks || 0);
            }, 0);

            if (totalConfiguredWeeks !== totalWeeksInSchedule) {
              setError(`PGY-${config.pgyLevel}: configured weeks (${totalConfiguredWeeks}) must equal total weeks (${totalWeeksInSchedule}).`);
              setGeneratingSchedule(false);
              return;
            }

            newSchedule = await generateBlockSchedule(filteredResidents, config);
          } else {
            newSchedule = await generateWeeklySchedule(filteredResidents, config);
          }

          await axios.post(
            `${API_ENDPOINTS.SCHEDULES}/rotations`,
            {
              pgyLevel: config.pgyLevel,
              startDate,
              endDate,
              schedule: newSchedule,
              academicYear: selectedAcademicYear,
              scheduleType: scheduleType,
              weekDuration: scheduleType === "weekly" ? weekDuration : null,
              blockDuration: scheduleType === "blocks" ? blockDuration : null,
              blockPattern: scheduleType === "blocks" ? blockPattern : null,
              blockRotations: scheduleType === "blocks" ? config.blockRotations : null,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          generatedPgyLevels.push(`PGY-${config.pgyLevel}`);
        }

        showSuccess(`Generated and saved schedules for ${generatedPgyLevels.join(", ")}`);

        // Refresh view to the generated academic year and refetch
        console.log("[Generate Schedule] Refreshing schedules...");
        setViewAcademicYear(selectedAcademicYear);
        await fetchSchedules();
        console.log("[Generate Schedule] Schedules refreshed");

        // Weekend call schedules are now generated on demand via the Weekend Call tab

        setShowModal(false);
        resetScheduleConfigState();
      } catch (error) {
        console.error("[Generate Schedule] Error saving schedule to database:", error);
        console.error("[Generate Schedule] Error details:", error.response?.data || error.message);
        setError("Failed to save schedule. Please try again.");
      } finally {
        console.log("[Generate Schedule] Setting generatingSchedule to false");
        setGeneratingSchedule(false);
      }
    } catch (error) {
      console.error("[Generate Schedule] Error generating schedule:", error);
      console.error("[Generate Schedule] Error stack:", error.stack);
      setError(
        "An error occurred while generating the schedule. Please try again."
      );
    } finally {
      console.log("[Generate Schedule] Finally block - setting generatingSchedule to false");
      setGeneratingSchedule(false);
    }
  };

  // Use imported generateMonthlySchedule
  const generateMonthlySchedule = async (filteredResidents, scheduleConfig) => {
    return generateMonthlyScheduleUtil(
      filteredResidents,
      startDate,
      endDate,
      scheduleConfig.selectedRotations,
      scheduleConfig.rotationFrequency,
      scheduleConfig.recurrentRotations
    );
  };

  const generateWeeklySchedule = async (filteredResidents, scheduleConfig) => {
    return generateWeeklyScheduleUtil(
      filteredResidents,
      startDate,
      endDate,
      weekDuration,
      scheduleConfig.selectedRotations,
      scheduleConfig.rotationFrequency,
      scheduleConfig.recurrentRotations
    );
  };

  const generateBlockSchedule = async (filteredResidents, scheduleConfig) => {
    return generateBlockScheduleUtil(
      filteredResidents,
      startDate,
      endDate,
      blockPattern,
      blockDuration,
      scheduleConfig.selectedRotations,
      scheduleConfig.blockRotations
    );
  };

  const deleteSchedule = async (pgyLevel) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Please log in to delete schedules");
        return;
      }

      // Delete the schedule from the database
      await axios.delete(
        `${API_ENDPOINTS.SCHEDULES}/rotations/${pgyLevel}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { academicYear: viewAcademicYear }
        }
      );

      // Remove the schedule from state
      setSchedules((prevSchedules) => {
        const newSchedules = { ...prevSchedules };
        delete newSchedules[pgyLevel];
        return newSchedules;
      });

      // Remove weekend schedules for this PGY level
      setWeekendCallSchedules((prevSchedules) => {
        const newSchedules = { ...prevSchedules };
        delete newSchedules[pgyLevel];
        localStorage.setItem(
          "weekendCallSchedules",
          JSON.stringify(newSchedules)
        );
        return newSchedules;
      });

      showSuccess("Schedule has been deleted.");
    } catch (error) {
      console.error("Error deleting schedule:", error);
      setError("Failed to delete schedule. Please try again.");
    }
  };


  const clearAllSchedules = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Please log in to clear schedules");
        return;
      }

      // Delete rotation schedules for each PGY level
      for (const pgyLevel of Object.keys(schedules)) {
        try {
          await axios.delete(
            `${API_ENDPOINTS.SCHEDULES}/rotations/${pgyLevel}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        } catch (error) {
          console.error(
            `Error deleting PGY-${pgyLevel} rotation schedule:`,
            error
          );
          // Continue with other deletions even if one fails
        }
      }

      // Delete weekend call schedules
      try {
        await axios.delete(
          `${API_ENDPOINTS.SCHEDULES}/weekend-call`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } catch (error) {
        console.error("Error deleting weekend call schedules:", error);
        // Continue with other deletions even if this fails
      }

      // Delete calendar events
      try {
        // Get all unique resident IDs from schedules
        const residentIds = new Set();
        Object.values(schedules).forEach((scheduleData) => {
          Object.keys(scheduleData.schedule).forEach((residentId) => {
            residentIds.add(residentId);
          });
        });

        // Delete events for each resident
        for (const residentId of residentIds) {
          try {
            await axios.delete(
              `${API_ENDPOINTS.EVENTS}/resident/${residentId}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
          } catch (error) {
            console.error(
              `Error deleting events for resident ${residentId}:`,
              error
            );
            // Continue with other deletions even if one fails
          }
        }
      } catch (error) {
        console.error("Error processing calendar events:", error);
      }

      // Clear local state
      setSchedules({});
      setWeekendCallSchedules({});
      setEditedWeekendSchedules({});
      setOriginalWeekendSchedules({});
      setMovedResidents(new Map());
      setAffectedCells(new Set());

      // Clear all relevant data from localStorage
      localStorage.removeItem("rotationSchedules");
      localStorage.removeItem("weekendCallSchedules");
      localStorage.removeItem("rotationSchedulerTab");

      // Reset other related state
      setCurrentMonthIndex(0);
      setCurrentDate(new Date());
      setSelectedRotations([]);
      setRotationFrequency({});
      setError("");

      showSuccess("All schedules have been cleared successfully.");
    } catch (error) {
      console.error("Error clearing schedules:", error);
      setError("Failed to clear schedules. Please try again.");
    }
  };

  const exportToCalendar = async (pgyLevel, scheduleData) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Please log in to add events to calendar");
        return;
      }

      // Get current user info to check if they're a director
      const userResponse = await axios.get(
        `${API_ENDPOINTS.AUTH}/me`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const currentUserId = userResponse.data.id;
      const isDirector = userResponse.data.role === "director";

      if (!isDirector) {
        setError("Only directors can export schedules to calendars");
        return;
      }

      console.log("Schedule data:", scheduleData);
      console.log("Starting calendar export for PGY", pgyLevel);
      const scheduleType = scheduleData.scheduleType || 'monthly';
      let successCount = 0;
      let failCount = 0;

      // Process each resident's events
      for (const [residentId, residentData] of Object.entries(
        scheduleData.schedule
      )) {
        // Skip if this is the director's ID
        if (residentId === currentUserId) continue;

        // First, delete any existing rotation events for this resident (future only)
        // Include endDate to properly delete events
        try {
          console.log(
            `Deleting existing rotation events for resident ${residentId}`
          );
          const today = new Date().toISOString().split('T')[0];
          const endDate = scheduleData.endDate || new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0];
          await axios.delete(
            `${API_ENDPOINTS.EVENTS}/resident/${residentId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              params: {
                startDate: today,
                endDate: endDate,
                isWeekendCall: 'false',
                isRotation: 'true'
              }
            }
          );
        } catch (error) {
          console.error("Error deleting existing events:", error);
          // Continue even if deletion fails
        }

        const programId = localStorage.getItem('programId');
        const today = new Date();
        const todayYMD = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        if (scheduleType === 'blocks') {
          // For block schedules, use buildBlockRotationRanges to get accurate date ranges
          // This handles gaps and properly groups consecutive weeks
          const blockRanges = buildBlockRotationRanges({ [pgyLevel]: scheduleData });
          const residentRanges = blockRanges[residentId] || {};
          
          console.log(`Block ranges for resident ${residentId}:`, residentRanges);
          
          const rotationRanges = [];
          
          // Process each rotation's ranges
          Object.entries(residentRanges).forEach(([rotationName, ranges]) => {
            console.log(`Processing rotation ${rotationName} with ${ranges.length} ranges`);
            ranges.forEach(range => {
              // Only add if it's in the future
              if (range.end >= todayYMD) {
                rotationRanges.push({
                  rotation: rotationName,
                  start: range.start,
                  end: range.end
                });
                console.log(`Added rotation range: ${rotationName} from ${range.start.toISOString().split('T')[0]} to ${range.end.toISOString().split('T')[0]}`);
              } else {
                console.log(`Skipping past rotation range: ${rotationName} ends ${range.end.toISOString().split('T')[0]}`);
              }
            });
          });
          
          console.log(`Total rotation ranges to create events for: ${rotationRanges.length}`);
          
          // Create events for each rotation range
          for (const range of rotationRanges) {
            const eventData = {
              title: `${range.rotation} - ${residentData.name}`,
              description: `Rotation assignment for ${residentData.name}`,
              start: range.start.toISOString(),
              end: range.end.toISOString(),
              allDay: true,
              label: "Education",
              location: "Hospital",
              repeat: "none",
              userId: residentId,
              residentId: residentId,
              createdBy: currentUserId,
              isRotation: true,
              isPublic: true,
              color: rotationColors[range.rotation] || "#3788d8",
              programId: parseInt(programId, 10)
            };

            try {
              console.log("Creating new event for rotation range:", range);
              const response = await axios.post(
                `${API_ENDPOINTS.EVENTS}/add`,
                eventData,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );
              console.log("Event created successfully:", response.data);
              successCount++;
            } catch (error) {
              console.error(
                "Error creating event:",
                error.response?.data || error.message
              );
              failCount++;
            }
          }
        } else if (scheduleType === 'weekly') {
          // For weekly schedules, rotations are stored by week keys
          // Group consecutive weeks with the same rotation into date ranges
          const weekKeys = Object.keys(residentData.rotations || {}).sort();
          const rotationRanges = [];
          
          let currentRotation = null;
          let rangeStart = null;
          let rangeStartDate = null;
          let rangeEnd = null;
          let rangeEndDate = null;
          
          for (const weekKey of weekKeys) {
            const rotation = residentData.rotations[weekKey];
            
            if (rotation !== currentRotation) {
              // Save previous range if it exists
              if (currentRotation && rangeStart && rangeEnd) {
                // Calculate end date based on the last week (Sunday of that week)
                const endDateObj = new Date(rangeEnd + 'T00:00:00');
                endDateObj.setDate(endDateObj.getDate() + 6); // Add 6 days to get Sunday
                endDateObj.setHours(23, 59, 59, 999);
                
                // Only add if it's in the future
                if (endDateObj >= todayYMD) {
                  rotationRanges.push({
                    rotation: currentRotation,
                    start: rangeStartDate,
                    end: endDateObj
                  });
                }
              }
              
              // Start new range
              currentRotation = rotation;
              rangeStart = weekKey;
              rangeStartDate = new Date(weekKey + 'T00:00:00');
              rangeEnd = weekKey;
              rangeEndDate = new Date(weekKey + 'T00:00:00');
            } else if (currentRotation && rotation === currentRotation) {
              // Same rotation, update the end week
              rangeEnd = weekKey;
              rangeEndDate = new Date(weekKey + 'T00:00:00');
            }
          }
          
          // Don't forget the last range
          if (currentRotation && rangeStart && rangeEnd) {
            const endDateObj = new Date(rangeEnd + 'T00:00:00');
            endDateObj.setDate(endDateObj.getDate() + 6); // Add 6 days to get Sunday
            endDateObj.setHours(23, 59, 59, 999);
            
            if (endDateObj >= todayYMD) {
              rotationRanges.push({
                rotation: currentRotation,
                start: rangeStartDate,
                end: endDateObj
              });
            }
          }
          
          // Create events for each rotation range
          for (const range of rotationRanges) {
            const eventData = {
              title: `${range.rotation} - ${residentData.name}`,
              description: `Rotation assignment for ${residentData.name}`,
              start: range.start.toISOString(),
              end: range.end.toISOString(),
              allDay: true,
              label: "Education",
              location: "Hospital",
              repeat: "none",
              userId: residentId,
              residentId: residentId,
              createdBy: currentUserId,
              isRotation: true,
              isPublic: true,
              color: rotationColors[range.rotation] || "#3788d8",
              programId: parseInt(programId, 10)
            };

            try {
              console.log("Creating new event for rotation range:", range);
              const response = await axios.post(
                `${API_ENDPOINTS.EVENTS}/add`,
                eventData,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );
              console.log("Event created successfully:", response.data);
              successCount++;
            } catch (error) {
              console.error(
                "Error creating event:",
                error.response?.data || error.message
              );
              failCount++;
            }
          }
        } else {
          // Monthly schedule - original logic
          const months = getMonthsBetween(
            scheduleData.startDate,
            scheduleData.endDate
          );
          
        for (const monthStr of months) {
          const rotation = residentData.rotations[monthStr];
          if (rotation) {
            const [year, month] = monthStr.split("-");
            const startDate = new Date(year, parseInt(month) - 1, 1);
            const endDate = new Date(year, parseInt(month), 0);

            // Only create future events
            if (endDate < todayYMD) {
              continue; // skip past months
            }

            const eventData = {
              title: `${rotation} - ${residentData.name}`,
              description: `Rotation assignment for ${residentData.name}`,
              start: startDate.toISOString(),
              end: endDate.toISOString(),
              allDay: true,
              label: "Education",
              location: "Hospital",
              repeat: "none",
              userId: residentId,
              residentId: residentId,
              createdBy: currentUserId,
              isRotation: true,
              isPublic: true,
              color: rotationColors[rotation] || "#3788d8",
              programId: parseInt(programId, 10)
            };

            try {
              console.log("Creating new event for month:", monthStr);
              const response = await axios.post(
                `${API_ENDPOINTS.EVENTS}/add`,
                eventData,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );
              console.log("Event created successfully:", response.data);
              successCount++;
            } catch (error) {
              console.error(
                "Error creating event:",
                error.response?.data || error.message
              );
              failCount++;
              }
            }
          }
        }
      }

      if (successCount > 0) {
        showSuccess(`Successfully created ${successCount} rotation events${failCount > 0 ? ` (${failCount} failed)` : ''}`);
      } else {
        setError("Failed to create events. Check console for details.");
      }
    } catch (error) {
      console.error("Error exporting to calendar:", error);
      setError("Failed to export to calendar. Please try again.");
    }
  };

  // getWeekendsForMonth is now imported from '../utils/scheduleGenerators/dateHelpers'

  // Function to navigate months
  const handleMonthNavigation = (direction) => {
    const months = getAllMonths(schedules);
    if (direction === "next" && currentMonthIndex < months.length - 1) {
      setCurrentMonthIndex((prev) => prev + 1);
    } else if (direction === "prev" && currentMonthIndex > 0) {
      setCurrentMonthIndex((prev) => prev - 1);
    }
  };

  // Function to get combined weekend schedules
  const getCombinedWeekendSchedules = () => {
    // If in edit mode, use the edited schedule
    if (isEditMode && Object.keys(editedWeekendSchedules).length > 0) {
      return editedWeekendSchedules;
    }
    // Otherwise, use the stored weekend call schedules
    return weekendCallSchedules || {};
  };

  // Function to get current rotation schedules
  const getCurrentRotationSchedules = () => {
    // If in rotation edit mode, use the edited schedules
    if (isRotationEditMode && Object.keys(editedRotationSchedules).length > 0) {
      return editedRotationSchedules;
    }
    // Otherwise, use the stored schedules
    return schedules || {};
  };

  const handleDeleteClick = (pgyLevel) => {
    setScheduleToDelete(pgyLevel);
    setShowDeleteConfirmModal(true);
  };

  const handleClearClick = () => {
    setShowClearConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (scheduleToDelete) {
      await deleteSchedule(scheduleToDelete);
      setShowDeleteConfirmModal(false);
      setScheduleToDelete(null);
    }
  };

  const handleConfirmClear = () => {
    clearAllSchedules();
    setShowClearConfirmModal(false);
  };

  // getAllDates is now imported from '../utils/scheduleGenerators/dateHelpers'
  // Use the imported function directly
  const getAllDatesLocal = () => getAllDates(schedules);

  // Function to handle date navigation
  const handleDateNavigation = (direction) => {
    const allDates = getAllDatesLocal();
    if (allDates.length === 0) return;
    
    const currentDateStr = currentDate.toISOString().split("T")[0];
    const currentIndex = allDates.indexOf(currentDateStr);

    if (direction === "next" && currentIndex < allDates.length - 1) {
      const [year, month, day] = allDates[currentIndex + 1]
        .split("-")
        .map(Number);
      setCurrentDate(new Date(year, month - 1, day));
    } else if (direction === "prev" && currentIndex > 0) {
      const [year, month, day] = allDates[currentIndex - 1]
        .split("-")
        .map(Number);
      setCurrentDate(new Date(year, month - 1, day));
    }
  };

  // Function to get residents for a specific date and rotation
  const getResidentsForDate = (date) => {
      const weekendSchedules = getCombinedWeekendSchedules();
    return getResidentsForDateHelper(date, schedules, offServiceResidents, weekendSchedules);
  };

  // Daily schedule rendering moved to separate component

  // Update localStorage when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem("rotationSchedulerTab", tab);
  };

  const handleDragStart = (resident, sourceRotation, sourceDate) => {
    setDraggedResident({
      resident,
      sourceRotation,
      sourceDate,
    });
  };

  // Rotation drag and drop handlers
  const handleRotationDragStart = (residentId, sourceMonth, sourceRotation) => {
    setDraggedRotation({
      residentId,
      sourceMonth,
      sourceRotation,
    });
  };
  
  // Handler for dragging from rotation types (legend) for block schedules
  const handleRotationTypeDragStart = (e, rotationType, columnType) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ''); // Prevent text selection
    setDraggedRotation({
      rotationType,
      columnType,
      sourceType: 'rotationType',
    });
  };
  
  // Handler for dragging block rotations
  const handleBlockRotationDragStart = (e, residentId, blockStart, weekStart, rotation, columnType) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ''); // Prevent text selection
    setDraggedRotation({
      residentId,
      blockStart,
      weekStart,
      sourceRotation: rotation,
      sourceType: 'block',
      columnType,
    });
  };
  
  // Helper function to count changes by comparing current state with original
  const countBlockScheduleChanges = () => {
    if (!isRotationEditMode || !originalRotationSchedules || !editedRotationSchedules || 
        Object.keys(originalRotationSchedules).length === 0 || Object.keys(editedRotationSchedules).length === 0) {
      return 0;
    }
    
    let changeCount = 0;
    
    // For each PGY level
    Object.keys(editedRotationSchedules).forEach(pgyLevel => {
      const currentSchedule = editedRotationSchedules[pgyLevel];
      const originalSchedule = originalRotationSchedules[pgyLevel];
      
      if (!currentSchedule || !originalSchedule || !currentSchedule.schedule || !originalSchedule.schedule) {
        return;
      }
      
      // For each resident
      Object.keys(currentSchedule.schedule).forEach(residentId => {
        const currentResident = currentSchedule.schedule[residentId];
        const originalResident = originalSchedule.schedule[residentId];
        
        if (!currentResident || !originalResident || !currentResident.rotations || !originalResident.rotations) {
          return;
        }
        
        // Get all week keys from both current and original
        const allWeekKeys = new Set([
          ...Object.keys(currentResident.rotations),
          ...Object.keys(originalResident.rotations)
        ]);
        
        // Count weeks that differ from original
        allWeekKeys.forEach(weekKey => {
          const currentRotation = currentResident.rotations[weekKey];
          const originalRotation = originalResident.rotations[weekKey];
          
          // If current differs from original, count it as a change
          if (currentRotation !== originalRotation) {
            changeCount++;
          }
        });
      });
    });
    
    return changeCount;
  };
  
  // Helper function to normalize changes by comparing with original state
  // This ensures that rotations changed back to their original value are not tracked as changes
  const normalizeBlockScheduleChanges = (rotationSchedules) => {
    if (!isRotationEditMode || !originalRotationSchedules || Object.keys(originalRotationSchedules).length === 0) {
      return rotationSchedules;
    }
    
    const normalized = JSON.parse(JSON.stringify(rotationSchedules));
    
    // For each PGY level
    Object.keys(normalized).forEach(pgyLevel => {
      const currentSchedule = normalized[pgyLevel];
      const originalSchedule = originalRotationSchedules[pgyLevel];
      
      if (!currentSchedule || !originalSchedule || !currentSchedule.schedule || !originalSchedule.schedule) {
        return;
      }
      
      // For each resident
      Object.keys(currentSchedule.schedule).forEach(residentId => {
        const currentResident = currentSchedule.schedule[residentId];
        const originalResident = originalSchedule.schedule[residentId];
        
        if (!currentResident || !originalResident || !currentResident.rotations || !originalResident.rotations) {
          return;
        }
        
        // Get all week keys from both current and original
        const allWeekKeys = new Set([
          ...Object.keys(currentResident.rotations),
          ...Object.keys(originalResident.rotations)
        ]);
        
        // For each week, if it matches the original, restore to original value
        allWeekKeys.forEach(weekKey => {
          const currentRotation = currentResident.rotations[weekKey];
          const originalRotation = originalResident.rotations[weekKey];
          
          // If current matches original, restore to original value
          // This ensures changes that are reverted are not tracked
          if (currentRotation === originalRotation) {
            if (originalRotation !== undefined) {
              currentResident.rotations[weekKey] = originalRotation;
            } else if (currentRotation === null || currentRotation === undefined) {
              // Both are null/undefined, remove the key to clean up
              delete currentResident.rotations[weekKey];
            }
          }
        });
      });
    });
    
    return normalized;
  };
  
  // Handler for dropping on block cells
  const handleBlockRotationDrop = (targetResidentId, targetBlockStart, targetWeekStart, targetColumnType, targetRotation) => {
    if (!draggedRotation) return;
    
    const newRotationSchedules = JSON.parse(JSON.stringify(editedRotationSchedules));
    
    // Find the PGY level
    let pgyLevel = null;
    Object.entries(newRotationSchedules).forEach(([pgy, scheduleData]) => {
      if (scheduleData.schedule && scheduleData.schedule[targetResidentId || draggedRotation.residentId]) {
        pgyLevel = pgy;
      }
    });
    
    if (!pgyLevel) {
      setDraggedRotation(null);
      return;
    }
    
    const scheduleData = newRotationSchedules[pgyLevel];
    const blockPatternValue = scheduleData.blockPattern || blockPattern || '4-1';
    const [majorWeeks, minorWeeks] = blockPatternValue.split('-').map(Number);
    const blockDurationValue = scheduleData.blockDuration || blockDuration || 5;
    const weeks = getWeeksBetween(scheduleData.startDate, scheduleData.endDate);
    
    // Find the target block
    let targetBlockIndex = -1;
    let targetBlockWeeks = [];
    for (let i = 0; i < weeks.length; i += blockDurationValue) {
      const blockWeeks = weeks.slice(i, i + blockDurationValue);
      if (blockWeeks.some(w => w.start === targetBlockStart)) {
        targetBlockIndex = Math.floor(i / blockDurationValue);
        targetBlockWeeks = blockWeeks;
        break;
      }
    }
    
    if (targetBlockIndex === -1) {
      setDraggedRotation(null);
      return;
    }
    
    const targetMajorWeeks = targetBlockWeeks.slice(0, majorWeeks);
    const targetMinorWeeks = targetBlockWeeks.slice(majorWeeks, majorWeeks + minorWeeks);
    const targetWeeks = targetColumnType === 'major' ? targetMajorWeeks : targetMinorWeeks;
    
    // If dragging from rotation types, add the rotation
    if (draggedRotation.sourceType === 'rotationType') {
      const rotationToAdd = draggedRotation.rotationType;
      
      // For 4-week major blocks: check if it's empty (placeholder) or has existing rotations
      if (targetColumnType === 'major' && majorWeeks === 4 && targetWeeks.length === 4) {
        // Check if the block is empty (all null) or has a placeholder
        const allWeeksEmpty = targetWeeks.every(w => !scheduleData.schedule[targetResidentId].rotations[w.start]);
        const existingRotation = scheduleData.schedule[targetResidentId].rotations[targetWeeks[0].start];
        const has4WeekRotation = existingRotation && 
          targetWeeks.every(w => scheduleData.schedule[targetResidentId].rotations[w.start] === existingRotation);
        
        if (allWeeksEmpty) {
          // Empty placeholder: add as full 4-week block
          targetWeeks.forEach(week => {
            scheduleData.schedule[targetResidentId].rotations[week.start] = rotationToAdd;
          });
        } else if (has4WeekRotation) {
          // Split the 4-week rotation into two 2-week blocks
          const firstHalf = targetWeeks.slice(0, 2);
          const secondHalf = targetWeeks.slice(2, 4);
          
          // First half gets the new rotation
          firstHalf.forEach(week => {
            scheduleData.schedule[targetResidentId].rotations[week.start] = rotationToAdd;
          });
          
          // Second half keeps the original rotation (now becomes a 2-week block)
          secondHalf.forEach(week => {
            scheduleData.schedule[targetResidentId].rotations[week.start] = existingRotation;
          });
        } else {
          // Has partial rotation (e.g., 2-week), assign to empty half
          const firstHalf = targetWeeks.slice(0, 2);
          const secondHalf = targetWeeks.slice(2, 4);
          const firstHalfRotation = scheduleData.schedule[targetResidentId].rotations[firstHalf[0].start];
          const secondHalfRotation = scheduleData.schedule[targetResidentId].rotations[secondHalf[0].start];
          
          if (!firstHalfRotation && secondHalfRotation) {
            // First half is empty, assign to first half
            firstHalf.forEach(week => {
              scheduleData.schedule[targetResidentId].rotations[week.start] = rotationToAdd;
            });
          } else if (firstHalfRotation && !secondHalfRotation) {
            // Second half is empty, assign to second half
            secondHalf.forEach(week => {
              scheduleData.schedule[targetResidentId].rotations[week.start] = rotationToAdd;
            });
          } else {
            // Both have rotations, replace first half
            firstHalf.forEach(week => {
              scheduleData.schedule[targetResidentId].rotations[week.start] = rotationToAdd;
            });
          }
        }
      } else {
        // Assign to all weeks in the target column
        targetWeeks.forEach(week => {
          scheduleData.schedule[targetResidentId].rotations[week.start] = rotationToAdd;
        });
      }
    } 
    // If dragging from a block cell, remove it (dragging outside removes)
    else if (draggedRotation.sourceType === 'block') {
      const { residentId: sourceResidentId, blockStart: sourceBlockStart, weekStart: sourceWeekStart, sourceRotation: sourceRot, columnType: sourceColumnType } = draggedRotation;
      
      // Find source block
      let sourceBlockIndex = -1;
      let sourceBlockWeeks = [];
      for (let i = 0; i < weeks.length; i += blockDurationValue) {
        const blockWeeks = weeks.slice(i, i + blockDurationValue);
        if (blockWeeks.some(w => w.start === sourceBlockStart)) {
          sourceBlockIndex = Math.floor(i / blockDurationValue);
          sourceBlockWeeks = blockWeeks;
          break;
        }
      }
      
      if (sourceBlockIndex >= 0) {
        const sourceMajorWeeks = sourceBlockWeeks.slice(0, majorWeeks);
        const sourceMinorWeeks = sourceBlockWeeks.slice(majorWeeks);
        const sourceWeeks = sourceColumnType === 'major' ? sourceMajorWeeks : sourceMinorWeeks;
        
        // Find which weeks have this rotation
        const weeksWithRotation = sourceWeeks.filter(week => 
          scheduleData.schedule[sourceResidentId].rotations[week.start] === sourceRot
        );
        
        // If it's a 2-week block in a 4-week major, expand the other 2-week block
        if (sourceColumnType === 'major' && majorWeeks === 4 && weeksWithRotation.length === 2) {
          // Check if there's another 2-week rotation in the same block
          const otherWeeks = sourceMajorWeeks.filter(w => !weeksWithRotation.includes(w));
          const otherRotation = otherWeeks.length > 0 ? scheduleData.schedule[sourceResidentId].rotations[otherWeeks[0].start] : null;
          
          // Remove the dragged rotation
          weeksWithRotation.forEach(week => {
            scheduleData.schedule[sourceResidentId].rotations[week.start] = null;
          });
          
          // If there's another 2-week rotation, expand it to fill all 4 weeks
          if (otherRotation && otherWeeks.length === 2) {
            sourceMajorWeeks.forEach(week => {
              scheduleData.schedule[sourceResidentId].rotations[week.start] = otherRotation;
            });
          }
        } else {
          // Remove the rotation from all matching weeks
          weeksWithRotation.forEach(week => {
            scheduleData.schedule[sourceResidentId].rotations[week.start] = null;
          });
        }
      }
    }
    
    // Normalize changes - remove any that match original state
    const normalizedSchedules = normalizeBlockScheduleChanges(newRotationSchedules);
    setEditedRotationSchedules(normalizedSchedules);
    setDraggedRotation(null);
  };
  
  // Handler for changing rotation via dropdown click
  const handleBlockRotationChange = (residentId, blockStart, weekStart, columnType, newRotation) => {
    const newRotationSchedules = JSON.parse(JSON.stringify(editedRotationSchedules));
    
    let pgyLevel = null;
    Object.entries(newRotationSchedules).forEach(([pgy, scheduleData]) => {
      if (scheduleData.schedule && scheduleData.schedule[residentId]) {
        pgyLevel = pgy;
      }
    });
    
    if (!pgyLevel) return;
    
    const scheduleData = newRotationSchedules[pgyLevel];
    const blockPatternValue = scheduleData.blockPattern || blockPattern || '4-1';
    const [majorWeeks, minorWeeks] = blockPatternValue.split('-').map(Number);
    const blockDurationValue = scheduleData.blockDuration || blockDuration || 5;
    const weeks = getWeeksBetween(scheduleData.startDate, scheduleData.endDate);
    
    // Find the block
    let targetBlockWeeks = [];
    for (let i = 0; i < weeks.length; i += blockDurationValue) {
      const blockWeeks = weeks.slice(i, i + blockDurationValue);
      if (blockWeeks.some(w => w.start === blockStart)) {
        targetBlockWeeks = blockWeeks;
        break;
      }
    }
    
    const targetMajorWeeks = targetBlockWeeks.slice(0, majorWeeks);
    const targetMinorWeeks = targetBlockWeeks.slice(majorWeeks, majorWeeks + minorWeeks);
    const targetWeeks = columnType === 'major' ? targetMajorWeeks : targetMinorWeeks;
    
    // Find the current rotation group - need to find all weeks with the same rotation as the starting week
    const currentRotation = scheduleData.schedule[residentId].rotations[weekStart];
    
    // Find all consecutive weeks with the same rotation starting from weekStart
    let weeksWithCurrentRotation = [];
    let foundStart = false;
    for (let i = 0; i < targetWeeks.length; i++) {
      const week = targetWeeks[i];
      const weekRotation = scheduleData.schedule[residentId].rotations[week.start];
      
      if (week.start === weekStart) {
        foundStart = true;
        weeksWithCurrentRotation.push(week);
      } else if (foundStart && weekRotation === currentRotation) {
        weeksWithCurrentRotation.push(week);
      } else if (foundStart && weekRotation !== currentRotation) {
        break;
      }
    }
    
    // If we didn't find the start, try to find all weeks with the same rotation
    if (weeksWithCurrentRotation.length === 0) {
      weeksWithCurrentRotation = targetWeeks.filter(week => 
        scheduleData.schedule[residentId].rotations[week.start] === currentRotation
      );
    }
    
    if (newRotation === null) {
      // Clear the rotation
      weeksWithCurrentRotation.forEach(week => {
        scheduleData.schedule[residentId].rotations[week.start] = null;
      });
      
      // If it's a 2-week block in a 4-week major, check if there's another 2-week block to expand
      if (columnType === 'major' && majorWeeks === 4 && weeksWithCurrentRotation.length === 2) {
        const targetMajorWeeks = targetBlockWeeks.slice(0, majorWeeks);
        const otherWeeks = targetMajorWeeks.filter(w => !weeksWithCurrentRotation.includes(w));
        
        if (otherWeeks.length === 2) {
          // Check if the other 2 weeks have the same rotation
          const otherRotation = scheduleData.schedule[residentId].rotations[otherWeeks[0].start];
          const otherWeeksHaveSameRotation = otherRotation && 
            otherWeeks.every(w => scheduleData.schedule[residentId].rotations[w.start] === otherRotation);
          
          if (otherWeeksHaveSameRotation) {
            // Expand the other 2-week block to fill all 4 weeks
            targetMajorWeeks.forEach(week => {
              scheduleData.schedule[residentId].rotations[week.start] = otherRotation;
            });
          }
        }
      }
    } else {
      // Replace the rotation
      // For 4-week major blocks: if replacing a 2-week block, keep the split
      if (columnType === 'major' && majorWeeks === 4 && weeksWithCurrentRotation.length === 2) {
        // Replace only the 2-week block
        weeksWithCurrentRotation.forEach(week => {
          scheduleData.schedule[residentId].rotations[week.start] = newRotation;
        });
      } else {
        // Replace all weeks in the group
        weeksWithCurrentRotation.forEach(week => {
          scheduleData.schedule[residentId].rotations[week.start] = newRotation;
        });
      }
    }
    
    // Normalize changes - remove any that match original state
    const normalizedSchedules = normalizeBlockScheduleChanges(newRotationSchedules);
    setEditedRotationSchedules(normalizedSchedules);
  };

  // Handler for swapping two 2-week rotations in a block
  const handleBlockRotationSwap = (residentId, blockStart) => {
    const newRotationSchedules = JSON.parse(JSON.stringify(editedRotationSchedules));
    
    let pgyLevel = null;
    Object.entries(newRotationSchedules).forEach(([pgy, scheduleData]) => {
      if (scheduleData.schedule && scheduleData.schedule[residentId]) {
        pgyLevel = pgy;
      }
    });
    
    if (!pgyLevel) return;
    
    const scheduleData = newRotationSchedules[pgyLevel];
    const blockPatternValue = scheduleData.blockPattern || blockPattern || '4-1';
    const [majorWeeks, minorWeeks] = blockPatternValue.split('-').map(Number);
    const blockDurationValue = scheduleData.blockDuration || blockDuration || 5;
    const weeks = getWeeksBetween(scheduleData.startDate, scheduleData.endDate);
    
    // Find the block
    let targetBlockWeeks = [];
    for (let i = 0; i < weeks.length; i += blockDurationValue) {
      const blockWeeks = weeks.slice(i, i + blockDurationValue);
      if (blockWeeks.some(w => w.start === blockStart)) {
        targetBlockWeeks = blockWeeks;
        break;
      }
    }
    
    if (targetBlockWeeks.length === 0) return;
    
    const targetMajorWeeks = targetBlockWeeks.slice(0, majorWeeks);
    
    // Check if we have exactly 4 major weeks (for 2+2 split)
    if (targetMajorWeeks.length !== 4) return;
    
    // Get rotations for first 2 weeks and last 2 weeks
    const firstTwoWeeks = targetMajorWeeks.slice(0, 2);
    const lastTwoWeeks = targetMajorWeeks.slice(2, 4);
    
    const firstRotation = scheduleData.schedule[residentId].rotations[firstTwoWeeks[0].start];
    const secondRotation = scheduleData.schedule[residentId].rotations[lastTwoWeeks[0].start];
    
    // Check if both are 2-week rotations (same rotation in both weeks of each group)
    const firstGroupSame = firstTwoWeeks.every(w => 
      scheduleData.schedule[residentId].rotations[w.start] === firstRotation
    );
    const secondGroupSame = lastTwoWeeks.every(w => 
      scheduleData.schedule[residentId].rotations[w.start] === secondRotation
    );
    
    if (!firstGroupSame || !secondGroupSame) return;
    
    // Swap the rotations
    firstTwoWeeks.forEach(week => {
      scheduleData.schedule[residentId].rotations[week.start] = secondRotation;
    });
    
    lastTwoWeeks.forEach(week => {
      scheduleData.schedule[residentId].rotations[week.start] = firstRotation;
    });
    
    // Normalize changes - remove any that match original state
    const normalizedSchedules = normalizeBlockScheduleChanges(newRotationSchedules);
    setEditedRotationSchedules(normalizedSchedules);
  };

  const handleRotationDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const highlightMovedResident = (resident, sourceCell, targetCell) => {
    // Add to moved residents map
    setMovedResidents((prev) => {
      const newMap = new Map(prev);
      newMap.set(`${resident.id}-${targetCell}`, {
        resident,
        sourceCell,
        targetCell,
        timestamp: Date.now(),
      });
      return newMap;
    });

    // Add both source and target cells to affected cells
    setAffectedCells((prev) => {
      const newSet = new Set(prev);
      newSet.add(sourceCell);
      newSet.add(targetCell);
      return newSet;
    });
  };

  const handleDrop = (targetRotation, targetDate) => {
    if (!draggedResident) return;

    const { resident, sourceRotation, sourceDate } = draggedResident;

    // If dragging from Unassigned, allow adding directly without weekend equality check
    if (!sourceDate) {
      // Check if resident already exists in target cell
      const existingResidents = editedWeekendSchedules[targetRotation]?.[targetDate]?.residents || [];
      if (existingResidents.some((r) => r.id === resident.id)) {
        alert(`${capitalizeName(resident.name)} is already assigned to this day.`);
        setDraggedResident(null);
        return;
      }

      const newSchedules = JSON.parse(JSON.stringify(editedWeekendSchedules));
      if (!newSchedules[targetRotation]) newSchedules[targetRotation] = {};
      if (!newSchedules[targetRotation][targetDate]) {
        newSchedules[targetRotation][targetDate] = {
          date: targetDate,
          residents: [],
          type: targetDate.includes("Saturday") ? "Saturday" : "Sunday",
        };
      }
      newSchedules[targetRotation][targetDate].residents.push(resident);
      setEditedWeekendSchedules(newSchedules);
      setDraggedResident(null);
      // Remove from manually-unassigned bucket if present
      const months = getAllMonths(schedules);
      const currentMonthStr = months && months[currentMonthIndex];
      if (currentMonthStr) {
        const key = `${targetRotation}-${currentMonthStr}`;
        setManuallyUnassignedByRotationMonth((prev) => {
          const copy = { ...prev };
          if (copy[key]) {
            const updated = { ...copy[key] };
            delete updated[resident.id];
            copy[key] = updated;
          }
          return copy;
        });
      }
      // Track change: each add from Unassigned counts separately (one per target cell)
      setMovedResidents((prev) => {
        const newMap = new Map(prev);
        const key = `${resident.id}-${targetRotation}-${targetDate}`;
        newMap.set(key, {
          resident,
          sourceCell: null,
          targetCell: `${targetRotation}-${targetDate}`,
          timestamp: Date.now(),
        });
        return newMap;
      });
      setAffectedCells((prev) => {
        const s = new Set(prev);
        s.add(`${targetRotation}-${targetDate}`);
        return s;
      });
      return;
    }

    // Check if the move is valid (same weekend)
    const sourceWeekend = sourceDate.split("T")[0];
    const targetWeekend = targetDate.split("T")[0];

    // Get the Saturday of the source weekend
    const sourceDateObj = new Date(sourceWeekend);
    const sourceSaturday = new Date(sourceDateObj);
    sourceSaturday.setDate(sourceDateObj.getDate() - sourceDateObj.getDay());

    // Get the Saturday of the target weekend
    const targetDateObj = new Date(targetWeekend);
    const targetSaturday = new Date(targetDateObj);
    targetSaturday.setDate(targetDateObj.getDate() - targetDateObj.getDay());

    // Check if it's the same weekend
    if (sourceSaturday.getTime() !== targetSaturday.getTime()) {
      alert("You can only move residents between Saturday and Sunday of the same weekend.");
      setDraggedResident(null);
      return;
    }

    // Check if rotation movement is allowed
    if (!allowRotationMovement && sourceRotation !== targetRotation) {
      alert("Movement between rotations is not allowed. Please check 'Enable Movement' to enable this feature.");
      setDraggedResident(null);
      return;
    }

    // Check if the resident is being moved to the same cell
    const isSameCell =
      sourceRotation === targetRotation && sourceDate === targetDate;

    // Check if resident already exists in target cell (when moving from another cell)
    if (!isSameCell) {
      const existingResidents = editedWeekendSchedules[targetRotation]?.[targetDate]?.residents || [];
      if (existingResidents.some((r) => r.id === resident.id)) {
        alert(`${capitalizeName(resident.name)} is already assigned to this day.`);
        setDraggedResident(null);
        return;
      }
    }

    // Create a deep copy of the current schedules
    const newSchedules = JSON.parse(JSON.stringify(editedWeekendSchedules));

    // Remove resident from source
    if (newSchedules[sourceRotation]?.[sourceDate]?.residents) {
      newSchedules[sourceRotation][sourceDate].residents = newSchedules[
        sourceRotation
      ][sourceDate].residents.filter((r) => r.id !== resident.id);
    }

    // Add resident to target
    if (!newSchedules[targetRotation]) {
      newSchedules[targetRotation] = {};
    }
    if (!newSchedules[targetRotation][targetDate]) {
      newSchedules[targetRotation][targetDate] = {
        date: targetDate,
        residents: [],
        type: targetDate.includes("Saturday") ? "Saturday" : "Sunday",
      };
    }
    newSchedules[targetRotation][targetDate].residents.push(resident);

    setEditedWeekendSchedules(newSchedules);
    setDraggedResident(null);

    // Determine the original cell for this resident
    let originalCell = null;
    Object.entries(originalWeekendSchedules).forEach(
      ([rotation, weekendData]) => {
        Object.entries(weekendData).forEach(([date, data]) => {
          if (data.residents.some((r) => r.id === resident.id)) {
            originalCell = { rotation, date };
          }
        });
      }
    );

    // Only update movedResidents if it's a real change
    if (
      !isSameCell &&
      (!originalCell || originalCell.rotation !== targetRotation || originalCell.date !== targetDate)
    ) {
      setMovedResidents((prev) => {
        const newMap = new Map(prev);
        const prevKeyForThisCell = Array.from(newMap.keys()).find((k) => k === `${resident.id}-${sourceRotation}-${sourceDate}`);
        const entryForThisCell = prevKeyForThisCell ? newMap.get(prevKeyForThisCell) : null;
        // If this cell was created from Unassigned earlier (sourceCell === null), move that entry
        if (entryForThisCell && entryForThisCell.sourceCell === null) {
          newMap.delete(prevKeyForThisCell);
          const newKey = `${resident.id}-${targetRotation}-${targetDate}`;
          newMap.set(newKey, {
            ...entryForThisCell,
            targetCell: `${targetRotation}-${targetDate}`,
          });
          return newMap;
        }
        // Otherwise, treat as standard change for moving existing assignment
        // Remove any prior tracking for this specific source cell only
        if (prevKeyForThisCell) newMap.delete(prevKeyForThisCell);
        const key = `${resident.id}-${targetRotation}-${targetDate}`;
        newMap.set(key, {
          resident,
          sourceCell: `${sourceRotation}-${sourceDate}`,
          targetCell: `${targetRotation}-${targetDate}`,
          timestamp: Date.now(),
        });
        return newMap;
      });

      // Only update affected cells if it's a real change
      setAffectedCells((prev) => {
        const newSet = new Set(prev);
        newSet.add(`${sourceRotation}-${sourceDate}`);
        newSet.add(`${targetRotation}-${targetDate}`);
        return newSet;
      });
    } else {
      // If it's the same cell or original position, remove from movedResidents
      // No net change; if this was a tracked unassigned entry, keep it as-is
    }
  };

  // Clear all residents from a specific weekend day cell for a rotation
  const handleClearWeekendDayForRotation = (rotation, date) => {
    if (!isEditMode) return;
    setClearConfirmData({ type: 'day', rotation, date });
    setShowWeekendClearConfirmModal(true);
  };

  const confirmClearWeekendDay = () => {
    if (!clearConfirmData || clearConfirmData.type !== 'day') return;
    const { rotation, date } = clearConfirmData;
    setShowWeekendClearConfirmModal(false);
    setClearConfirmData(null);
    const months = getAllMonths(schedules);
    const currentMonthStr = months && months[currentMonthIndex];
    const newSchedules = JSON.parse(JSON.stringify(editedWeekendSchedules));
    if (!newSchedules[rotation] || !newSchedules[rotation][date]) return;
    const removed = (newSchedules[rotation][date].residents || []).slice();
    newSchedules[rotation][date].residents = [];
    setEditedWeekendSchedules(newSchedules);
    if (currentMonthStr && removed.length > 0) {
      const key = `${rotation}-${currentMonthStr}`;
      setManuallyUnassignedByRotationMonth((prev) => {
        const copy = { ...prev };
        const existing = { ...(copy[key] || {}) };
        removed.forEach((r) => {
          existing[r.id] = r;
        });
        copy[key] = existing;
        return copy;
      });
    }
    // If any removed resident was previously added from Unassigned in this edit, remove their change entry
    if (removed.length > 0) {
      setMovedResidents((prev) => {
        const newMap = new Map(prev);
        removed.forEach((r) => {
          const keyForThisCell = `${r.id}-${rotation}-${date}`;
          if (newMap.has(keyForThisCell)) {
            const entry = newMap.get(keyForThisCell);
            if (entry && entry.sourceCell === null) {
              newMap.delete(keyForThisCell);
            }
          }
        });
        return newMap;
      });
    }
    // bump a version to force recalculation in child if needed
    setCurrentMonthUnassignedVersion((v) => v + 1);
  };

  // Clear all weekend call residents for the entire current month for a rotation
  const handleClearMonthForRotation = (rotation) => {
    if (!isEditMode) return;
    setClearConfirmData({ type: 'month', rotation });
    setShowWeekendClearConfirmModal(true);
  };

  const confirmClearMonth = () => {
    if (!clearConfirmData || clearConfirmData.type !== 'month') return;
    const { rotation } = clearConfirmData;
    setShowWeekendClearConfirmModal(false);
    setClearConfirmData(null);
    const months = getAllMonths(schedules);
    const currentMonthStr = months && months[currentMonthIndex];
    if (!currentMonthStr) return;
    const weekends = getWeekendsForMonth(currentMonthStr);
    const newSchedules = JSON.parse(JSON.stringify(editedWeekendSchedules));
    if (!newSchedules[rotation]) return;
    const removedAll = [];
    weekends.forEach((wk) => {
      [wk.saturday, wk.sunday].forEach((d) => {
        if (newSchedules[rotation][d]) {
          const removed = (newSchedules[rotation][d].residents || []).slice();
          if (removed.length > 0) removedAll.push(...removed);
          newSchedules[rotation][d].residents = [];
        }
      });
    });
    setEditedWeekendSchedules(newSchedules);
    if (removedAll.length > 0) {
      const key = `${rotation}-${currentMonthStr}`;
      setManuallyUnassignedByRotationMonth((prev) => {
        const copy = { ...prev };
        const existing = { ...(copy[key] || {}) };
        removedAll.forEach((r) => {
          existing[r.id] = r;
        });
        copy[key] = existing;
        return copy;
      });
    }
    // Remove change entries for residents that were added from Unassigned
    if (removedAll.length > 0) {
      setMovedResidents((prev) => {
        const newMap = new Map(prev);
        weekends.forEach((wk) => {
          [wk.saturday, wk.sunday].forEach((d) => {
            removedAll.forEach((r) => {
              const keyForCell = `${r.id}-${rotation}-${d}`;
              const entry = newMap.get(keyForCell);
              if (entry && entry.sourceCell === null) {
                newMap.delete(keyForCell);
              }
            });
          });
        });
        return newMap;
      });
    }
    setCurrentMonthUnassignedVersion((v) => v + 1);
  };

  const handleRotationDrop = (targetResidentId, targetMonth, targetRotation) => {
    if (!draggedRotation) return;

    const { residentId, sourceMonth, sourceRotation } = draggedRotation;

    // Only allow dragging within the same resident
    if (residentId !== targetResidentId) {
      alert("You can only move rotations within the same resident's schedule.");
      setDraggedRotation(null);
      return;
    }

    // Check if the rotation is being moved to the same cell
    const isSameCell = sourceMonth === targetMonth;

    // Create a deep copy of the current rotation schedules
    const newRotationSchedules = JSON.parse(JSON.stringify(editedRotationSchedules));

    // Find the PGY level for this resident
    let pgyLevel = null;
    Object.entries(newRotationSchedules).forEach(([pgy, scheduleData]) => {
      if (scheduleData.schedule && scheduleData.schedule[residentId]) {
        pgyLevel = pgy;
      }
    });

    if (!pgyLevel) {
      setDraggedRotation(null);
      return;
    }

    // Determine whether monthly or weekly schedule and operate on group blocks for weekly
    const scheduleData = newRotationSchedules[pgyLevel];
    const isWeekly = scheduleData.scheduleType === 'weekly';
    const isBlocks = scheduleData.scheduleType === 'blocks';
    if (isWeekly || isBlocks) {
      const sourceGroup = getGroupWeekStarts(scheduleData, sourceMonth);
      const targetGroup = getGroupWeekStarts(scheduleData, targetMonth);
      // Swap entire groups
      sourceGroup.forEach((wk, idx) => {
        const tgtWk = targetGroup[idx] || targetGroup[targetGroup.length - 1];
        const temp = scheduleData.schedule[residentId].rotations[wk];
        scheduleData.schedule[residentId].rotations[wk] = scheduleData.schedule[residentId].rotations[tgtWk];
        scheduleData.schedule[residentId].rotations[tgtWk] = temp;
      });
    } else {
      // Monthly swap single cells
    const tempRotation = newRotationSchedules[pgyLevel].schedule[residentId].rotations[sourceMonth];
    newRotationSchedules[pgyLevel].schedule[residentId].rotations[sourceMonth] = 
      newRotationSchedules[pgyLevel].schedule[residentId].rotations[targetMonth];
    newRotationSchedules[pgyLevel].schedule[residentId].rotations[targetMonth] = tempRotation;
    }

    setEditedRotationSchedules(newRotationSchedules);
    setDraggedRotation(null);

    // Update moved rotations tracking
    if (!isSameCell) {
      setMovedRotations((prev) => {
        const newMap = new Map(prev);
        const key = `${residentId}-${targetMonth}`;
        newMap.set(key, {
          residentId,
          sourceMonth,
          targetMonth,
          sourceRotation,
          targetRotation,
          timestamp: Date.now(),
        });
        return newMap;
      });

      setAffectedRotationCells((prev) => {
        const newSet = new Set(prev);
        newSet.add(`${residentId}-${sourceMonth}`);
        newSet.add(`${residentId}-${targetMonth}`);
        return newSet;
      });
    } else {
      setMovedRotations((prev) => {
        const newMap = new Map(prev);
        Array.from(newMap.keys()).forEach((k) => {
          if (k.startsWith(`${residentId}-`)) {
            newMap.delete(k);
          }
        });
        return newMap;
      });
    }
  };

  const handleSaveChanges = async () => {
    try {
      // Save the edited schedules to database
      const success = await saveWeekendCallSchedules(editedWeekendSchedules);
      if (success) {
        // Save to localStorage as backup
        localStorage.setItem(
          "weekendCallSchedules",
          JSON.stringify(editedWeekendSchedules)
        );
        setWeekendCallSchedules(editedWeekendSchedules);
        setIsEditMode(false);
        // Clear highlights
        setMovedResidents(new Map());
        setAffectedCells(new Set());
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      setError("Failed to save changes. Please try again.");
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditedWeekendSchedules({});
    setMovedResidents(new Map());
    setAffectedCells(new Set());
  };

  // Handle browser navigation (back button, refresh, close tab)
  useEffect(() => {
    if (!isRotationEditMode && !isEditMode) return;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = 'You are in edit mode. Are you sure you want to leave?';
      return 'You are in edit mode. Are you sure you want to leave?';
    };

    const handlePopState = (event) => {
      setShowNavigationWarning(true);
      setPendingNavigation(window.location.pathname);
      
      // Push the current state back to prevent navigation
      setTimeout(() => {
        window.history.pushState(null, '', window.location.href);
      }, 0);
    };

    // Add history state to track back button
    window.history.pushState(null, '', window.location.href);

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isRotationEditMode, isEditMode]);

  // Handle navbar navigation clicks
  useEffect(() => {
    if (!isRotationEditMode && !isEditMode) return;

    const handleNavbarClick = (event) => {
      // Check if the clicked element is a navigation link
      const navLink = event.target.closest('a[href]');
      if (!navLink) return;

      const href = navLink.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

      // Prevent default navigation
      event.preventDefault();
      event.stopPropagation();
      
      // Show warning modal
      setPendingNavigation(href);
      setShowNavigationWarning(true);
    };

    // Add event listener to the document to catch all navigation clicks
    document.addEventListener('click', handleNavbarClick, true);
    
    return () => {
      document.removeEventListener('click', handleNavbarClick, true);
    };
  }, [isRotationEditMode, isEditMode]);

  // Handle save and leave
  const handleSaveAndLeave = async () => {
    try {
      if (isRotationEditMode) {
        await handleRotationSaveChanges();
      } else if (isEditMode) {
        await handleSaveChanges();
      }
      
      setShowNavigationWarning(false);
      const targetPath = pendingNavigation;
      setPendingNavigation(null);
      
      if (targetPath) {
        navigate(targetPath);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      setError('Failed to save changes. Please try again.');
    }
  };

  // Handle leave without saving
  const handleLeaveWithoutSaving = () => {
    // Reset edit states
    if (isRotationEditMode) {
      handleRotationCancel();
    } else if (isEditMode) {
      handleCancel();
    }
    
    setShowNavigationWarning(false);
    const targetPath = pendingNavigation;
    setPendingNavigation(null);
    
    if (targetPath) {
      navigate(targetPath);
    } else {
      // Default to going back
      navigate(-1);
    }
  };

  // Handle cancel navigation
  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  // When entering edit mode, store the original schedule
  const handleEditClick = () => {
    setEditedWeekendSchedules(
      JSON.parse(JSON.stringify(getCombinedWeekendSchedules()))
    );
    setOriginalWeekendSchedules(
      JSON.parse(JSON.stringify(getCombinedWeekendSchedules()))
    );
    setIsEditMode(true);
  };

  const handleRotationEditClick = () => {
    setEditedRotationSchedules(
      JSON.parse(JSON.stringify(schedules))
    );
    setOriginalRotationSchedules(
      JSON.parse(JSON.stringify(schedules))
    );
    setIsRotationEditMode(true);
  };

  const handleRotationSaveChanges = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Please log in to save changes");
        return;
      }

      // Save each PGY level schedule to database
      for (const [pgyLevel, scheduleData] of Object.entries(editedRotationSchedules)) {
        try {
          await axios.put(
            `${API_ENDPOINTS.SCHEDULES}/rotations/${pgyLevel}`,
            {
              pgyLevel,
              startDate: scheduleData.startDate,
              endDate: scheduleData.endDate,
              schedule: scheduleData.schedule,
              academicYear: scheduleData.academicYear,
              scheduleType: scheduleData.scheduleType,
              weekDuration: scheduleData.weekDuration,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
        } catch (error) {
          console.error(`Error updating PGY-${pgyLevel} schedule:`, error);
          setError(`Failed to update PGY-${pgyLevel} schedule. Please try again.`);
          return;
        }
      }

      // Update local state
      setSchedules(editedRotationSchedules);
      setIsRotationEditMode(false);
      setMovedRotations(new Map());
      setAffectedRotationCells(new Set());

      // Regenerate weekend call schedules
      const weekendSchedules = generateAndStoreWeekendSchedules(editedRotationSchedules);
      await saveWeekendCallSchedules(weekendSchedules);

      alert("Rotation schedules updated successfully!");
    } catch (error) {
      console.error("Error saving rotation changes:", error);
      setError("Failed to save changes. Please try again.");
    }
  };

  const handleRotationCancel = () => {
    setIsRotationEditMode(false);
    setEditedRotationSchedules({});
    setMovedRotations(new Map());
    setAffectedRotationCells(new Set());
  };

  const handleRotationChange = (residentId, month, newRotation) => {
    if (!isRotationEditMode) return;

    const newRotationSchedules = JSON.parse(JSON.stringify(editedRotationSchedules));

    // Find the PGY level for this resident
    let pgyLevel = null;
    Object.entries(newRotationSchedules).forEach(([pgy, scheduleData]) => {
      if (scheduleData.schedule && scheduleData.schedule[residentId]) {
        pgyLevel = pgy;
      }
    });

    if (!pgyLevel) return;

    // Update the rotation
    newRotationSchedules[pgyLevel].schedule[residentId].rotations[month] = newRotation;
    setEditedRotationSchedules(newRotationSchedules);

    // Update moved rotations tracking
    setMovedRotations((prev) => {
      const newMap = new Map(prev);
      const key = `${residentId}-${month}`;
      newMap.set(key, {
        residentId,
        month,
        newRotation,
        timestamp: Date.now(),
      });
      return newMap;
    });

    setAffectedRotationCells((prev) => {
      const newSet = new Set(prev);
      newSet.add(`${residentId}-${month}`);
      return newSet;
    });
  };

  // Add new function to export weekend call schedule to calendar
  const exportWeekendCallToCalendar = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Please log in to add events to calendar");
        return;
      }

      // Get current user info to check if they're a director
      const userResponse = await axios.get(
        `${API_ENDPOINTS.AUTH}/me`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const currentUserId = userResponse.data.id;
      const isDirector = userResponse.data.role === "director";

      if (!isDirector) {
        setError("Only directors can export schedules to calendars");
        return;
      }

      const currentMonth = getAllMonths(schedules)[currentMonthIndex];
      const weekendSchedules = isEditMode
        ? editedWeekendSchedules
        : getCombinedWeekendSchedules();
      let successCount = 0;
      let failCount = 0;
      let deletedCount = 0;

      // Get the start and end dates for the current month
      const [year, month] = currentMonth.split("-");
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);

      // Format dates in YYYY-MM-DD format
      const formattedStartDate = startDate.toISOString().split("T")[0];
      const formattedEndDate = endDate.toISOString().split("T")[0];

      console.log("Processing events for date range:", {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        originalStartDate: startDate.toISOString(),
        originalEndDate: endDate.toISOString(),
      });

      // First, collect all unique resident IDs from the weekend schedules
      const residentIds = new Set();
      Object.values(weekendSchedules).forEach((weekendData) => {
        Object.values(weekendData).forEach((data) => {
          data.residents.forEach((resident) => {
            residentIds.add(resident.id);
          });
        });
      });

      console.log("Residents to process:", Array.from(residentIds));

      // Delete events for each resident (only current month weekend calls)
      for (const residentId of residentIds) {
        try {
          console.log(`Attempting to delete events for resident ${residentId}`);
          const deleteResponse = await axios.delete(
            `${API_ENDPOINTS.EVENTS}/resident/${residentId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              params: {
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                isWeekendCall: "true"
              },
            }
          );
          console.log(
            `Delete response for resident ${residentId}:`,
            deleteResponse.data
          );
          deletedCount += deleteResponse.data.deletedCount || 0;
        } catch (error) {
          console.error(`Error deleting events for resident ${residentId}:`, {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
          });
        }
      }

      // Wait a moment to ensure deletions are processed
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Now create new events for each resident
      for (const [rotation, weekendData] of Object.entries(weekendSchedules)) {
        for (const [date, data] of Object.entries(weekendData)) {
          // Skip if not in current month
          if (!date.startsWith(currentMonth)) continue;

          // Create events for each resident
          for (const resident of data.residents) {
            // Parse the date string and create a new date object
            const [year, month, day] = date.split("-").map(Number);
            // Create date using moment to ensure proper timezone handling
            const eventDate = moment(`${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`, "YYYY-MM-DD");
            const eventDateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

            const eventData = {
              title: `${rotation} Weekend Call`,
              description: `Weekend call for ${resident.name} (PGY-${resident.year}) on ${rotation}`,
              start: eventDate.startOf('day').toISOString(),
              end: eventDate.startOf('day').add(23, 'hours').add(59, 'minutes').toISOString(),
              allDay: true,
              label: "Weekend Call",
              location: "Hospital",
              repeat: "none",
              userId: resident.id,
              residentId: resident.id,
              createdBy: currentUserId,
              isWeekendCall: true, // Set this to true for weekend call events
              isPublic: true,
              color: "#dc3545", // Red color for call shifts
              programId: parseInt(localStorage.getItem('programId'), 10)
            };

            try {
              // Create new event
              console.log("Creating new event:", {
                resident: resident.name,
                date: eventDateStr,
                rotation,
                originalDate: date,
                eventDate: eventDate.toISOString(),
              });
              const response = await axios.post(
                `${API_ENDPOINTS.EVENTS}/add`,
                eventData,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );
              console.log("Event created successfully:", response.data);
              successCount++;
            } catch (error) {
              console.error("Error creating event:", {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                eventData,
              });
              failCount++;
            }
          }
        }
      }

      if (successCount > 0) {
        showSuccess(`Successfully created ${successCount} weekend call events${failCount > 0 ? ` (${failCount} failed)` : ''}. Deleted ${deletedCount} existing events.`);
      } else {
        setError("Failed to create events. Check console for details.");
      }
    } catch (error) {
      console.error("Error exporting to calendar:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setError("Failed to export to calendar. Please try again.");
    }
  };

  const buildBlockDefinitionsForExport = (scheduleData) => {
    if (!scheduleData || !scheduleData.startDate || !scheduleData.endDate) {
      return { blocks: [], majorWeeksCount: 0, minorWeeksCount: 0, blockDurationValue: 0 };
    }

    const pattern = scheduleData.blockPattern || blockPattern || "4-1";
    const [majorWeeksCountRaw, minorWeeksCountRaw] = pattern
      .split("-")
      .map((val) => parseInt(val, 10) || 0);
    const majorWeeksCount = Math.max(0, majorWeeksCountRaw);
    const minorWeeksCount = Math.max(0, minorWeeksCountRaw);
    const defaultDuration = majorWeeksCount + minorWeeksCount || 5;
    const blockDurationValue =
      scheduleData.blockDuration || blockDuration || defaultDuration || 5;

    const weeks = getWeeksBetween(scheduleData.startDate, scheduleData.endDate);
    const blocks = [];

    for (let i = 0; i < weeks.length; i += blockDurationValue) {
      const blockWeeks = weeks.slice(i, i + blockDurationValue);
      if (!blockWeeks.length) continue;

      const blockIndex = blocks.length + 1;
      const weekStartNumber = i + 1;
      const weekEndNumber = i + blockWeeks.length;
      const startDate = new Date(blockWeeks[0].start);
      const endDate = new Date(blockWeeks[blockWeeks.length - 1].end);
      const dateRange = `${startDate.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })} - ${endDate.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })}`;

      blocks.push({
        index: blockIndex,
        label: `Block ${blockIndex} (Weeks ${weekStartNumber}-${weekEndNumber})`,
        dateRange,
        majorWeeks: blockWeeks.slice(0, majorWeeksCount),
        minorWeeks: blockWeeks.slice(majorWeeksCount, majorWeeksCount + minorWeeksCount),
      });
    }

    return { blocks, majorWeeksCount, minorWeeksCount, blockDurationValue };
  };

  const summarizeRotationGroups = (weeksArray, residentData) => {
    if (!weeksArray || !weeksArray.length || !residentData?.rotations) return [];

    const groups = [];
    let currentRotation = null;
    let count = 0;

    weeksArray.forEach((week) => {
      const rotation = residentData.rotations?.[week.start] || null;
      if (rotation && rotation === currentRotation) {
        count += 1;
      } else {
        if (currentRotation && count > 0) {
          groups.push({ rotation: currentRotation, weeks: count });
        }
        currentRotation = rotation;
        count = rotation ? 1 : 0;
      }
    });

    if (currentRotation && count > 0) {
      groups.push({ rotation: currentRotation, weeks: count });
    }

    return groups;
  };

  const formatBlockGroups = (groups) => {
    if (!groups.length) return "";
    return groups
      .map((group) => {
        if (!group.rotation) return "";
        const abbrev = abbreviateRotation(group.rotation);
        return `${abbrev}${group.weeks > 1 ? ` (${group.weeks}w)` : ""}`;
      })
      .filter(Boolean)
      .join(", ");
  };

  const getBlockCellContent = (residentData, block) => {
    const majorSummary = formatBlockGroups(
      summarizeRotationGroups(block.majorWeeks, residentData)
    );
    const minorSummary = formatBlockGroups(
      summarizeRotationGroups(block.minorWeeks, residentData)
    );
    return { majorSummary, minorSummary };
  };

  const formatBlockCellPlain = (content) => {
    const lines = [];
    if (content.majorSummary) lines.push(`Major: ${content.majorSummary}`);
    if (content.minorSummary) lines.push(`Minor: ${content.minorSummary}`);
    return lines.join("\n");
  };

  const formatBlockCellHTML = (content, majorGroups = [], minorGroups = []) => {
    const allParts = [];
    
    // Format major rotations with colors
    if (majorGroups.length > 0) {
      const majorParts = majorGroups.map(group => {
        if (!group.rotation) return '';
        const color = rotationColors[group.rotation] || '#e9ecef';
        const abbrev = abbreviateRotation(group.rotation);
        const label = `${abbrev}${group.weeks > 1 ? ` (${group.weeks}w)` : ''}`;
        return `<span style="background-color: ${color}; height: 100%; text-wrap: wrap; width: 40px; color: white; padding: 4px 8px; border-radius: 4px; margin-right: 4px; display: inline-block; font-weight: 500; font-size: 0.75rem;">${label}</span>`;
      }).filter(Boolean);
      allParts.push(...majorParts);
    }
    
    // Format minor rotations with colors
    if (minorGroups.length > 0) {
      const minorParts = minorGroups.map(group => {
        if (!group.rotation) return '';
        const color = rotationColors[group.rotation] || '#e9ecef';
        const abbrev = abbreviateRotation(group.rotation);
        const label = `${abbrev}${group.weeks > 1 ? ` (${group.weeks}w)` : ''}`;
        return `<span style="background-color: ${color}; height: 100%; color: white; text-wrap: wrap; width: 40px; padding: 4px 8px; border-radius: 4px; margin-right: 4px; display: inline-block; font-weight: 500; font-size: 0.75rem;">${label}</span>`;
      }).filter(Boolean);
      allParts.push(...minorParts);
    }
    
    // Fallback to content summaries if no groups
    if (allParts.length === 0) {
      if (content.majorSummary || content.minorSummary) {
        const summaries = [content.majorSummary, content.minorSummary].filter(Boolean);
        return `<div>${summaries.join(', ')}</div>`;
      }
      return '<div class="text-muted">—</div>';
    }
    
    return `<div>${allParts.join('')}</div>`;
  };

  const buildBlockWorksheetData = (pgyLevel, scheduleData) => {
    const { blocks } = buildBlockDefinitionsForExport(scheduleData);
    if (!blocks.length) return [];

    const header = ["Resident", ...blocks.map((block) => block.label)];
    const rows = [header];

    Object.entries(scheduleData.schedule || {})
      .filter(([, residentData]) => residentData?.year?.toString() === pgyLevel)
      .forEach(([, residentData]) => {
        const row = [residentData.name];
        blocks.forEach((block) => {
          const content = getBlockCellContent(residentData, block);
          row.push(formatBlockCellPlain(content));
        });
        rows.push(row);
      });

    return rows;
  };

  const PRINT_STYLES = `
    @page { size: landscape; margin: 1cm; }
    body {
      font-family: Arial, sans-serif;
      margin: 0;
      padding: 20px;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      background-color: #f8f9fa;
    }

    h3 { margin: 0; }
    p { margin: 0; }


            .logo-container {
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: flex-start;
            }
            .logo-text-container {  
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              justify-content: flex-start;
            }
            .logo-text-container h4 {
              margin: 0;
            }
            .logo-text-container p {
              font-size: .8rem;
              margin: 0;
            }
    .cell-content {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: row;
      text-wrap: no-wrap;
      
    }





    .schedule-section {
      margin-bottom: 40px;
      page-break-inside: avoid;
    }
    .block-cell {
      font-size: 0.75rem;
    }
    .text-muted { color: #6c757d; }
    .no-print {
      padding: 6px 12px;
      background-color: #0d6efd;
      border: none;
      color: white;
      border-radius: 4px;
      cursor: pointer;
    }
    .no-print:hover { background-color: #0b5ed7; }
    .page-break { page-break-before: always; }
  `;

  const buildMonthlyPrintSection = (pgyLevel, scheduleData) => {
    if (!scheduleData) return "";
    const months = getMonthsBetween(scheduleData.startDate, scheduleData.endDate);
    if (!months.length) return "";

    const headerCells = months
      .map((monthStr) => {
        const [year, monthNum] = monthStr.split("-");
        const date = new Date(year, parseInt(monthNum, 10) - 1);
        return `<th>${date.toLocaleString("default", { month: "short", year: "numeric" })}</th>`;
      })
      .join("");

    const rows = Object.entries(scheduleData.schedule || {})
      .filter(([, residentData]) => residentData?.year?.toString() === pgyLevel)
      .map(([, residentData]) => {
        const cells = months
          .map((monthStr) => {
            const rotation = residentData.rotations?.[monthStr];
            if (!rotation) return "<td></td>";
            const backgroundColor = rotationColors[rotation] || "#e9ecef";
            return `
              <td>
                <div class="rotation-cell" style="background-color: ${backgroundColor}; color: white;">
                  ${rotation}
                </div>
              </td>
            `;
          })
          .join("");
        return `
          <tr>
            <td>${residentData.name}</td>
            ${cells}
          </tr>
        `;
      })
      .join("");

    if (!rows) return "";

    return `
      <section class="schedule-section">
        <div class="print-header">
          <div>
            <h3>Rotation Schedule - PGY ${pgyLevel}</h3>
            <p>Generated on ${new Date().toLocaleDateString()}</p>
          </div>
          <button class="no-print" onclick="window.print()">Print Schedule</button>
        </div>
        <table class="schedule-table">
          <thead>
            <tr>
              <th>Resident</th>
              ${headerCells}
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </section>
    `;
  };

  const formatBlockCellHTMLForPrint = (majorGroups = [], minorGroups = []) => {
    const majorParts = majorGroups.map(group => {
      if (!group.rotation) return '';
      const color = rotationColors[group.rotation] || '#e9ecef';
      const abbrev = abbreviateRotation(group.rotation);
      const label = `${abbrev}${group.weeks > 1 ? ` (${group.weeks}w)` : ''}`;
      return `<span style="background-color: ${color}; color: white; padding: 10px; border-radius: 4px; display: inline-block; font-weight: 500; font-size: 0.75rem;">${label}</span>`;
    }).filter(Boolean);
    
    const minorParts = minorGroups.map(group => {
      if (!group.rotation) return '';
      const color = rotationColors[group.rotation] || '#e9ecef';
      const abbrev = abbreviateRotation(group.rotation);
      const label = `${abbrev}${group.weeks > 1 ? ` (${group.weeks}w)` : ''}`;
      return `<span class="rotation-cell" style="background-color: ${color}; color: white;">${label}</span>`;
    }).filter(Boolean);
    
    if (majorParts.length === 0 && minorParts.length === 0) {
      return '<div class="text-muted">—</div>';
    }
    
    return `<div>${majorParts.join('')}${minorParts.join('')}</div>`;
  };

  const formatMajorRotationsForPrint = (majorGroups = []) => {
    if (majorGroups.length === 0) {
      return {
        left: '<div class="text-muted">—</div>',
        right: '<div class="text-muted">—</div>',
        leftColspan: 1,
        rightColspan: 1
      };
    }

    // Check if there's a single 4-week rotation
    if (majorGroups.length === 1 && majorGroups[0].weeks === 4) {
      const group = majorGroups[0];
      const color = rotationColors[group.rotation] || '#e9ecef';
      const abbrev = abbreviateRotation(group.rotation);
      const label = `${abbrev} (${group.weeks}w)`;
      const cell = `<div class="rotation-cell-block" style="background-color: ${color};">${label}</div>`;
      return {
        left: cell,
        right: '',
        leftColspan: 2,
        rightColspan: 0
      };
    }

    // Check if there are two 2-week rotations
    if (majorGroups.length === 2 && majorGroups[0].weeks === 2 && majorGroups[1].weeks === 2) {
      const leftGroup = majorGroups[0];
      const rightGroup = majorGroups[1];
      const leftColor = rotationColors[leftGroup.rotation] || '#e9ecef';
      const rightColor = rotationColors[rightGroup.rotation] || '#e9ecef';
      const leftAbbrev = abbreviateRotation(leftGroup.rotation);
      const rightAbbrev = abbreviateRotation(rightGroup.rotation);
      const leftLabel = `${leftAbbrev} (${leftGroup.weeks}w)`;
      const rightLabel = `${rightAbbrev} (${rightGroup.weeks}w)`;
      
      return {
        left: `<div class="rotation-cell-block" style="background-color: ${leftColor}">${leftLabel}</div>`,
        right: `<div class="rotation-cell-block" style="background-color: ${rightColor}">${rightLabel}</div>`,
        leftColspan: 1,
        rightColspan: 1
      };
    }

    // Default: distribute rotations (first in left, rest in right or span if only one)
    const leftGroup = majorGroups[0];
    const leftColor = rotationColors[leftGroup.rotation] || '#e9ecef';
    const leftAbbrev = abbreviateRotation(leftGroup.rotation);
    const leftLabel = `${leftAbbrev}${leftGroup.weeks > 1 ? ` (${leftGroup.weeks}w)` : ''}`;
    const leftCell = `<div class="rotation-cell-block" style="background-color: ${leftColor}">${leftLabel}</div>`;

    if (majorGroups.length === 1) {
      // Single rotation that's not 4 weeks - span both columns
      return {
        left: leftCell,
        right: '',
        leftColspan: 2,
        rightColspan: 0
      };
    }

    // Multiple rotations - put first in left, rest in right
    const rightParts = majorGroups.slice(1).map(group => {
      const color = rotationColors[group.rotation] || '#e9ecef';
      const abbrev = abbreviateRotation(group.rotation);
      const label = `${abbrev}${group.weeks > 1 ? ` (${group.weeks}w)` : ''}`;
      return `<div class="rotation-cell-block" style="background-color: ${color}">${label}</div>`;
    }).join('');

    return {
      left: leftCell,
      right: rightParts || '<div class="text-muted">—</div>',
      leftColspan: 1,
      rightColspan: 1
    };
  };

  const buildBlockPrintSection = (pgyLevel, scheduleData) => {
    const { blocks } = buildBlockDefinitionsForExport(scheduleData);
    if (!blocks.length) return "";

    // Create header with block labels spanning 3 columns each (2 major + 1 minor)
    const headerCells = blocks
      .map(
        (block) =>
          `<th colspan="3" style="text-align: center;">${block.label}<div class="text-muted">${block.dateRange}</div></th>`
      )
      .join("");

    // Add sub-header row for Major (2 columns, no label) and Minor (1 column) labels
    const subHeaderCells = blocks
      .map(() => 
        `<th colspan="2">Major</th><th>Minor</th>`
      )
      .join("");

    const rows = Object.entries(scheduleData.schedule || {})
      .filter(([, residentData]) => residentData?.year?.toString() === pgyLevel)
      .map(([, residentData]) => {
        const cells = blocks
          .map((block) => {
            const majorGroups = summarizeRotationGroups(block.majorWeeks, residentData);
            const minorGroups = summarizeRotationGroups(block.minorWeeks, residentData);
            
            const majorFormatted = formatMajorRotationsForPrint(majorGroups);
            const minorFormatted = formatBlockCellHTMLForPrint([], minorGroups);
            
            // Build major cells (left and right)
            let majorCells = '';
            if (majorFormatted.leftColspan === 2) {
              // Single 4-week rotation spans both columns
              majorCells = `<td class="block-cell" colspan="2"><div class="cell-content">${majorFormatted.left}</div></td>`;
            } else {
              // Two columns for major
              majorCells = `<td class="block-cell"><div class="cell-content">${majorFormatted.left}</div></td>`;
              majorCells += `<td class="block-cell"><div class="cell-content">${majorFormatted.right}</div></td>`;
            }
            
            const minorCell = `<td class="block-cell"><div class="cell-content">${minorFormatted}</div></td>`;
            
            return majorCells + minorCell;
          })
          .join("");
        return `
          <tr>
            <td>${residentData.name}</td>
            ${cells}
          </tr>
        `;
      })
      .join("");

    if (!rows) return "";

    return `
      <section class="schedule-section">
        <div class="print-header">
          <div>
            <h3>Block Schedule - PGY ${pgyLevel}</h3>
            <p>Generated on ${new Date().toLocaleDateString()}</p>
          </div>
          <button class="no-print" onclick="window.print()">Print Schedule</button>
        </div>
        <table class="schedule-table">
          <thead>
            <tr>
              <th rowspan="2">Resident</th>
              ${headerCells}
            </tr>
            <tr>
              ${subHeaderCells}
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </section>
    `;
  };

  const buildPrintDocument = (title, sectionsHtml, pageSize = 'landscape') => {
    return buildPrintDocumentUtil({
      title,
      content: sectionsHtml,
      headerTitle: title,
      headerSubtitle: `Generated on ${new Date().toLocaleDateString()}`,
      showPrintButton: true,
      customStyles: PRINT_STYLES,
      pageSize
    });
  };

  const exportBlockScheduleToExcel = (pgyLevel, scheduleData) => {
    try {
      const XLSX = require("xlsx");
      const { blocks } = buildBlockDefinitionsForExport(scheduleData);
      if (!blocks.length) {
        setError("No block schedule data available to export.");
        return;
      }

      // Create worksheet data
      const wsData = [];
      const header = ["Resident", ...blocks.map((block) => block.label)];
      wsData.push(header);

      // Add data rows with rotation information
      Object.entries(scheduleData.schedule || {})
        .filter(([, residentData]) => residentData?.year?.toString() === pgyLevel)
        .forEach(([, residentData]) => {
          const row = [residentData.name];
          blocks.forEach((block) => {
            const content = getBlockCellContent(residentData, block);
            row.push(formatBlockCellPlain(content));
          });
          wsData.push(row);
        });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
      const colWidths = [
        { wch: 20 }, // Resident name column
        ...blocks.map(() => ({ wch: 25 })), // Block columns
      ];
      ws["!cols"] = colWidths;

      // Add colors to cells based on rotations
      let rowIndex = 1; // Start after header
      Object.entries(scheduleData.schedule || {})
        .filter(([, residentData]) => residentData?.year?.toString() === pgyLevel)
        .forEach(([, residentData]) => {
          blocks.forEach((block, blockIndex) => {
            const majorGroups = summarizeRotationGroups(block.majorWeeks, residentData);
            const minorGroups = summarizeRotationGroups(block.minorWeeks, residentData);
            
            // Get the primary rotation color (first major rotation, or first minor if no major)
            const primaryRotation = majorGroups.length > 0 
              ? majorGroups[0].rotation 
              : (minorGroups.length > 0 ? minorGroups[0].rotation : null);
            
            if (primaryRotation && rotationColors[primaryRotation]) {
              const cellRef = XLSX.utils.encode_cell({
                r: rowIndex,
                c: blockIndex + 1, // +1 for resident name column
              });
              if (!ws[cellRef]) ws[cellRef] = { v: formatBlockCellPlain(getBlockCellContent(residentData, block)) };
              ws[cellRef].s = {
                fill: {
                  fgColor: { rgb: rotationColors[primaryRotation].replace("#", "") },
                  patternType: "solid",
                },
                font: { color: { rgb: "FFFFFF" }, bold: true },
                alignment: { wrapText: true, vertical: "top" },
              };
            } else {
              // Set alignment for cells without color
              const cellRef = XLSX.utils.encode_cell({
                r: rowIndex,
                c: blockIndex + 1,
              });
              if (ws[cellRef]) {
                ws[cellRef].s = {
                  ...(ws[cellRef].s || {}),
                  alignment: { wrapText: true, vertical: "top" },
                };
              }
            }
          });
          rowIndex++;
        });

      // Style header
      const headerRange = XLSX.utils.decode_range(ws["!ref"]);
      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!ws[cellRef]) ws[cellRef] = { v: "" };
        ws[cellRef].s = ws[cellRef].s || {};
        ws[cellRef].s.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        };
        ws[cellRef].s.font = { bold: true };
        ws[cellRef].s.alignment = { wrapText: true };
      }

      XLSX.utils.book_append_sheet(wb, ws, `PGY${pgyLevel}-Blocks`.slice(0, 31));
      const filename = `PGY${pgyLevel}_Block_Schedule_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error("Error exporting block schedule to Excel:", error);
      setError("Failed to export block schedule to Excel. Please try again.");
    }
  };

  const exportBlockScheduleToPrint = (pgyLevel, scheduleData) => {
    const section = buildBlockPrintSection(pgyLevel, scheduleData);
    if (!section) {
      setError("No block schedule data available to print.");
      return;
    }
    const printContent = buildPrintDocument(`Block Schedule - PGY ${pgyLevel}`, section, 'landscape');
    openPrintWindow(printContent);
  };

  const exportStandardScheduleToPrint = (pgyLevel, scheduleData) => {
    const section = buildMonthlyPrintSection(pgyLevel, scheduleData);
    if (!section) {
      setError("No schedule data available to print.");
      return;
    }
    const printContent = buildPrintDocument(`Rotation Schedule - PGY ${pgyLevel}`, section, 'landscape');
    openPrintWindow(printContent);
  };

  const exportToExcel = (pgyLevel, scheduleData) => {
    if (scheduleData?.scheduleType === "blocks") {
      exportBlockScheduleToExcel(pgyLevel, scheduleData);
      return;
    }
    try {
      const XLSX = require("xlsx");

      // Get months between start and end date
      const months = getMonthsBetween(
        scheduleData.startDate,
        scheduleData.endDate
      );

      // Create worksheet data
      const wsData = [];

      // Add header row
      const header = [
        "Resident",
        ...months.map((monthStr) => {
          const [year, monthNum] = monthStr.split("-");
          const date = new Date(year, parseInt(monthNum) - 1);
          return date.toLocaleString("default", {
            month: "short",
            year: "numeric",
          });
        }),
      ];
      wsData.push(header);

      // Add data rows
      Object.entries(scheduleData.schedule)
        .filter(
          ([_, residentData]) => residentData.year?.toString() === pgyLevel
        )
        .forEach(([_, residentData]) => {
          const row = [residentData.name];
          months.forEach((monthStr) => {
            const rotation = residentData.rotations[monthStr];
            row.push(rotation || "-");
          });
          wsData.push(row);
        });

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
      const colWidths = [
        { wch: 20 }, // Resident name column
        ...months.map(() => ({ wch: 15 })), // Month columns
      ];
      ws["!cols"] = colWidths;

      // Add colors to cells
      Object.entries(scheduleData.schedule)
        .filter(
          ([_, residentData]) => residentData.year?.toString() === pgyLevel
        )
        .forEach(([_, residentData], rowIndex) => {
          months.forEach((monthStr, colIndex) => {
            const rotation = residentData.rotations[monthStr];
            if (rotation) {
              const cellRef = XLSX.utils.encode_cell({
                r: rowIndex + 1,
                c: colIndex + 1,
              });
              if (!ws[cellRef]) ws[cellRef] = { v: rotation };
              ws[cellRef].s = {
                fill: {
                  fgColor: { rgb: rotationColors[rotation].replace("#", "") },
                  patternType: "solid",
                },
                font: { color: { rgb: "FFFFFF" } },
              };
            }
          });
        });

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `PGY-${pgyLevel} Schedule`);

      // Generate filename
      const filename = `PGY-${pgyLevel}_Schedule_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      setError("Failed to export to Excel. Please try again.");
    }
  };

  const exportAllToExcel = () => {
    try {
      const XLSX = require("xlsx");

      const scheduleEntries = Object.entries(schedules || {});
      if (!scheduleEntries.length) {
        setError("No schedules available to export.");
        return;
      }

      const wb = XLSX.utils.book_new();
      const nonBlockEntries = scheduleEntries.filter(
        ([, scheduleData]) => scheduleData?.scheduleType !== "blocks"
      );

      if (nonBlockEntries.length > 0) {
      const allMonths = new Set();
        nonBlockEntries.forEach(([, scheduleData]) => {
        const months = getMonthsBetween(
          scheduleData.startDate,
          scheduleData.endDate
        );
        months.forEach((month) => allMonths.add(month));
      });
      const sortedMonths = Array.from(allMonths).sort();

      const wsData = [];
      const header = [
        "PGY",
        "Resident",
        ...sortedMonths.map((monthStr) => {
          const [year, monthNum] = monthStr.split("-");
            const date = new Date(year, parseInt(monthNum, 10) - 1);
          return date.toLocaleString("default", {
            month: "short",
            year: "numeric",
          });
        }),
      ];
      wsData.push(header);

        nonBlockEntries.forEach(([pgyLevel, scheduleData]) => {
          Object.entries(scheduleData.schedule || {}).forEach(
            ([, residentData]) => {
              if (residentData.year?.toString() !== pgyLevel) return;
            const row = [`PGY-${pgyLevel}`, residentData.name];
            sortedMonths.forEach((monthStr) => {
                const rotation = residentData.rotations?.[monthStr];
                row.push(rotation || "");
            });
            wsData.push(row);
            }
          );
      });

        if (wsData.length > 1) {
      const ws = XLSX.utils.aoa_to_sheet(wsData);
          ws["!cols"] = [
            { wch: 8 },
            { wch: 20 },
            ...sortedMonths.map(() => ({ wch: 15 })),
          ];
          XLSX.utils.book_append_sheet(wb, ws, "Monthly_Weekly");
        }
      }

      const blockEntries = scheduleEntries.filter(
        ([, scheduleData]) => scheduleData?.scheduleType === "blocks"
      );

      blockEntries.forEach(([pgyLevel, scheduleData]) => {
        const { blocks } = buildBlockDefinitionsForExport(scheduleData);
        if (!blocks.length) return;

        const wsData = [];
        const header = ["Resident", ...blocks.map((block) => block.label)];
        wsData.push(header);

        Object.entries(scheduleData.schedule || {})
          .filter(([, residentData]) => residentData?.year?.toString() === pgyLevel)
          .forEach(([, residentData]) => {
            const row = [residentData.name];
            blocks.forEach((block) => {
              const content = getBlockCellContent(residentData, block);
              row.push(formatBlockCellPlain(content));
            });
            wsData.push(row);
          });

        if (wsData.length > 1) {
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
          ws["!cols"] = [
            { wch: 20 },
            ...blocks.map(() => ({ wch: 25 })),
          ];

      // Add colors to cells
          let rowIndex = 1;
          Object.entries(scheduleData.schedule || {})
            .filter(([, residentData]) => residentData?.year?.toString() === pgyLevel)
            .forEach(([, residentData]) => {
              blocks.forEach((block, blockIndex) => {
                const majorGroups = summarizeRotationGroups(block.majorWeeks, residentData);
                const minorGroups = summarizeRotationGroups(block.minorWeeks, residentData);
                
                const primaryRotation = majorGroups.length > 0 
                  ? majorGroups[0].rotation 
                  : (minorGroups.length > 0 ? minorGroups[0].rotation : null);
                
                if (primaryRotation && rotationColors[primaryRotation]) {
                const cellRef = XLSX.utils.encode_cell({
                  r: rowIndex,
                    c: blockIndex + 1,
                  });
                  if (!ws[cellRef]) ws[cellRef] = { v: formatBlockCellPlain(getBlockCellContent(residentData, block)) };
                ws[cellRef].s = {
                  fill: {
                      fgColor: { rgb: rotationColors[primaryRotation].replace("#", "") },
                    patternType: "solid",
                  },
                    font: { color: { rgb: "FFFFFF" }, bold: true },
                    alignment: { wrapText: true, vertical: "top" },
                  };
                } else {
                  const cellRef = XLSX.utils.encode_cell({
                    r: rowIndex,
                    c: blockIndex + 1,
                  });
                  if (ws[cellRef]) {
                    ws[cellRef].s = {
                      ...(ws[cellRef].s || {}),
                      alignment: { wrapText: true, vertical: "top" },
                    };
                  }
              }
            });
            rowIndex++;
      });

          // Style header
      const headerRange = XLSX.utils.decode_range(ws["!ref"]);
      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!ws[cellRef]) ws[cellRef] = { v: "" };
        ws[cellRef].s = ws[cellRef].s || {};
        ws[cellRef].s.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        };
        ws[cellRef].s.font = { bold: true };
        ws[cellRef].s.alignment = { wrapText: true };
      }

          XLSX.utils.book_append_sheet(wb, ws, `PGY${pgyLevel}-Blocks`.slice(0, 31));
        }
      });

      if (!wb.SheetNames.length) {
        setError("No schedules available to export.");
        return;
      }

      const filename = `All_Rotation_Schedules_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error("Error exporting all schedules to Excel:", error);
      setError("Failed to export schedules. Please try again.");
    }
  };

  const saveWeekendCallSchedules = async (schedules) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Please log in to save schedules");
        return false;
      }

      // Save the schedules to the database
      const response = await axios.post(
        `${API_ENDPOINTS.SCHEDULES}/weekend-call`,
        { schedules },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("Weekend call schedules saved to database:", response.data);
      setWeekendCallSchedules(schedules);
      return true;
    } catch (error) {
      console.error("Error saving weekend call schedules:", error);
      setError("Failed to save weekend call schedules. Please try again.");
      return false;
    }
  };

  const generateWeekendCallSchedule = async () => {
    try {
      // Get the token from localStorage
      const token = localStorage.getItem("token");
      if (!token) {
        setError("You must be logged in to generate weekend call schedules");
        return;
      }

      // Get all residents
      const residents = await axios.get(
        `${API_ENDPOINTS.USERS}/residents`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Get all rotations
      const rotations = Object.keys(weekendCallSchedules);

      // Generate schedule for each rotation
      const newSchedule = {};
      for (const rotation of rotations) {
        newSchedule[rotation] = {};
        const rotationResidents = residents.data.filter(
          (r) => r.rotation === rotation
        );

        // Get all weekends between start and end date
        const weekends = [];
        const currentDate = new Date(startDate);
        const end = new Date(endDate);

        while (currentDate <= end) {
          if (currentDate.getDay() === 6 || currentDate.getDay() === 0) {
            weekends.push(new Date(currentDate));
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }

        // Assign residents to weekends
        let residentIndex = 0;
        for (const weekend of weekends) {
          const date = weekend.toISOString().split("T")[0];
          const type = weekend.getDay() === 6 ? "Saturday" : "Sunday";

          // Get next resident
          const resident = rotationResidents[residentIndex];
          residentIndex = (residentIndex + 1) % rotationResidents.length;

          // Add to schedule
          newSchedule[rotation][date] = {
            date,
            residents: [
              {
                id: resident.id,
                name: `${resident.firstName} ${resident.lastName}`,
                year: resident.year,
              },
            ],
            type,
          };
        }
      }

      // Save to database
      await axios.post(
        `${API_ENDPOINTS.SCHEDULES}/weekend-call`,
        { schedules: newSchedule },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update state
      setWeekendCallSchedules(newSchedule);
      setError(null);
    } catch (error) {
      console.error("Error generating weekend call schedule:", error);
      setError("Failed to generate weekend call schedule");
    }
  };

  // Add this function before the return statement
  const exportWeekendCallToPrint = () => {
    const currentMonth = getAllMonths(schedules)[currentMonthIndex];
    const weekendSchedules = isEditMode
      ? editedWeekendSchedules
      : getCombinedWeekendSchedules();
    const weekends = getWeekendsForMonth(currentMonth);

    const monthLabel = (() => {
      const [year, month] = currentMonth.split("-");
      return new Date(year, parseInt(month) - 1).toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
    })();

    const tableContent = `
      <table class="schedule-table">
        <thead>
          <tr>
            <th>Rotation</th>
            ${weekends.map(weekend => `
              <th colspan="2">
                ${(() => {
                  const [year, month, day] = weekend.saturday.split("-").map(Number);
                  const saturdayDate = new Date(year, month - 1, day);
                  return saturdayDate.toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  });
                })()}
                - 
                ${(() => {
                  const [year, month, day] = weekend.sunday.split("-").map(Number);
                  const sundayDate = new Date(year, month - 1, day);
                  return sundayDate.toLocaleDateString(undefined, {
                    day: "numeric",
                  });
                })()}
              </th>
            `).join('')}
          </tr>
          <tr>
            <th></th>
            ${weekends.map(() => `
              <th>Saturday</th>
              <th>Sunday</th>
            `).join('')}
          </tr>
        </thead>
        <tbody>
          ${Object.entries(weekendSchedules).map(([rotation, weekendData]) => `
            <tr>
              <td>${rotation}</td>
              ${weekends.map(weekend => `
                <td>
                  ${weekendData[weekend.saturday]?.residents.map(resident => 
                    `<div class="resident-cell">${capitalizeName(resident.name)} (PGY-${resident.year})</div>`
                  ).join('') || '<div class="text-muted">No residents assigned</div>'}
                </td>
                <td>
                  ${weekendData[weekend.sunday]?.residents.map(resident => 
                    `<div class="resident-cell">${capitalizeName(resident.name)} (PGY-${resident.year})</div>`
                  ).join('') || '<div class="text-muted">No residents assigned</div>'}
                </td>
              `).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;

    const customStyles = `

  

    `;

    const printContent = buildPrintDocumentUtil({
      title: 'Weekend Call Schedule',
      content: tableContent,
      headerTitle: 'Weekend Call Schedule',
      headerSubtitle: `${monthLabel}`,
      showPrintButton: true,
      additionalHeaderInfo: [
        { label: 'Generated', value: new Date().toLocaleDateString() }
      ],
      customStyles,
      pageSize: 'landscape'
    });

    openPrintWindow(printContent);
  };

  // Add this function before the return statement
  const exportWeekendCallToExcel = () => {
    try {
      const XLSX = require("xlsx");

      // Get the current month's weekend schedule
      const currentMonth = getAllMonths(schedules)[currentMonthIndex];
      const weekendSchedules = isEditMode
        ? editedWeekendSchedules
        : getCombinedWeekendSchedules();
      const weekends = getWeekendsForMonth(currentMonth);

      // Create worksheet data
      const wsData = [];

      // Add header row
      const header = ["Rotation"];
      weekends.forEach((weekend) => {
        header.push(
          `${(() => {
            const [year, month, day] = weekend.saturday.split("-").map(Number);
            const saturdayDate = new Date(year, month - 1, day);
            return saturdayDate.toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
            });
          })()} - ${(() => {
            const [year, month, day] = weekend.sunday.split("-").map(Number);
            const sundayDate = new Date(year, month - 1, day);
            return sundayDate.toLocaleDateString(undefined, {
              day: "numeric",
            });
          })()}`
        );
      });
      wsData.push(header);

      // Add subheader row for Sat/Sun
      const subheader = [""];
      weekends.forEach(() => {
        subheader.push("Saturday", "Sunday");
      });
      wsData.push(subheader);

      // Add data rows for each rotation
      Object.entries(weekendSchedules).forEach(([rotation, weekendData]) => {
        const row = [rotation];
        weekends.forEach((weekend) => {
          // Add Saturday residents
          const saturdayResidents =
            weekendData[weekend.saturday]?.residents || [];
          row.push(
            saturdayResidents.map((r) => `${capitalizeName(r.name)} (PGY-${r.year})`).join("\n")
          );

          // Add Sunday residents
          const sundayResidents = weekendData[weekend.sunday]?.residents || [];
          row.push(
            sundayResidents.map((r) => `${capitalizeName(r.name)} (PGY-${r.year})`).join("\n")
          );
        });
        wsData.push(row);
      });

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
      const colWidths = [
        { wch: 20 }, // Rotation column
        ...weekends.map(() => ({ wch: 25 })), // Weekend columns (each weekend has Sat/Sun)
      ];
      ws["!cols"] = colWidths;

      // Add borders and styling to header
      const headerRange = XLSX.utils.decode_range(ws["!ref"]);
      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!ws[cellRef]) ws[cellRef] = { v: "" };
        ws[cellRef].s = ws[cellRef].s || {};
        ws[cellRef].s.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        };
        ws[cellRef].s.font = { bold: true };
        ws[cellRef].s.alignment = { wrapText: true };
      }

      // Style subheader
      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: 1, c: C });
        if (!ws[cellRef]) ws[cellRef] = { v: "" };
        ws[cellRef].s = ws[cellRef].s || {};
        ws[cellRef].s.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        };
        ws[cellRef].s.font = { bold: true };
        ws[cellRef].s.alignment = { wrapText: true };
      }

      // Style data cells
      for (let R = 2; R <= headerRange.e.r; ++R) {
        for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
          const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellRef]) ws[cellRef] = { v: "" };
          ws[cellRef].s = {
            border: {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            },
            alignment: { wrapText: true, vertical: "top" },
          };
        }
      }

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Weekend Call Schedule");

      // Generate filename
      const [year, month] = currentMonth.split("-");
      const filename = `Weekend_Call_Schedule_${year}_${month}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error("Error exporting weekend call schedule to Excel:", error);
      setError("Failed to export to Excel. Please try again.");
    }
  };

  // Helper to generate and store weekend call schedules for all months
  const generateAndStoreWeekendSchedules = (rotationSchedules) => {
    return generateAndStoreWeekendSchedulesUtil(rotationSchedules, offServiceResidents);
  };
  
  // Old implementation removed - now using imported version

  // New: Generate weekend schedules with options (mode, excluded rotations)
  const generateWeekendSchedulesWithOptions = (rotationSchedules, opts = {}) => {
    const offServiceResidents = opts.offServiceResidents || [];
    const base = generateAndStoreWeekendSchedules(rotationSchedules, offServiceResidents);
    const mode = opts.mode || 'one-day-off';
    const excluded = new Set(opts.excludedRotations || []);
    const blockRotationRanges = buildBlockRotationRanges(rotationSchedules);

    const formatDateKey = (dateObj) => {
      if (!dateObj) return null;
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

    const getWeekendFromEntry = (dateStr, type = 'Saturday') => {
      if (!dateStr) return null;
      const baseDate = new Date(`${dateStr}T00:00:00`);
      if (isNaN(baseDate.getTime())) return null;

      if (type === 'Sunday') {
        const saturday = new Date(baseDate);
        saturday.setDate(saturday.getDate() - 1);
        return {
          saturday: formatDateKey(saturday),
          sunday: dateStr,
        };
      }

      const sunday = new Date(baseDate);
      sunday.setDate(sunday.getDate() + 1);
      return {
        saturday: dateStr,
        sunday: formatDateKey(sunday),
      };
    };

    const enforceBlockWeekendBoundaries = () => {
      Object.entries(base).forEach(([rotationName, rotationData]) => {
        Object.entries(rotationData || {}).forEach(([dateKey, entry]) => {
          if (!entry?.residents || entry.residents.length === 0) return;
          const weekend = getWeekendFromEntry(dateKey, entry.type || 'Saturday');
          if (!weekend) return;

          entry.residents = entry.residents.filter((resident) => {
            if (!resident || !resident.id) return true;
            const ranges = blockRotationRanges?.[resident.id]?.[rotationName];
            if (!ranges || ranges.length === 0) return true;
            return weekendFallsWithinBlockRange(ranges, weekend);
          });
            });
          });
    };

    // 1) Exclude entire rotations if requested
    if (excluded.size > 0) {
      Object.keys(base).forEach((rotation) => {
        if (excluded.has(rotation)) {
          Object.keys(base[rotation] || {}).forEach((dateKey) => {
            base[rotation][dateKey].residents = [];
          });
        }
      });
    }

    // 2) Gold-weekend: Each resident works both Sat+Sun for exactly 2 weekends, off for 2 weekends
    // If 5 weekends: 2 off, 2 on, 1 split (half work both days, half off)
    if (mode === 'gold-weekend') {
      // First, clear all existing assignments for gold-weekend mode
      Object.keys(base).forEach((rotation) => {
        Object.keys(base[rotation] || {}).forEach((dateKey) => {
          base[rotation][dateKey].residents = [];
        });
      });

      // Build block definitions for each schedule to determine which block a weekend falls into
      const blockDefinitionsBySchedule = {};
      Object.entries(rotationSchedules).forEach(([pgyLevel, scheduleData]) => {
        if (scheduleData?.scheduleType === 'blocks' && scheduleData?.startDate && scheduleData?.endDate) {
          const pattern = scheduleData.blockPattern || "4-1";
          const [majorWeeksCountRaw, minorWeeksCountRaw] = pattern
            .split("-")
            .map((val) => parseInt(val, 10) || 0);
          const majorWeeksCount = Math.max(0, majorWeeksCountRaw);
          const minorWeeksCount = Math.max(0, minorWeeksCountRaw);
          const defaultDuration = majorWeeksCount + minorWeeksCount || 5;
          const blockDurationValue = scheduleData.blockDuration || defaultDuration || 5;

          const weeks = getWeeksBetween(scheduleData.startDate, scheduleData.endDate);
          const blocks = [];

          for (let i = 0; i < weeks.length; i += blockDurationValue) {
            const blockWeeks = weeks.slice(i, i + blockDurationValue);
            if (!blockWeeks.length) continue;

            const blockIndex = blocks.length + 1;
            const startDate = new Date(blockWeeks[0].start);
            const endDate = new Date(blockWeeks[blockWeeks.length - 1].end);

            blocks.push({
              index: blockIndex,
              startDate,
              endDate,
              majorWeeks: blockWeeks.slice(0, majorWeeksCount),
              minorWeeks: blockWeeks.slice(majorWeeksCount, majorWeeksCount + minorWeeksCount),
            });
          }

          blockDefinitionsBySchedule[pgyLevel] = blocks;
        }
      });

      // Helper function to parse date string
      const parseDate = (dateStr) => {
        if (!dateStr) return null;
        const date = new Date(`${dateStr}T00:00:00`);
        return isNaN(date.getTime()) ? null : date;
      };

      // Helper function to determine which block a weekend falls into for a given schedule
      const getBlockIndexForWeekend = (weekend, scheduleData, pgyLevel) => {
        if (scheduleData?.scheduleType !== 'blocks') return null;
        const blocks = blockDefinitionsBySchedule[pgyLevel];
        if (!blocks || !blocks.length) return null;

        const saturdayDate = parseDate(weekend.saturday);
        const sundayDate = weekend.sunday ? parseDate(weekend.sunday) : null;
        
        // Check if either Saturday or Sunday falls within a block
        for (const block of blocks) {
          const saturdayInBlock = saturdayDate && saturdayDate >= block.startDate && saturdayDate <= block.endDate;
          const sundayInBlock = sundayDate && sundayDate >= block.startDate && sundayDate <= block.endDate;
          
          if (saturdayInBlock || sundayInBlock) {
            return block.index;
          }
        }
        return null;
      };

      // Get all months from rotation schedules
      const allMonths = new Set();
      Object.values(rotationSchedules).forEach((scheduleData) => {
        if (!scheduleData?.startDate || !scheduleData?.endDate) return;
        const months = getMonthsBetween(scheduleData.startDate, scheduleData.endDate);
        months.forEach((m) => allMonths.add(m));
      });
      const months = Array.from(allMonths).sort();

      // Track which residents worked 3 weekends in previous 5-weekend months (per rotation)
      // Format: { rotation: { residentId: true if worked 3 weekends in last 5-week month } }
      const threeWeekendWorkers = {};

      // Helper function to pair residents for a single weekend using PGY-aware logic
      // Returns an object with saturdayPairs and sundayPairs arrays
      // weekendSeed: a unique identifier (like weekend date) to ensure different pairings each weekend
      const pairResidentsForWeekend = (residents, weekendSeed = '') => {
        console.log('\n🟢 === pairResidentsForWeekend START ===');
        console.log('Weekend Seed (date):', weekendSeed);
        console.log('Residents to pair:', residents.map(r => `${r.name} (PGY-${r.year})`));
        
        if (residents.length === 0) return { saturdayPairs: [], sundayPairs: [] };
        if (residents.length === 1) {
          return { saturdayPairs: [residents], sundayPairs: [residents] };
        }

        // Create a seeded random function based on weekend seed to ensure different pairings
        // Convert weekend seed (date string) to a numeric seed
        let seed = 0;
        if (weekendSeed) {
          // Use the date string as a seed - convert to number for better distribution
          // Multiply by position to ensure different dates produce very different seeds
          seed = weekendSeed.split('').reduce((acc, char, idx) => {
            return acc + (char.charCodeAt(0) * Math.pow(31, idx));
          }, 0);
          console.log('Initial seed from date string:', seed);
          // Add some extra variation and ensure it's a large prime-based seed
          seed = (seed * 7919 + 15485863) % 2147483647;
          console.log('After prime multiplication:', seed);
          // Add additional variation based on residents count and their IDs to ensure different pairings
          // Include resident IDs in seed to make it more unique even with same count
          const residentIdsHash = residents.reduce((acc, r, idx) => {
            const idHash = typeof r.id === 'string' 
              ? r.id.split('').reduce((s, c, i) => s + c.charCodeAt(0) * (i + 1), 0)
              : r.id;
            return acc + idHash * (idx + 1);
          }, 0);
          seed = (seed ^ (residents.length * 1234567) ^ (residentIdsHash * 9876543)) % 2147483647;
          console.log('Final seed after XOR with resident count and IDs:', seed);
        } else {
          // Fallback to time-based seed if no weekend seed provided
          seed = Date.now() % 2147483647;
          console.log('Using time-based seed:', seed);
        }
        
        const initialSeed = seed; // Store initial seed for logging
        const seededRandom = () => {
          seed = (seed * 9301 + 49297) % 233280;
          return seed / 233280;
        };

        // Shuffle residents using seeded random to ensure different pairings each weekend
        // Pre-generate random values for each resident to ensure proper shuffling
        const residentsWithRandom = residents.map(r => ({ resident: r, random: seededRandom() }));
        console.log('Residents with random values:', residentsWithRandom.map(r => `${r.resident.name}: ${r.random.toFixed(4)}`));
        residentsWithRandom.sort((a, b) => a.random - b.random);
        const shuffled = residentsWithRandom.map(item => item.resident);
        console.log('Shuffled order:', shuffled.map(r => `${r.name} (PGY-${r.year})`));
        
        const pgy1 = shuffled.filter(r => r.year === 1);
        const pgy2 = shuffled.filter(r => r.year === 2);
        const pgy3 = shuffled.filter(r => r.year === 3);
        const pgy4 = shuffled.filter(r => r.year === 4);
        const pgy5 = shuffled.filter(r => r.year === 5);
        
        const saturdayPairs = [];
        const sundayPairs = [];
        const used = new Set();
        
        // Helper to find unused high-level resident for PGY-1
        // Rule: if there is a PGY-1 and a PGY-3/4/5, pair with those levels if feasible (avoid PGY-2 unless no 3/4/5)
        const findUnusedHighPGY = (used) => {
          const avail4 = pgy4.filter(r => !used.has(r.id));
          const avail5 = pgy5.filter(r => !used.has(r.id));
          const avail3 = pgy3.filter(r => !used.has(r.id));
          // Combine 4/5/3 together and pick randomly (slight natural variation from shuffle)
          const poolHigh = [...avail4, ...avail5, ...avail3].sort(() => seededRandom() - 0.5);
          if (poolHigh.length > 0) return poolHigh[0];
          // Fallback to PGY-2 only if no 3/4/5 available
          const avail2 = pgy2.filter(r => !used.has(r.id)).sort(() => seededRandom() - 0.5);
          if (avail2.length > 0) return avail2[0];
          return null;
        };
        
        // Helper to find unused high-level resident for PGY-2 (returns random from available)
        const findUnusedHighPGYFor2 = (used) => {
          const available4 = pgy4.filter(r => !used.has(r.id)).sort(() => seededRandom() - 0.5);
          if (available4.length > 0) return available4[0];
          const available5 = pgy5.filter(r => !used.has(r.id)).sort(() => seededRandom() - 0.5);
          if (available5.length > 0) return available5[0];
          const available3 = pgy3.filter(r => !used.has(r.id)).sort(() => seededRandom() - 0.5);
          if (available3.length > 0) return available3[0];
          return null;
        };

        console.log('PGY-1 residents:', pgy1.map(r => `${r.name} (PGY-${r.year})`));
        console.log('PGY-2 residents:', pgy2.map(r => `${r.name} (PGY-${r.year})`));
        console.log('PGY-3 residents:', pgy3.map(r => `${r.name} (PGY-${r.year})`));
        console.log('PGY-4 residents:', pgy4.map(r => `${r.name} (PGY-${r.year})`));
        console.log('PGY-5 residents:', pgy5.map(r => `${r.name} (PGY-${r.year})`));
        
        // Pair PGY-1 residents first (process in shuffled order)
        // PGY-1 MUST be paired with someone higher (2-5), never with another PGY-1
        pgy1.forEach((r1) => {
          if (used.has(r1.id)) return;
          
          console.log(`\nPairing PGY-1: ${r1.name}`);
          // Try to find levels 3/4/5 (either level is acceptable per rule). Fallback to 2 if none.
          const highPGY = findUnusedHighPGY(used);
          if (highPGY) {
            console.log(`  -> Paired with ${highPGY.name} (PGY-${highPGY.year})`);
            saturdayPairs.push([r1, highPGY]);
            sundayPairs.push([r1, highPGY]);
            used.add(r1.id);
            used.add(highPGY.id);
          } else {
            // Try to find ANY resident from levels 2-5 (pick randomly from available)
            // This ensures PGY-1 is never paired with another PGY-1
            const availableHigher = shuffled.filter(r => r.year >= 2 && r.year <= 5 && !used.has(r.id)).sort(() => seededRandom() - 0.5);
            console.log(`  -> No preferred high PGY found, available higher levels:`, availableHigher.map(r => `${r.name} (PGY-${r.year})`));
            if (availableHigher.length > 0) {
              const anyHigher = availableHigher[0];
              console.log(`  -> Paired with ${anyHigher.name} (PGY-${anyHigher.year})`);
              saturdayPairs.push([r1, anyHigher]);
              sundayPairs.push([r1, anyHigher]);
              used.add(r1.id);
              used.add(anyHigher.id);
            } else {
              // No higher-level resident available - PGY-1 must work alone (shouldn't happen in normal cases)
              // This is better than pairing with another PGY-1
              console.log(`  -> No higher-level resident available, working alone`);
              saturdayPairs.push([r1]);
              sundayPairs.push([r1]);
              used.add(r1.id);
            }
          }
        });

        // Pair PGY-2 residents (process in shuffled order)
        pgy2.forEach((r2) => {
          if (used.has(r2.id)) return;
          console.log(`\nPairing PGY-2: ${r2.name}`);
          const highPGY = findUnusedHighPGYFor2(used);
          if (highPGY) {
            console.log(`  -> Paired with ${highPGY.name} (PGY-${highPGY.year})`);
            saturdayPairs.push([r2, highPGY]);
            sundayPairs.push([r2, highPGY]);
            used.add(r2.id);
            used.add(highPGY.id);
          } else {
            // PGY-2 can be alone
            console.log(`  -> No higher-level resident available, working alone`);
            saturdayPairs.push([r2]);
            sundayPairs.push([r2]);
            used.add(r2.id);
          }
        });

        // Add remaining residents (should only be PGY-3, 4, 5 or unpaired PGY-2)
        // Never pair PGY-1 with anyone here - they should have been processed above
        const remaining = shuffled.filter(r => !used.has(r.id));
        console.log('\nRemaining unpaired residents:', remaining.map(r => `${r.name} (PGY-${r.year})`));
        
        // Safety check: if any PGY-1s are still unpaired, they must work alone
        const unpairedPGY1 = remaining.filter(r => r.year === 1);
        if (unpairedPGY1.length > 0) {
          console.log('WARNING: Unpaired PGY-1s found:', unpairedPGY1.map(r => r.name));
        }
        unpairedPGY1.forEach((r1) => {
          console.log(`  -> Adding unpaired PGY-1 alone: ${r1.name}`);
          saturdayPairs.push([r1]);
          sundayPairs.push([r1]);
          used.add(r1.id);
        });
        
        // Now process remaining residents (PGY-2, 3, 4, 5) - pair them randomly if possible
        const remainingAfterPGY1 = remaining.filter(r => !used.has(r.id));
        console.log('Remaining after PGY-1 safety check:', remainingAfterPGY1.map(r => `${r.name} (PGY-${r.year})`));
        // Shuffle remaining for random pairing using seeded random
        const shuffledRemaining = [...remainingAfterPGY1].sort(() => seededRandom() - 0.5);
        console.log('Shuffled remaining:', shuffledRemaining.map(r => `${r.name} (PGY-${r.year})`));
        shuffledRemaining.forEach((r, idx) => {
          if (idx < shuffledRemaining.length - 1 && idx % 2 === 0) {
            // Pair with next resident if available
            console.log(`  -> Pairing ${r.name} (PGY-${r.year}) with ${shuffledRemaining[idx + 1].name} (PGY-${shuffledRemaining[idx + 1].year})`);
            saturdayPairs.push([r, shuffledRemaining[idx + 1]]);
            sundayPairs.push([r, shuffledRemaining[idx + 1]]);
            used.add(r.id);
            used.add(shuffledRemaining[idx + 1].id);
          } else if (!used.has(r.id)) {
            // Alone
            console.log(`  -> Adding alone: ${r.name} (PGY-${r.year})`);
            saturdayPairs.push([r]);
            sundayPairs.push([r]);
            used.add(r.id);
          }
        });

        console.log('\n🟢 Final Saturday Pairs:');
        saturdayPairs.forEach((pair, idx) => {
          console.log(`  Pair ${idx + 1}:`, pair.map(r => `${r.name} (PGY-${r.year})`).join(' + '));
        });
        console.log('🟢 === pairResidentsForWeekend END ===\n');
        
        return { saturdayPairs, sundayPairs };
      };

      // Helper function to split residents into two groups with PGY-aware pairing
      const splitResidentsWithPGYPairing = (residents) => {
        const count = residents.length;
        const pgy1 = residents.filter(r => r.year === 1);
        const pgy2 = residents.filter(r => r.year === 2);
        const pgy3 = residents.filter(r => r.year === 3);
        const pgy4 = residents.filter(r => r.year === 4);
        const pgy5 = residents.filter(r => r.year === 5);
        
        // Helper to find unused high-level resident for PGY-1 (prefer 4/5, then 3, then 2 as last resort)
        const findUnusedHighPGY = (used, prefer4or5 = true) => {
          if (prefer4or5) {
            const available4 = pgy4.filter(r => !used.has(r.id));
            if (available4.length > 0) return available4[0];
            const available5 = pgy5.filter(r => !used.has(r.id));
            if (available5.length > 0) return available5[0];
          }
          const available3 = pgy3.filter(r => !used.has(r.id));
          if (available3.length > 0) return available3[0];
          // Last resort: PGY-2 (though we prefer to avoid this)
          const available2 = pgy2.filter(r => !used.has(r.id));
          if (available2.length > 0) return available2[0];
          return null;
        };
        
        // Helper to find unused PGY-4 or 5 for PGY-2 pairing (prefer 4/5, then 3)
        const findUnusedHighPGYFor2 = (used) => {
          const available4 = pgy4.filter(r => !used.has(r.id));
          if (available4.length > 0) return available4[0];
          const available5 = pgy5.filter(r => !used.has(r.id));
          if (available5.length > 0) return available5[0];
          const available3 = pgy3.filter(r => !used.has(r.id));
          if (available3.length > 0) return available3[0];
          return null;
        };

        if (count === 2) {
          // Even split: 1 and 1
          return [residents.slice(0, 1), residents.slice(1)];
        } else if (count === 3) {
          // 1 vs 2 pairing
          // Try to pair PGY-1 with higher level (prefer 4 or 5, then 3)
          if (pgy1.length > 0) {
            const pgy1Res = pgy1[0];
            const used = new Set([pgy1Res.id]);
            const highPGY = findUnusedHighPGY(used);
            if (highPGY) {
              const remaining = residents.filter(r => r.id !== pgy1Res.id && r.id !== highPGY.id)[0];
              return [[pgy1Res, highPGY], [remaining]];
            }
          }
          // Fallback: random split
          return [residents.slice(0, 2), residents.slice(2)];
        } else if (count === 4) {
          // Even split: 2 and 2
          const group1 = [];
          const group2 = [];
          const used = new Set();
          const targetSize = 2;

          // Track which higher-level residents are paired with which lower-level residents
          // This helps alternate pairings when there are multiple lower levels and fewer higher levels
          const higherLevelPairings = new Map(); // higherLevelId -> array of paired lower level ids

          // First, pair PGY-1 with higher levels, distributing them across groups
          // If multiple PGY-1s and only one higher level, split them - one gets paired, others go to other group
          pgy1.forEach((r1, idx) => {
            const highPGY = findUnusedHighPGY(used, true);
            if (highPGY) {
              // Track this pairing
              if (!higherLevelPairings.has(highPGY.id)) {
                higherLevelPairings.set(highPGY.id, []);
              }
              higherLevelPairings.get(highPGY.id).push(r1.id);

              // If this is the first PGY-1 pairing with this higher-level resident, pair them
              // If there are multiple PGY-1s and only one higher level, alternate which group gets the pairing
              const existingPairings = higherLevelPairings.get(highPGY.id);
              const isFirstPairing = existingPairings.length === 1;
              
              if (isFirstPairing) {
                // First PGY-1 gets paired with higher level - put in group1
                if (group1.length < targetSize) {
                  group1.push(r1, highPGY);
                } else {
                  group2.push(r1, highPGY);
                }
                used.add(r1.id);
                used.add(highPGY.id);
              } else {
                // Multiple PGY-1s and only one higher level - this PGY-1 goes to the other group without pairing
                // Alternate which group gets unpaired PGY-1s
                const alternateGroup = idx % 2 === 0 ? group2 : group1;
                const otherGroup = idx % 2 === 0 ? group1 : group2;
                
                if (alternateGroup.length < targetSize) {
                  alternateGroup.push(r1);
                } else {
                  otherGroup.push(r1);
                }
                used.add(r1.id);
              }
            } else {
              // Try to find any resident from levels 2-5
              const anyHigher = residents.find(r => r.year >= 2 && r.year <= 5 && !used.has(r.id));
              if (anyHigher) {
                // Track pairing
                if (!higherLevelPairings.has(anyHigher.id)) {
                  higherLevelPairings.set(anyHigher.id, []);
                }
                higherLevelPairings.get(anyHigher.id).push(r1.id);

                // Alternate groups
                const alternateGroup = idx % 2 === 0 ? group1 : group2;
                const otherGroup = idx % 2 === 0 ? group2 : group1;
                
                if (alternateGroup.length < targetSize) {
                  alternateGroup.push(r1, anyHigher);
                } else {
                  otherGroup.push(r1, anyHigher);
                }
                used.add(r1.id);
                used.add(anyHigher.id);
              } else {
                // No one available - alternate groups
                const alternateGroup = idx % 2 === 0 ? group1 : group2;
                const otherGroup = idx % 2 === 0 ? group2 : group1;
                
                if (alternateGroup.length < targetSize) {
                  alternateGroup.push(r1);
                  used.add(r1.id);
                } else {
                  otherGroup.push(r1);
                  used.add(r1.id);
                }
              }
            }
          });

          // Pair PGY-2 with higher levels (prefer 4/5, then 3)
          // If there are higher levels (4 or 5), pair them with PGY-2
          pgy2.forEach((r2, idx) => {
            if (used.has(r2.id)) return;
            const highPGY = findUnusedHighPGYFor2(used);
            if (highPGY) {
              // Track pairing
              if (!higherLevelPairings.has(highPGY.id)) {
                higherLevelPairings.set(highPGY.id, []);
              }
              higherLevelPairings.get(highPGY.id).push(r2.id);

              // Alternate between groups to distribute PGY-2s
              const alternateGroup = idx % 2 === 0 ? group1 : group2;
              const otherGroup = idx % 2 === 0 ? group2 : group1;
              
              if (alternateGroup.length < targetSize) {
                alternateGroup.push(r2, highPGY);
              } else {
                otherGroup.push(r2, highPGY);
              }
              used.add(r2.id);
              used.add(highPGY.id);
            } else {
              // PGY-2 can be alone, but still distribute across groups
              const alternateGroup = idx % 2 === 0 ? group1 : group2;
              const otherGroup = idx % 2 === 0 ? group2 : group1;
              
              if (alternateGroup.length < targetSize) {
                alternateGroup.push(r2);
                used.add(r2.id);
              } else {
                otherGroup.push(r2);
                used.add(r2.id);
              }
            }
          });

          // Add remaining residents to balance
          residents.forEach((r) => {
            if (!used.has(r.id)) {
              if (group1.length < targetSize) group1.push(r);
              else group2.push(r);
            }
          });

          return [group1, group2];
        } else if (count === 5) {
          // 2 vs 3 pairing
          const group1 = []; // Target: 2
          const group2 = []; // Target: 3
          const used = new Set();
          const higherLevelPairings = new Map();

          // Pair PGY-1 with higher levels, distributing them across groups
          // If multiple PGY-1s and only one higher level, split them - one gets paired, others go to other group
          pgy1.forEach((r1, idx) => {
            const highPGY = findUnusedHighPGY(used, true);
            if (highPGY) {
              // Track pairing
              if (!higherLevelPairings.has(highPGY.id)) {
                higherLevelPairings.set(highPGY.id, []);
              }
              higherLevelPairings.get(highPGY.id).push(r1.id);

              // If this is the first PGY-1 pairing with this higher-level resident, pair them
              const existingPairings = higherLevelPairings.get(highPGY.id);
              const isFirstPairing = existingPairings.length === 1;
              
              if (isFirstPairing) {
                // First PGY-1 gets paired with higher level
                if (group1.length < 2) {
                  group1.push(r1, highPGY);
                } else {
                  group2.push(r1, highPGY);
                }
                used.add(r1.id);
                used.add(highPGY.id);
              } else {
                // Multiple PGY-1s and only one higher level - this PGY-1 goes to the other group without pairing
                const alternateGroup = idx % 2 === 0 ? group2 : group1;
                const otherGroup = idx % 2 === 0 ? group1 : group2;
                
                if (alternateGroup.length < (alternateGroup === group1 ? 2 : 3)) {
                  alternateGroup.push(r1);
                } else {
                  otherGroup.push(r1);
                }
                used.add(r1.id);
              }
            } else {
              // Try to find any resident from levels 2-5
              const anyHigher = residents.find(r => r.year >= 2 && r.year <= 5 && !used.has(r.id));
              if (anyHigher) {
                // Track pairing
                if (!higherLevelPairings.has(anyHigher.id)) {
                  higherLevelPairings.set(anyHigher.id, []);
                }
                higherLevelPairings.get(anyHigher.id).push(r1.id);

                // Alternate groups for multiple PGY-1s
                const alternateGroup = idx % 2 === 0 ? group1 : group2;
                const otherGroup = idx % 2 === 0 ? group2 : group1;
                
                if (alternateGroup.length < (alternateGroup === group1 ? 2 : 3)) {
                  alternateGroup.push(r1, anyHigher);
                } else {
                  otherGroup.push(r1, anyHigher);
                }
                used.add(r1.id);
                used.add(anyHigher.id);
              } else {
                // No one available - alternate groups
                const alternateGroup = idx % 2 === 0 ? group1 : group2;
                const otherGroup = idx % 2 === 0 ? group2 : group1;
                
                if (alternateGroup.length < (alternateGroup === group1 ? 2 : 3)) {
                  alternateGroup.push(r1);
                  used.add(r1.id);
                } else {
                  otherGroup.push(r1);
                  used.add(r1.id);
                }
              }
            }
          });

          // Pair remaining PGY-2 with higher levels (prefer 4/5, then 3)
          pgy2.forEach((r2, idx) => {
            if (used.has(r2.id)) return;
            const highPGY = findUnusedHighPGYFor2(used);
            if (highPGY) {
              // Track pairing
              if (!higherLevelPairings.has(highPGY.id)) {
                higherLevelPairings.set(highPGY.id, []);
              }
              higherLevelPairings.get(highPGY.id).push(r2.id);

              // Alternate groups to distribute PGY-2s
              const alternateGroup = idx % 2 === 0 ? group1 : group2;
              const otherGroup = idx % 2 === 0 ? group2 : group1;
              
              if (alternateGroup.length < (alternateGroup === group1 ? 2 : 3)) {
                alternateGroup.push(r2, highPGY);
              } else {
                otherGroup.push(r2, highPGY);
              }
              used.add(r2.id);
              used.add(highPGY.id);
            } else {
              // PGY-2 can be alone, but distribute across groups
              const alternateGroup = idx % 2 === 0 ? group1 : group2;
              const otherGroup = idx % 2 === 0 ? group2 : group1;
              
              if (alternateGroup.length < (alternateGroup === group1 ? 2 : 3)) {
                alternateGroup.push(r2);
                used.add(r2.id);
              } else {
                otherGroup.push(r2);
                used.add(r2.id);
              }
            }
          });

          // Add remaining residents to balance (group1 should be 2, group2 should be 3)
          residents.forEach((r) => {
            if (!used.has(r.id)) {
              if (group1.length < 2) group1.push(r);
              else group2.push(r);
            }
          });

          return [group1, group2];
        } else if (count === 6) {
          // Even split: 3 and 3
          const group1 = [];
          const group2 = [];
          const used = new Set();
          const targetSize = 3;
          const higherLevelPairings = new Map();

          // Pair PGY-1 with higher levels, distributing them across groups
          // If multiple PGY-1s and only one higher level, split them - one gets paired, others go to other group
          pgy1.forEach((r1, idx) => {
            const highPGY = findUnusedHighPGY(used, true);
            if (highPGY) {
              // Track pairing
              if (!higherLevelPairings.has(highPGY.id)) {
                higherLevelPairings.set(highPGY.id, []);
              }
              higherLevelPairings.get(highPGY.id).push(r1.id);

              // If this is the first PGY-1 pairing with this higher-level resident, pair them
              const existingPairings = higherLevelPairings.get(highPGY.id);
              const isFirstPairing = existingPairings.length === 1;
              
              if (isFirstPairing) {
                // First PGY-1 gets paired with higher level
                if (group1.length < targetSize) {
                  group1.push(r1, highPGY);
                } else {
                  group2.push(r1, highPGY);
                }
                used.add(r1.id);
                used.add(highPGY.id);
              } else {
                // Multiple PGY-1s and only one higher level - this PGY-1 goes to the other group without pairing
                const alternateGroup = idx % 2 === 0 ? group2 : group1;
                const otherGroup = idx % 2 === 0 ? group1 : group2;
                
                if (alternateGroup.length < targetSize) {
                  alternateGroup.push(r1);
                } else {
                  otherGroup.push(r1);
                }
                used.add(r1.id);
              }
            } else {
              // Try to find any resident from levels 2-5
              const anyHigher = residents.find(r => r.year >= 2 && r.year <= 5 && !used.has(r.id));
              if (anyHigher) {
                // Track pairing
                if (!higherLevelPairings.has(anyHigher.id)) {
                  higherLevelPairings.set(anyHigher.id, []);
                }
                higherLevelPairings.get(anyHigher.id).push(r1.id);

                // Alternate groups for multiple PGY-1s
                const alternateGroup = idx % 2 === 0 ? group1 : group2;
                const otherGroup = idx % 2 === 0 ? group2 : group1;
                
                if (alternateGroup.length < targetSize) {
                  alternateGroup.push(r1, anyHigher);
                } else {
                  otherGroup.push(r1, anyHigher);
                }
                used.add(r1.id);
                used.add(anyHigher.id);
              } else {
                // No one available - alternate groups
                const alternateGroup = idx % 2 === 0 ? group1 : group2;
                const otherGroup = idx % 2 === 0 ? group2 : group1;
                
                if (alternateGroup.length < targetSize) {
                  alternateGroup.push(r1);
                  used.add(r1.id);
                } else {
                  otherGroup.push(r1);
                  used.add(r1.id);
                }
              }
            }
          });

          // Pair PGY-2 with higher levels (prefer 4/5, then 3)
          pgy2.forEach((r2, idx) => {
            if (used.has(r2.id)) return;
            const highPGY = findUnusedHighPGYFor2(used);
            if (highPGY) {
              // Track pairing
              if (!higherLevelPairings.has(highPGY.id)) {
                higherLevelPairings.set(highPGY.id, []);
              }
              higherLevelPairings.get(highPGY.id).push(r2.id);

              // Alternate groups to distribute PGY-2s
              const alternateGroup = idx % 2 === 0 ? group1 : group2;
              const otherGroup = idx % 2 === 0 ? group2 : group1;
              
              if (alternateGroup.length < targetSize) {
                alternateGroup.push(r2, highPGY);
              } else {
                otherGroup.push(r2, highPGY);
              }
              used.add(r2.id);
              used.add(highPGY.id);
            } else {
              // PGY-2 can be alone, but distribute across groups
              const alternateGroup = idx % 2 === 0 ? group1 : group2;
              const otherGroup = idx % 2 === 0 ? group2 : group1;
              
              if (alternateGroup.length < targetSize) {
                alternateGroup.push(r2);
                used.add(r2.id);
              } else {
                otherGroup.push(r2);
                used.add(r2.id);
              }
            }
          });

          // Add remaining residents
          residents.forEach((r) => {
            if (!used.has(r.id)) {
              if (group1.length < targetSize) group1.push(r);
              else group2.push(r);
            }
          });

          return [group1, group2];
        } else {
          // For other counts, simple split
          const halfCount = Math.ceil(count / 2);
          return [residents.slice(0, halfCount), residents.slice(halfCount)];
        }
      };

      // Process each rotation and month
      Object.keys(base).forEach((rotation) => {
        // Initialize tracking for this rotation if needed
        if (!threeWeekendWorkers[rotation]) {
          threeWeekendWorkers[rotation] = {};
        }
        
        months.forEach((month) => {
          const weekends = getWeekendsForMonth(month);
          const numWeekends = weekends.length;
          
          // Get all residents assigned to this rotation for this month
          const residentsForMonth = [];
          Object.values(rotationSchedules).forEach((scheduleData) => {
            Object.entries(scheduleData.schedule || {}).forEach(([residentId, residentData]) => {
              if (!residentData.rotations) return;
              const isAssigned = (scheduleData.scheduleType === 'weekly' || scheduleData.scheduleType === 'blocks')
                ? Object.keys(residentData.rotations).some(weekKey => {
                    const weekStart = new Date(weekKey + 'T00:00:00');
                    const monthKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}`;
                    return monthKey === month && residentData.rotations[weekKey] === rotation;
                  })
                : residentData.rotations[month] === rotation;
              
              if (isAssigned) {
                // Check if excluded
                const isExcluded = excluded.has(rotation);
                if (!isExcluded) {
                  residentsForMonth.push({
                    id: residentId,
                    name: residentData.name,
                    year: parseInt(residentData.year)
                  });
                }
              }
            });
          });

          // Add off-service residents for this rotation and month
          offServiceResidents.forEach(osr => {
            const normalizedRotation = (rotation || '').trim();
            let shouldInclude = false;
            let rotationDateRange = null; // Store the date range for this rotation

            // Handle block schedules
            if (osr.scheduleType === 'blocks') {
              const osrBlockIndex = osr.blockIndex ? parseInt(osr.blockIndex, 10) : null;
              const osrMajorRotation = (osr.majorRotation || '').trim();
              const osrMinorRotation = (osr.minorRotation || '').trim();

              // Check if rotation matches major or minor rotation
              const isMajorRotation = osrMajorRotation && osrMajorRotation.toLowerCase() === normalizedRotation.toLowerCase();
              const isMinorRotation = osrMinorRotation && osrMinorRotation.toLowerCase() === normalizedRotation.toLowerCase();
              const rotationMatches = isMajorRotation || isMinorRotation;

              if (rotationMatches && osrBlockIndex) {
                // Find which schedule this off-service resident belongs to (by PGY level)
                Object.entries(rotationSchedules).forEach(([pgyLevel, scheduleData]) => {
                  // Match by PGY level (pgyLevel key should match osr.pgyLevel)
                  if (scheduleData?.scheduleType === 'blocks' && 
                      pgyLevel?.toString() === osr.pgyLevel?.toString()) {
                    // Get the block definition to determine rotation date range
                    const blocks = blockDefinitionsBySchedule[pgyLevel];
                    if (blocks && blocks.length >= osrBlockIndex) {
                      const block = blocks[osrBlockIndex - 1]; // blockIndex is 1-based
                      if (block) {
                        // Determine date range based on whether it's major or minor rotation
                        if (isMajorRotation && block.majorWeeks && block.majorWeeks.length > 0) {
                          const firstWeek = block.majorWeeks[0];
                          const lastWeek = block.majorWeeks[block.majorWeeks.length - 1];
                          rotationDateRange = {
                            start: new Date(firstWeek.start),
                            end: new Date(lastWeek.end)
                          };
                        } else if (isMinorRotation && block.minorWeeks && block.minorWeeks.length > 0) {
                          const firstWeek = block.minorWeeks[0];
                          const lastWeek = block.minorWeeks[block.minorWeeks.length - 1];
                          rotationDateRange = {
                            start: new Date(firstWeek.start),
                            end: new Date(lastWeek.end)
                          };
                        }
                        
                        // Check if any weekend in this month falls within the rotation date range
                        const weekendInRange = weekends.some(weekend => {
                          const weekendDate = parseDate(weekend.saturday);
                          if (weekendDate && rotationDateRange) {
                            return weekendDate >= rotationDateRange.start && weekendDate <= rotationDateRange.end;
                          }
                          // Fallback: check if weekend falls in block
                          const blockIndexForWeekend = getBlockIndexForWeekend(weekend, scheduleData, pgyLevel);
                          return blockIndexForWeekend === osrBlockIndex;
                        });

                        if (weekendInRange) {
                          shouldInclude = true;
                        }
                      }
                    }
                  }
                });
              }
            } else {
              // Handle monthly and weekly schedules
            const osrMonth = (osr.month || '').trim();
            const osrRotation = (osr.rotationName || '').trim();
            
            // Check if this off-service resident matches the rotation and month
            if (osrRotation.toLowerCase() === normalizedRotation.toLowerCase() && 
                osrMonth === month) {
                shouldInclude = true;
              }
            }

            if (shouldInclude) {
              // Check if excluded
              const isExcluded = excluded.has(rotation);
              if (!isExcluded) {
                residentsForMonth.push({
                  id: `off-service-${osr.id}`,
                  name: osr.residentName,
                  year: osr.pgyLevel,
                  isOffService: true,
                  rotationDateRange: rotationDateRange // Store date range for filtering weekends
                });
              }
            }
          });

          // Remove duplicates
          const uniqueResidents = Array.from(new Map(residentsForMonth.map(r => [r.id, r])).values());
          
          if (uniqueResidents.length === 0) return;

          console.log(`\n\n🔵 === GENERATING WEEKEND CALLS ===`);
          console.log(`Rotation: ${rotation}`);
          console.log(`Month: ${month}`);
          console.log(`Number of weekends: ${numWeekends}`);
          console.log(`Residents: ${uniqueResidents.map(r => `${r.name} (PGY-${r.year})`).join(', ')}`);
          console.log(`Weekend dates: ${weekends.map(w => w.saturday).join(', ')}`);

          // Helper function to filter weekends for off-service residents based on rotation date range
          const getFilteredWeekendsForResident = (resident) => {
            if (resident.isOffService && resident.rotationDateRange) {
              return weekends.filter(weekend => {
                const weekendDate = parseDate(weekend.saturday);
                if (!weekendDate) return false;
                return weekendDate >= resident.rotationDateRange.start && weekendDate <= resident.rotationDateRange.end;
              });
            }
            return weekends; // Regular residents get all weekends
          };

          // Special case: If there's only one resident for this rotation
          if (uniqueResidents.length === 1) {
            const singleResident = uniqueResidents[0];
            console.log(`\n⚠️ Single resident case: ${singleResident.name} (PGY-${singleResident.year})`);
            
            // Get filtered weekends for this resident (if off-service with date range)
            const residentWeekends = getFilteredWeekendsForResident(singleResident);
            const residentNumWeekends = residentWeekends.length;
            
            console.log(`Resident weekends (filtered): ${residentNumWeekends}`, residentWeekends.map(w => w.saturday));
            
            // Track which weekends they work
            const weekendsPerResident = {};
            weekendsPerResident[singleResident.id] = [];
            
            const weekendAssignments = {};
            weekends.forEach(w => {
              weekendAssignments[w.saturday] = [];
            });
            
            // Create seed for consistent randomness
            const rotationMonthSeed = `${rotation}-${month}`;
            let seed = rotationMonthSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
            const getRandom = () => {
              seed = (seed * 9301 + 49297) % 233280;
              return seed / 233280;
            };
            
            // Determine work count based on number of weekends in rotation date range
            let workCount;
            if (residentNumWeekends === 3) {
              // 3 weekends: work 1-2 weekends
              workCount = getRandom() < 0.5 ? 1 : 2;
            } else if (residentNumWeekends === 4) {
              // 4 weekends: work exactly 2 weekends
              workCount = 2;
            } else if (residentNumWeekends === 5) {
              // 5 weekends: work 2-3 weekends
              workCount = getRandom() < 0.5 ? 2 : 3;
            } else if (residentNumWeekends >= 6) {
              // 6+ weekends: work 2-3 weekends (similar to 5)
              workCount = getRandom() < 0.5 ? 2 : 3;
            } else {
              // For 1-2 weekends: work as many as possible (up to 2)
              workCount = Math.min(2, residentNumWeekends);
            }
            
            const shuffled = [...residentWeekends].sort(() => getRandom() - 0.5);
              const selectedWeekends = shuffled.slice(0, workCount);
              selectedWeekends.forEach(w => {
                weekendsPerResident[singleResident.id].push(w.saturday);
                weekendAssignments[w.saturday].push(singleResident);
              });
              console.log(`✅ Single resident assigned to ${workCount} weekends:`, selectedWeekends.map(w => w.saturday));
              
            // Track for balancing in future months (only for 5 weekends case)
            if (residentNumWeekends === 5) {
              threeWeekendWorkers[rotation][singleResident.id] = workCount === 3;
            }
            
            // Process each weekend and assign the resident
            weekends.forEach((weekend) => {
              const workingResidents = weekendAssignments[weekend.saturday] || [];
              
              if (workingResidents.length > 0) {
                if (!base[rotation][weekend.saturday]) {
                  base[rotation][weekend.saturday] = { date: weekend.saturday, residents: [], type: "Saturday" };
                }
                if (!base[rotation][weekend.sunday]) {
                  base[rotation][weekend.sunday] = { date: weekend.sunday, residents: [], type: "Sunday" };
                }
                
                // Single resident works both days
                base[rotation][weekend.saturday].residents.push(singleResident);
                base[rotation][weekend.sunday].residents.push(singleResident);
              }
            });
            
            // Skip the rest of the logic for single resident case
            return;
          }

          // Shuffle residents and weekends for random assignment
          const shuffledResidents = [...uniqueResidents].sort(() => Math.random() - 0.5);
          const shuffledWeekends = [...weekends].sort(() => Math.random() - 0.5);

          if (numWeekends === 4) {
            // 4 weekends: Assign residents individually per weekend (not as fixed groups)
            // Each resident works 2 weekends, but different residents work different weekends
            // Reset tracking for 5-weekend balancing since this is a 4-weekend month
            uniqueResidents.forEach(r => {
              threeWeekendWorkers[rotation][r.id] = false;
            });
            
            // Create a schedule: assign residents individually per weekend
            // Each resident works 2 weekends, ensuring full coverage and alternating groups
            // Use seeded random based on rotation and month for consistent assignment
            const rotationMonthSeed = `${rotation}-${month}`;
            let assignmentSeed = rotationMonthSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
            const getAssignmentRandom = () => {
              assignmentSeed = (assignmentSeed * 9301 + 49297) % 233280;
              return assignmentSeed / 233280;
            };
            
            // Track which residents work which weekends
            const weekendsPerResident = {};
            uniqueResidents.forEach(r => {
              weekendsPerResident[r.id] = [];
            });
            
            // Assign residents individually per weekend
            // Strategy: Each resident works 2 weekends, but we vary the combinations per weekend
            // Use weekend-specific seeds to ensure different resident selections each weekend
            
            // First, assign each resident to 2 weekends using a balanced algorithm
            // We'll use a round-robin approach with variation per weekend
            
            // Calculate target: each resident works 2 weekends, so total assignments = N * 2
            // For 4 weekends, average per weekend = (N * 2) / 4 = N / 2
            const targetPerWeekend = Math.ceil(uniqueResidents.length / 2);
            
            // Create weekend-specific assignment lists
            const weekendAssignments = {};
            shuffledWeekends.forEach(w => {
              weekendAssignments[w.saturday] = [];
            });
            
            // Use a balanced assignment algorithm
            // Strategy: Assign residents in a way that ensures each weekend gets ~half the residents
            // but allows different combinations per weekend
            
            // First pass: assign each resident to 2 weekends using weekend-specific shuffling
            shuffledResidents.forEach((resident) => {
              // Use resident-specific seed for consistent assignment
              const residentSeed = `${rotationMonthSeed}-${resident.id}`;
              let residentAssignmentSeed = residentSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
              const getResidentRandom = () => {
                residentAssignmentSeed = (residentAssignmentSeed * 9301 + 49297) % 233280;
                return residentAssignmentSeed / 233280;
              };
              
              // For each weekend, calculate a "preference score" based on weekend-specific seed
              // This ensures different residents prefer different weekends
              const weekendScores = shuffledWeekends.map(w => {
                const weekendSeed = `${rotationMonthSeed}-${w.saturday}-${resident.id}`;
                let scoreSeed = weekendSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
                const getScoreRandom = () => {
                  scoreSeed = (scoreSeed * 9301 + 49297) % 233280;
                  return scoreSeed / 233280;
                };
                return { weekend: w, score: getScoreRandom() };
              });
              
              // Sort by score and select top 2
              weekendScores.sort((a, b) => a.score - b.score);
              const selectedWeekends = weekendScores.slice(0, 2).map(ws => ws.weekend);
              
              selectedWeekends.forEach(w => {
                weekendsPerResident[resident.id].push(w.saturday);
                weekendAssignments[w.saturday].push(resident);
              });
            });
            
            // Balance: ensure each weekend has approximately half the residents
            shuffledWeekends.forEach((weekend, weekendIdx) => {
              const currentCount = weekendAssignments[weekend.saturday].length;
              const needsMore = targetPerWeekend - currentCount;
              
              if (needsMore > 0) {
                const available = shuffledResidents.filter(r => 
                  weekendsPerResident[r.id].length < 2 && 
                  !weekendsPerResident[r.id].includes(weekend.saturday)
                );
                
                if (available.length > 0) {
                  // Use weekend-specific seed for selection to vary which residents fill gaps
                  const weekendSeed = `${rotationMonthSeed}-${weekend.saturday}`;
                  let weekendAssignmentSeed = weekendSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
                  const getWeekendRandom = () => {
                    weekendAssignmentSeed = (weekendAssignmentSeed * 9301 + 49297) % 233280;
                    return weekendAssignmentSeed / 233280;
                  };
                  
                  const shuffled = [...available].sort(() => getWeekendRandom() - 0.5);
                  const selected = shuffled.slice(0, Math.min(needsMore, available.length));
                  selected.forEach(r => {
                    if (!weekendsPerResident[r.id].includes(weekend.saturday)) {
                      weekendsPerResident[r.id].push(weekend.saturday);
                      weekendAssignments[weekend.saturday].push(r);
                    }
                  });
                }
              }
              
              console.log(`\nWeekend ${weekendIdx + 1} (${weekend.saturday}) - Assigned:`, weekendAssignments[weekend.saturday].map(r => `${r.name} (PGY-${r.year})`));
            });
            
            // Final verification: Ensure every weekend has at least one resident (guarantee coverage)
            // For 4-weekend months: Each resident must work exactly 2 weekends
            const MAX_WEEKENDS_PER_RESIDENT = 2; // Strict limit for 4-weekend months
            
            shuffledWeekends.forEach((weekend, weekendIdx) => {
              const workingResidents = weekendAssignments[weekend.saturday] || [];
              
              if (workingResidents.length === 0) {
                console.log(`⚠️ Weekend ${weekendIdx + 1} (${weekend.saturday}) has NO coverage - assigning residents`);
                
                // Find residents with fewer than 2 weekends (they can still be assigned)
                const availableResidents = shuffledResidents
                  .filter(r => weekendsPerResident[r.id].length < MAX_WEEKENDS_PER_RESIDENT)
                  .sort((a, b) => weekendsPerResident[a.id].length - weekendsPerResident[b.id].length);
                
                if (availableResidents.length > 0) {
                  // Assign from available residents
                  const weekendSeed = `${rotationMonthSeed}-${weekend.saturday}`;
                  let weekendAssignmentSeed = weekendSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
                  const getWeekendRandom = () => {
                    weekendAssignmentSeed = (weekendAssignmentSeed * 9301 + 49297) % 233280;
                    return weekendAssignmentSeed / 233280;
                  };
                  
                  const shuffled = [...availableResidents].sort(() => getWeekendRandom() - 0.5);
                  const needed = Math.max(1, Math.ceil(uniqueResidents.length / 2));
                  const selected = shuffled.slice(0, Math.min(needed, availableResidents.length));
                  selected.forEach(r => {
                    if (!weekendsPerResident[r.id].includes(weekend.saturday) && weekendsPerResident[r.id].length < MAX_WEEKENDS_PER_RESIDENT) {
                      weekendsPerResident[r.id].push(weekend.saturday);
                      weekendAssignments[weekend.saturday].push(r);
                    }
                  });
                  console.log(`✅ Assigned ${selected.length} available residents:`, selected.map(r => `${r.name} (PGY-${r.year})`));
                } else {
                  // All residents already have 2 weekends - need to reassign
                  // Find residents who have this weekend assigned to another weekend, and swap
                  // Or find residents with only 1 weekend and add this weekend
                  const residentsWithOneWeekend = shuffledResidents.filter(r => weekendsPerResident[r.id].length === 1);
                  if (residentsWithOneWeekend.length > 0) {
                    // Add this weekend to residents who only have 1 weekend
                    const weekendSeed = `${rotationMonthSeed}-${weekend.saturday}`;
                    let weekendAssignmentSeed = weekendSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
                    const getWeekendRandom = () => {
                      weekendAssignmentSeed = (weekendAssignmentSeed * 9301 + 49297) % 233280;
                      return weekendAssignmentSeed / 233280;
                    };
                    
                    const shuffled = [...residentsWithOneWeekend].sort(() => getWeekendRandom() - 0.5);
                    const selected = shuffled.slice(0, Math.max(1, Math.ceil(uniqueResidents.length / 2)));
                    selected.forEach(r => {
                      if (!weekendsPerResident[r.id].includes(weekend.saturday)) {
                        weekendsPerResident[r.id].push(weekend.saturday);
                        weekendAssignments[weekend.saturday].push(r);
                      }
                    });
                    console.log(`✅ Assigned ${selected.length} residents (had 1 weekend, now have 2):`, selected.map(r => `${r.name} (PGY-${r.year})`));
                  } else {
                    // All residents have exactly 2 weekends - reassign one weekend from a resident to this weekend
                    // Pick a resident who has 2 weekends assigned, remove one, and assign this weekend
                    const weekendSeed = `${rotationMonthSeed}-${weekend.saturday}`;
                    let weekendAssignmentSeed = weekendSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
                    const getWeekendRandom = () => {
                      weekendAssignmentSeed = (weekendAssignmentSeed * 9301 + 49297) % 233280;
                      return weekendAssignmentSeed / 233280;
                    };
                    
                    // Find a resident who has this weekend assigned to another weekend, and swap
                    // Or find a resident with 2 weekends and replace one
                    const shuffled = [...shuffledResidents].sort(() => getWeekendRandom() - 0.5);
                    for (const r of shuffled) {
                      if (weekendsPerResident[r.id].length === MAX_WEEKENDS_PER_RESIDENT && !weekendsPerResident[r.id].includes(weekend.saturday)) {
                        // Remove one of their weekends and assign this one
                        const oldWeekend = weekendsPerResident[r.id][0];
                        const oldIndex = weekendAssignments[oldWeekend]?.findIndex(res => res.id === r.id);
                        if (oldIndex !== undefined && oldIndex >= 0) {
                          weekendAssignments[oldWeekend].splice(oldIndex, 1);
                          weekendsPerResident[r.id] = weekendsPerResident[r.id].filter(w => w !== oldWeekend);
                        }
                        weekendsPerResident[r.id].push(weekend.saturday);
                        weekendAssignments[weekend.saturday].push(r);
                        console.log(`✅ Reassigned ${r.name} from ${oldWeekend} to ${weekend.saturday}`);
                        break;
                      }
                    }
                  }
                }
              }
            });
            
            // Now process each weekend independently with the assigned residents
            // Anti-stacking: balance resident counts across weekends (aim for ~targetPerWeekend)
            {
              const weekendKeys = shuffledWeekends.map(w => w.saturday);
              let safety = 0;
              const maxPerResident = 2;
              const pickMovable = (fromKey, toKey) => {
                // Prefer residents who have 1 assignment so far, then those with 2 (swap)
                const candidates = [...weekendAssignments[fromKey]].sort((a, b) =>
                  weekendsPerResident[a.id].length - weekendsPerResident[b.id].length
                );
                for (const r of candidates) {
                  if (!weekendsPerResident[r.id].includes(toKey) && weekendsPerResident[r.id].length < maxPerResident) {
                    return { resident: r, swapOldKey: null };
                  }
                }
                // Try swap from someone at cap 2: move by replacing one of their other weekends
                for (const r of weekendAssignments[fromKey]) {
                  if (!weekendsPerResident[r.id].includes(toKey) && weekendsPerResident[r.id].length === maxPerResident) {
                    const oldKey = weekendsPerResident[r.id].find(k => k !== fromKey);
                    if (oldKey && oldKey !== toKey) {
                      return { resident: r, swapOldKey: oldKey };
                    }
                  }
                }
                return null;
              };
              while (safety++ < 200) {
                const counts = weekendKeys.map(k => ({ key: k, n: weekendAssignments[k].length }));
                counts.sort((a, b) => a.n - b.n);
                const under = counts[0];
                const over = counts[counts.length - 1];
                if (!under || !over) break;
                if (over.n <= targetPerWeekend && under.n >= targetPerWeekend) break;
                // allow within ±1 window
                if (over.n - under.n <= 1) break;
                const move = pickMovable(over.key, under.key);
                if (!move) break;
                // Execute move (and optional swap removal)
                const fromList = weekendAssignments[over.key];
                const idxMove = fromList.findIndex(r => r.id === move.resident.id);
                if (idxMove >= 0) {
                  fromList.splice(idxMove, 1);
                  weekendAssignments[under.key].push(move.resident);
                  // update weekendsPerResident
                  weekendsPerResident[move.resident.id] = weekendsPerResident[move.resident.id].filter(k => k !== over.key);
                  weekendsPerResident[move.resident.id].push(under.key);
                  if (move.swapOldKey) {
                    // remove swapOldKey from resident and free it
                    weekendsPerResident[move.resident.id] = weekendsPerResident[move.resident.id].filter(k => k !== move.swapOldKey);
                    const swapIdx = weekendAssignments[move.swapOldKey].findIndex(r => r.id === move.resident.id);
                    if (swapIdx >= 0) weekendAssignments[move.swapOldKey].splice(swapIdx, 1);
                  }
                } else {
                  break;
                }
              }
            }

            shuffledWeekends.forEach((weekend, idx) => {
              console.log(`\n📅 Processing Weekend ${idx + 1} (${weekend.saturday})`);
              
              // Get residents assigned to work this weekend (from weekendAssignments)
              const workingResidents = weekendAssignments[weekend.saturday] || [];
              console.log(`Working residents for this weekend:`, workingResidents.map(r => `${r.name} (PGY-${r.year})`));
              
              // Final safety check: ensure we have residents (should be handled by verification above)
              let finalWorkingResidents = workingResidents;
              if (workingResidents.length === 0) {
                console.log(`🚨 CRITICAL: Weekend ${idx + 1} still has no coverage after verification - reassigning`);
                // Re-fetch after verification pass
                finalWorkingResidents = weekendAssignments[weekend.saturday] || [];
                if (finalWorkingResidents.length === 0) {
                  // Last resort: reassign from a resident who has 2 weekends
                  const shuffled = [...shuffledResidents].sort(() => Math.random() - 0.5);
                  for (const r of shuffled) {
                    if (weekendsPerResident[r.id].length === MAX_WEEKENDS_PER_RESIDENT && !weekendsPerResident[r.id].includes(weekend.saturday)) {
                      const oldWeekend = weekendsPerResident[r.id][0];
                      const oldIndex = weekendAssignments[oldWeekend]?.findIndex(res => res.id === r.id);
                      if (oldIndex !== undefined && oldIndex >= 0) {
                        weekendAssignments[oldWeekend].splice(oldIndex, 1);
                        weekendsPerResident[r.id] = weekendsPerResident[r.id].filter(w => w !== oldWeekend);
                      }
                      weekendsPerResident[r.id].push(weekend.saturday);
                      weekendAssignments[weekend.saturday].push(r);
                      finalWorkingResidents = [r];
                      console.log(`✅ Final reassignment: ${r.name} from ${oldWeekend} to ${weekend.saturday}`);
                      break;
                    }
                  }
                }
              }
              
              if (!base[rotation][weekend.saturday]) {
                base[rotation][weekend.saturday] = { date: weekend.saturday, residents: [], type: "Saturday" };
              }
              if (!base[rotation][weekend.sunday]) {
                base[rotation][weekend.sunday] = { date: weekend.sunday, residents: [], type: "Sunday" };
              }
              
              // Pair residents for this specific weekend (pairings vary per weekend)
              // Use weekend Saturday date as seed to ensure different pairings each weekend
              const { saturdayPairs, sundayPairs } = pairResidentsForWeekend(finalWorkingResidents, weekend.saturday);
              saturdayPairs.forEach(pair => {
                base[rotation][weekend.saturday].residents.push(...pair);
              });
              sundayPairs.forEach(pair => {
                base[rotation][weekend.sunday].residents.push(...pair);
              });
            });
            
            // Validation: Verify all residents have exactly 2 weekends
            console.log('\n📊 Validation - Weekend counts per resident (4-weekend month):');
            uniqueResidents.forEach(r => {
              const count = weekendsPerResident[r.id].length;
              if (count !== 2) {
                console.log(`⚠️ ${r.name}: ${count} weekends (expected 2)`);
              } else {
                console.log(`✅ ${r.name}: ${count} weekends`);
              }
            });
          } else if (numWeekends === 5) {
            // 5 weekends: Assign residents individually per weekend
            // Half work 3 weekends, half work 2 weekends (for balancing)
            // Check if any residents worked 3 weekends in previous 5-week month
            const residentsWhoWorked3 = uniqueResidents.filter(r => threeWeekendWorkers[rotation][r.id]);
            const residentsWhoWorked2 = uniqueResidents.filter(r => !threeWeekendWorkers[rotation][r.id]);
            
            // Determine which residents work 3 vs 2 weekends
            let residentsWork3, residentsWork2;
            
            if (residentsWhoWorked3.length > 0 && residentsWhoWorked2.length > 0) {
              // Previous 5-week month existed: balance the workload
              // Residents who worked 3 weekends before now work only 2
              // Residents who worked 2 weekends before now work 3 weekends
              residentsWork2 = [...residentsWhoWorked3].sort(() => Math.random() - 0.5);
              residentsWork3 = [...residentsWhoWorked2].sort(() => Math.random() - 0.5);
            } else {
              // First 5-weekend month or no previous tracking
              const halfCount = Math.ceil(uniqueResidents.length / 2);
              // Randomly choose which half works 3 weekends
              if (Math.random() < 0.5) {
                residentsWork3 = shuffledResidents.slice(0, halfCount);
                residentsWork2 = shuffledResidents.slice(halfCount);
              } else {
                residentsWork2 = shuffledResidents.slice(0, halfCount);
                residentsWork3 = shuffledResidents.slice(halfCount);
              }
            }
            
            // Track which residents work which weekends
            const weekendsPerResident = {};
            uniqueResidents.forEach(r => {
              weekendsPerResident[r.id] = [];
            });
            
            // Create weekend-specific assignment lists
            const weekendAssignments = {};
            shuffledWeekends.forEach(w => {
              weekendAssignments[w.saturday] = [];
            });
            
            const rotationMonthSeed = `${rotation}-${month}`;
            
            // Assign residents who work 3 weekends
            residentsWork3.forEach((resident) => {
              const residentSeed = `${rotationMonthSeed}-${resident.id}`;
              let residentAssignmentSeed = residentSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
              const getResidentRandom = () => {
                residentAssignmentSeed = (residentAssignmentSeed * 9301 + 49297) % 233280;
                return residentAssignmentSeed / 233280;
              };
              
              const availableWeekends = shuffledWeekends.filter(w => !weekendsPerResident[resident.id].includes(w.saturday));
              const shuffledForResident = [...availableWeekends].sort(() => getResidentRandom() - 0.5);
              const selectedWeekends = shuffledForResident.slice(0, 3);
              
              selectedWeekends.forEach(w => {
                weekendsPerResident[resident.id].push(w.saturday);
                weekendAssignments[w.saturday].push(resident);
              });
            });
            
            // Assign residents who work 2 weekends
            residentsWork2.forEach((resident) => {
              const residentSeed = `${rotationMonthSeed}-${resident.id}`;
              let residentAssignmentSeed = residentSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
              const getResidentRandom = () => {
                residentAssignmentSeed = (residentAssignmentSeed * 9301 + 49297) % 233280;
                return residentAssignmentSeed / 233280;
              };
              
              const availableWeekends = shuffledWeekends.filter(w => !weekendsPerResident[resident.id].includes(w.saturday));
              const shuffledForResident = [...availableWeekends].sort(() => getResidentRandom() - 0.5);
              const selectedWeekends = shuffledForResident.slice(0, 2);
              
              selectedWeekends.forEach(w => {
                weekendsPerResident[resident.id].push(w.saturday);
                weekendAssignments[w.saturday].push(resident);
              });
            });
            
            // Ensure all weekends have coverage
            const targetPerWeekend = Math.ceil(uniqueResidents.length / 2);
            shuffledWeekends.forEach((weekend, weekendIdx) => {
              const currentCount = weekendAssignments[weekend.saturday].length;
              const needsMore = targetPerWeekend - currentCount;
              
              if (needsMore > 0) {
                const residentsWork3Ids = new Set(residentsWork3.map(r => r.id));
                const available = shuffledResidents.filter(r => 
                  weekendsPerResident[r.id].length < (residentsWork3Ids.has(r.id) ? 3 : 2) && 
                  !weekendsPerResident[r.id].includes(weekend.saturday)
                );
                
                if (available.length > 0) {
                  const weekendSeed = `${rotationMonthSeed}-${weekend.saturday}`;
                  let weekendAssignmentSeed = weekendSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
                  const getWeekendRandom = () => {
                    weekendAssignmentSeed = (weekendAssignmentSeed * 9301 + 49297) % 233280;
                    return weekendAssignmentSeed / 233280;
                  };
                  
                  const shuffled = [...available].sort(() => getWeekendRandom() - 0.5);
                  const selected = shuffled.slice(0, Math.min(needsMore, available.length));
                  selected.forEach(r => {
                    if (!weekendsPerResident[r.id].includes(weekend.saturday)) {
                      weekendsPerResident[r.id].push(weekend.saturday);
                      weekendAssignments[weekend.saturday].push(r);
                    }
                  });
                }
              }
              
              console.log(`\nWeekend ${weekendIdx + 1} (${weekend.saturday}) - Assigned:`, weekendAssignments[weekend.saturday].map(r => `${r.name} (PGY-${r.year})`));
            });
            
            // Final verification: Ensure every weekend has at least one resident (guarantee coverage)
            // For 5-weekend months: Half work 2 weekends, half work 3 weekends (strict limits)
            const residentsWork3Ids = new Set(residentsWork3.map(r => r.id));
            
            shuffledWeekends.forEach((weekend, weekendIdx) => {
              const workingResidents = weekendAssignments[weekend.saturday] || [];
              
              if (workingResidents.length === 0) {
                console.log(`⚠️ Weekend ${weekendIdx + 1} (${weekend.saturday}) has NO coverage - assigning residents`);
                
                // Find residents who haven't reached their max
                const availableResidents = shuffledResidents
                  .filter(r => {
                    const maxWeekends = residentsWork3Ids.has(r.id) ? 3 : 2;
                    return weekendsPerResident[r.id].length < maxWeekends;
                  })
                  .sort((a, b) => weekendsPerResident[a.id].length - weekendsPerResident[b.id].length);
                
                if (availableResidents.length > 0) {
                  // Assign from available residents
                  const weekendSeed = `${rotationMonthSeed}-${weekend.saturday}`;
                  let weekendAssignmentSeed = weekendSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
                  const getWeekendRandom = () => {
                    weekendAssignmentSeed = (weekendAssignmentSeed * 9301 + 49297) % 233280;
                    return weekendAssignmentSeed / 233280;
                  };
                  
                  const shuffled = [...availableResidents].sort(() => getWeekendRandom() - 0.5);
                  const needed = Math.max(1, Math.ceil(uniqueResidents.length / 2));
                  const selected = shuffled.slice(0, Math.min(needed, availableResidents.length));
                  selected.forEach(r => {
                    const maxWeekends = residentsWork3Ids.has(r.id) ? 3 : 2;
                    if (!weekendsPerResident[r.id].includes(weekend.saturday) && weekendsPerResident[r.id].length < maxWeekends) {
                      weekendsPerResident[r.id].push(weekend.saturday);
                      weekendAssignments[weekend.saturday].push(r);
                    }
                  });
                  console.log(`✅ Assigned ${selected.length} available residents:`, selected.map(r => `${r.name} (PGY-${r.year})`));
                } else {
                  // All residents at max - need to reassign
                  // Find a resident who has reached max and swap one of their weekends
                  const weekendSeed = `${rotationMonthSeed}-${weekend.saturday}`;
                  let weekendAssignmentSeed = weekendSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
                  const getWeekendRandom = () => {
                    weekendAssignmentSeed = (weekendAssignmentSeed * 9301 + 49297) % 233280;
                    return weekendAssignmentSeed / 233280;
                  };
                  
                  const shuffled = [...shuffledResidents].sort(() => getWeekendRandom() - 0.5);
                  for (const r of shuffled) {
                    const maxWeekends = residentsWork3Ids.has(r.id) ? 3 : 2;
                    if (weekendsPerResident[r.id].length === maxWeekends && !weekendsPerResident[r.id].includes(weekend.saturday)) {
                      // Remove one of their weekends and assign this one
                      const oldWeekend = weekendsPerResident[r.id][0];
                      const oldIndex = weekendAssignments[oldWeekend]?.findIndex(res => res.id === r.id);
                      if (oldIndex !== undefined && oldIndex >= 0) {
                        weekendAssignments[oldWeekend].splice(oldIndex, 1);
                        weekendsPerResident[r.id] = weekendsPerResident[r.id].filter(w => w !== oldWeekend);
                      }
                      weekendsPerResident[r.id].push(weekend.saturday);
                      weekendAssignments[weekend.saturday].push(r);
                      console.log(`✅ Reassigned ${r.name} from ${oldWeekend} to ${weekend.saturday} (still ${weekendsPerResident[r.id].length}/${maxWeekends})`);
                      break;
                    }
                  }
                }
              }
            });
            
            // Process each weekend independently
            // Anti-stacking: balance resident counts across weekends (aim for average T/5 within ±1)
            {
              const weekendKeys = shuffledWeekends.map(w => w.saturday);
              const residentsWork3Ids = new Set(residentsWork3.map(r => r.id));
              const totalAssignments = uniqueResidents.reduce((acc, r) => acc + (residentsWork3Ids.has(r.id) ? 3 : 2), 0);
              const target = Math.round(totalAssignments / weekendKeys.length);
              let safety = 0;
              const pickMovable = (fromKey, toKey) => {
                // Prefer residents who haven't hit their cap; otherwise attempt a swap
                const candidates = [...weekendAssignments[fromKey]].sort((a, b) =>
                  weekendsPerResident[a.id].length - weekendsPerResident[b.id].length
                );
                for (const r of candidates) {
                  const cap = residentsWork3Ids.has(r.id) ? 3 : 2;
                  if (!weekendsPerResident[r.id].includes(toKey) && weekendsPerResident[r.id].length < cap) {
                    return { resident: r, swapOldKey: null };
                  }
                }
                for (const r of weekendAssignments[fromKey]) {
                  const cap = residentsWork3Ids.has(r.id) ? 3 : 2;
                  if (!weekendsPerResident[r.id].includes(toKey) && weekendsPerResident[r.id].length === cap) {
                    const oldKey = weekendsPerResident[r.id].find(k => k !== fromKey);
                    if (oldKey && oldKey !== toKey) {
                      return { resident: r, swapOldKey: oldKey };
                    }
                  }
                }
                return null;
              };
              while (safety++ < 300) {
                const counts = weekendKeys.map(k => ({ key: k, n: weekendAssignments[k].length }));
                counts.sort((a, b) => a.n - b.n);
                const under = counts[0];
                const over = counts[counts.length - 1];
                if (!under || !over) break;
                // allow within ±1 window around target
                if (over.n <= target && under.n >= target - 1) break;
                if (over.n - under.n <= 1) break;
                const move = pickMovable(over.key, under.key);
                if (!move) break;
                // Execute move (and optional swap removal)
                const fromList = weekendAssignments[over.key];
                const idxMove = fromList.findIndex(r => r.id === move.resident.id);
                if (idxMove >= 0) {
                  fromList.splice(idxMove, 1);
                  weekendAssignments[under.key].push(move.resident);
                  weekendsPerResident[move.resident.id] = weekendsPerResident[move.resident.id].filter(k => k !== over.key);
                  weekendsPerResident[move.resident.id].push(under.key);
                  if (move.swapOldKey) {
                    weekendsPerResident[move.resident.id] = weekendsPerResident[move.resident.id].filter(k => k !== move.swapOldKey);
                    const swapIdx = weekendAssignments[move.swapOldKey].findIndex(r => r.id === move.resident.id);
                    if (swapIdx >= 0) weekendAssignments[move.swapOldKey].splice(swapIdx, 1);
                  }
                } else {
                  break;
                }
              }
            }

            shuffledWeekends.forEach((weekend, idx) => {
              console.log(`\n📅 Processing Weekend ${idx + 1} (${weekend.saturday})`);
              
              let workingResidents = weekendAssignments[weekend.saturday] || [];
              console.log(`Working residents for this weekend:`, workingResidents.map(r => `${r.name} (PGY-${r.year})`));
              
              // Final safety check: ensure we have residents (should be handled by verification above)
              if (workingResidents.length === 0) {
                console.log(`🚨 CRITICAL: Weekend ${idx + 1} still has no coverage after verification - reassigning`);
                // Re-fetch after verification pass
                workingResidents = weekendAssignments[weekend.saturday] || [];
                if (workingResidents.length === 0) {
                  // Last resort: reassign from a resident who has reached max
                  const shuffled = [...shuffledResidents].sort(() => Math.random() - 0.5);
                  for (const r of shuffled) {
                    const maxWeekends = residentsWork3Ids.has(r.id) ? 3 : 2;
                    if (weekendsPerResident[r.id].length === maxWeekends && !weekendsPerResident[r.id].includes(weekend.saturday)) {
                      const oldWeekend = weekendsPerResident[r.id][0];
                      const oldIndex = weekendAssignments[oldWeekend]?.findIndex(res => res.id === r.id);
                      if (oldIndex !== undefined && oldIndex >= 0) {
                        weekendAssignments[oldWeekend].splice(oldIndex, 1);
                        weekendsPerResident[r.id] = weekendsPerResident[r.id].filter(w => w !== oldWeekend);
                      }
                      weekendsPerResident[r.id].push(weekend.saturday);
                      weekendAssignments[weekend.saturday].push(r);
                      workingResidents = [r];
                      console.log(`✅ Final reassignment: ${r.name} from ${oldWeekend} to ${weekend.saturday} (${weekendsPerResident[r.id].length}/${maxWeekends})`);
                      break;
                    }
                  }
                }
              }
              
              if (!base[rotation][weekend.saturday]) base[rotation][weekend.saturday] = { date: weekend.saturday, residents: [], type: "Saturday" };
              if (!base[rotation][weekend.sunday]) base[rotation][weekend.sunday] = { date: weekend.sunday, residents: [], type: "Sunday" };
              
              const { saturdayPairs, sundayPairs } = pairResidentsForWeekend(workingResidents, weekend.saturday);
              saturdayPairs.forEach(pair => {
                base[rotation][weekend.saturday].residents.push(...pair);
              });
              sundayPairs.forEach(pair => {
                base[rotation][weekend.sunday].residents.push(...pair);
              });
            });
            
            // Validation: Verify all residents have correct number of weekends
            console.log('\n📊 Validation - Weekend counts per resident:');
            uniqueResidents.forEach(r => {
              const count = weekendsPerResident[r.id].length;
              const maxWeekends = residentsWork3Ids.has(r.id) ? 3 : 2;
              const expected = maxWeekends;
              if (count !== expected) {
                console.log(`⚠️ ${r.name}: ${count} weekends (expected ${expected})`);
              } else {
                console.log(`✅ ${r.name}: ${count} weekends`);
              }
            });
            
            // Track which residents worked 3 weekends this month
            residentsWork3.forEach(r => { threeWeekendWorkers[rotation][r.id] = true; });
            residentsWork2.forEach(r => { threeWeekendWorkers[rotation][r.id] = false; });
          } else {
            // For other cases (3 or fewer weekends): Assign residents individually per weekend
            const targetPerWeekend = Math.ceil(uniqueResidents.length / 2);
            const weekendsPerResident = {};
            const weekendAssignments = {};
            
            uniqueResidents.forEach(r => {
              weekendsPerResident[r.id] = [];
            });
            shuffledWeekends.forEach(w => {
              weekendAssignments[w.saturday] = [];
            });
            
            const rotationMonthSeed = `${rotation}-${month}`;
            
            // Calculate how many weekends each resident should work
            const workCount = Math.min(2, Math.floor(numWeekends / 2) || 1);
            
            // Assign residents to weekends
            shuffledResidents.forEach((resident) => {
              const residentSeed = `${rotationMonthSeed}-${resident.id}`;
              let residentAssignmentSeed = residentSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
              const getResidentRandom = () => {
                residentAssignmentSeed = (residentAssignmentSeed * 9301 + 49297) % 233280;
                return residentAssignmentSeed / 233280;
              };
              
              const availableWeekends = shuffledWeekends.filter(w => !weekendsPerResident[resident.id].includes(w.saturday));
              const shuffledForResident = [...availableWeekends].sort(() => getResidentRandom() - 0.5);
              const selectedWeekends = shuffledForResident.slice(0, workCount);
              
              selectedWeekends.forEach(w => {
                weekendsPerResident[resident.id].push(w.saturday);
                weekendAssignments[w.saturday].push(resident);
              });
            });
            
            // Ensure all weekends have coverage
            shuffledWeekends.forEach((weekend, weekendIdx) => {
              const workingResidents = weekendAssignments[weekend.saturday] || [];
              
              if (workingResidents.length === 0) {
                const weekendSeed = `${rotationMonthSeed}-${weekend.saturday}`;
                let weekendAssignmentSeed = weekendSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
                const getWeekendRandom = () => {
                  weekendAssignmentSeed = (weekendAssignmentSeed * 9301 + 49297) % 233280;
                  return weekendAssignmentSeed / 233280;
                };
                
                const available = shuffledResidents.filter(r => weekendsPerResident[r.id].length < workCount);
                const shuffled = [...available].sort(() => getWeekendRandom() - 0.5);
                const selected = shuffled.slice(0, targetPerWeekend);
                selected.forEach(r => {
                  if (!weekendsPerResident[r.id].includes(weekend.saturday)) {
                    weekendsPerResident[r.id].push(weekend.saturday);
                    weekendAssignments[weekend.saturday].push(r);
                  }
                });
              }
              
              console.log(`\nWeekend ${weekendIdx + 1} (${weekend.saturday}) - Assigned:`, (weekendAssignments[weekend.saturday] || []).map(r => `${r.name} (PGY-${r.year})`));
            });
            
            // Final verification: Ensure every weekend has at least one resident (guarantee coverage)
            shuffledWeekends.forEach((weekend, weekendIdx) => {
              const workingResidents = weekendAssignments[weekend.saturday] || [];
              
              if (workingResidents.length === 0) {
                console.log(`⚠️ Weekend ${weekendIdx + 1} (${weekend.saturday}) has NO coverage - assigning residents`);
                
                const available = shuffledResidents.filter(r => weekendsPerResident[r.id].length < workCount);
                
                if (available.length === 0) {
                  // All residents at max - assign any resident
                  const weekendSeed = `${rotationMonthSeed}-${weekend.saturday}`;
                  let weekendAssignmentSeed = weekendSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
                  const getWeekendRandom = () => {
                    weekendAssignmentSeed = (weekendAssignmentSeed * 9301 + 49297) % 233280;
                    return weekendAssignmentSeed / 233280;
                  };
                  
                  const shuffled = [...shuffledResidents].sort(() => getWeekendRandom() - 0.5);
                  const selected = shuffled.slice(0, Math.max(1, targetPerWeekend));
                  selected.forEach(r => {
                    if (!weekendsPerResident[r.id].includes(weekend.saturday)) {
                      weekendsPerResident[r.id].push(weekend.saturday);
                      weekendAssignments[weekend.saturday].push(r);
                    }
                  });
                  console.log(`✅ Assigned ${selected.length} residents (at max weekends):`, selected.map(r => `${r.name} (PGY-${r.year})`));
                } else {
                  const weekendSeed = `${rotationMonthSeed}-${weekend.saturday}`;
                  let weekendAssignmentSeed = weekendSeed.split('').reduce((acc, char, idx) => acc + char.charCodeAt(0) * Math.pow(31, idx), 0);
                  const getWeekendRandom = () => {
                    weekendAssignmentSeed = (weekendAssignmentSeed * 9301 + 49297) % 233280;
                    return weekendAssignmentSeed / 233280;
                  };
                  
                  const shuffled = [...available].sort(() => getWeekendRandom() - 0.5);
                  const selected = shuffled.slice(0, Math.max(1, targetPerWeekend));
                  selected.forEach(r => {
                    if (!weekendsPerResident[r.id].includes(weekend.saturday)) {
                      weekendsPerResident[r.id].push(weekend.saturday);
                      weekendAssignments[weekend.saturday].push(r);
                    }
                  });
                  console.log(`✅ Assigned ${selected.length} available residents:`, selected.map(r => `${r.name} (PGY-${r.year})`));
                }
              }
            });
            
            // Process each weekend independently
            shuffledWeekends.forEach((weekend, idx) => {
              console.log(`\n📅 Processing Weekend ${idx + 1} (${weekend.saturday})`);
              
              let workingResidents = weekendAssignments[weekend.saturday] || [];
              console.log(`Working residents for this weekend:`, workingResidents.map(r => `${r.name} (PGY-${r.year})`));
              
              // Final safety check: if still no residents, assign all residents
              if (workingResidents.length === 0) {
                console.log(`🚨 CRITICAL: Weekend ${idx + 1} still has no coverage - assigning ALL residents`);
                shuffledResidents.forEach(r => {
                  if (!weekendsPerResident[r.id].includes(weekend.saturday)) {
                    weekendsPerResident[r.id].push(weekend.saturday);
                    weekendAssignments[weekend.saturday].push(r);
                  }
                });
                workingResidents = weekendAssignments[weekend.saturday] || [];
                console.log(`✅ Emergency assignment complete:`, workingResidents.map(r => `${r.name} (PGY-${r.year})`));
              }
              
              if (!base[rotation][weekend.saturday]) base[rotation][weekend.saturday] = { date: weekend.saturday, residents: [], type: "Saturday" };
              if (!base[rotation][weekend.sunday]) base[rotation][weekend.sunday] = { date: weekend.sunday, residents: [], type: "Sunday" };
              
              const { saturdayPairs, sundayPairs } = pairResidentsForWeekend(workingResidents, weekend.saturday);
              saturdayPairs.forEach(pair => {
                base[rotation][weekend.saturday].residents.push(...pair);
              });
              sundayPairs.forEach(pair => {
                base[rotation][weekend.sunday].residents.push(...pair);
              });
            });
          }
        });
      });

      enforceBlockWeekendBoundaries();
    }

    return base;
  };

  const exportToPrint = (pgyLevel, scheduleData) => {
    if (scheduleData?.scheduleType === "blocks") {
      exportBlockScheduleToPrint(pgyLevel, scheduleData);
    } else {
      exportStandardScheduleToPrint(pgyLevel, scheduleData);
    }
  };

  const exportAllToPrint = () => {
    // Separate block schedules from monthly/weekly schedules
    const blockSchedules = Object.entries(schedules).filter(
      ([_, scheduleData]) => scheduleData?.scheduleType === 'blocks'
    );
    const standardSchedules = Object.entries(schedules).filter(
      ([_, scheduleData]) => scheduleData?.scheduleType !== 'blocks'
    );

    // Get all unique months across standard schedules
    const allMonths = new Set();
    standardSchedules.forEach(([_, scheduleData]) => {
      const months = getMonthsBetween(scheduleData.startDate, scheduleData.endDate);
      months.forEach(month => allMonths.add(month));
    });
    const sortedMonths = Array.from(allMonths).sort();

    // Build standard schedule section
    let standardScheduleSection = '';
    if (standardSchedules.length > 0 && sortedMonths.length > 0) {
      standardScheduleSection = `
        <div class="print-section">
          <h2>Monthly/Weekly Schedules</h2>
          <table class="schedule-table">
            <thead>
              <tr>
                <th>Resident</th>
                ${sortedMonths.map(monthStr => {
                  const [year, monthNum] = monthStr.split('-');
                  const date = new Date(year, parseInt(monthNum) - 1);
                  return `<th>${date.toLocaleString('default', { month: 'short', year: 'numeric' })}</th>`;
                }).join('')}
              </tr>
            </thead>
            <tbody>
              ${standardSchedules.map(([pgyLevel, scheduleData]) => [
                `<tr>
                  <td colspan="${sortedMonths.length + 1}" class="pgy-header">
                    Post Graduate Year ${pgyLevel}
                  </td>
                </tr>`,
                ...Object.entries(scheduleData.schedule || {})
                  .filter(([_, residentData]) => residentData.year?.toString() === pgyLevel)
                  .map(([_, residentData]) => `
                    <tr>
                      <td>${residentData.name}</td>
                      ${sortedMonths.map(monthStr => {
                        const rotation = residentData.rotations?.[monthStr];
                        if (!rotation) return '<td></td>';
                        
                        const backgroundColor = rotationColors[rotation] || '#e9ecef';
                        return `
                          <td>
                            <div class="rotation-cell" style="background-color: ${backgroundColor}; color: white;">
                              ${rotation}
                            </div>
                          </td>
                        `;
                      }).join('')}
                    </tr>
                  `)
              ]).flat().join('')}
            </tbody>
          </table>
        </div>
      `;
    }

    // Build combined block schedule table
    let blockScheduleSection = '';
    if (blockSchedules.length > 0) {
      // Get all blocks from all schedules to determine max columns
      const allBlocksByPgy = {};
      let maxBlocks = 0;
      let referencePgy = null;
      
      blockSchedules.forEach(([pgyLevel, scheduleData]) => {
        const { blocks } = buildBlockDefinitionsForExport(scheduleData);
        if (blocks && blocks.length > 0) {
          allBlocksByPgy[pgyLevel] = blocks;
          if (blocks.length > maxBlocks) {
            maxBlocks = blocks.length;
            referencePgy = pgyLevel;
          }
        }
      });

      if (maxBlocks > 0 && referencePgy) {
        const referenceBlocks = allBlocksByPgy[referencePgy];

        // Create header with block labels spanning 2 columns each (major + minor)
        // Create header with block labels spanning 3 columns each (2 major + 1 minor)
        const headerCells = referenceBlocks
          .map(
            (block) =>
              `<th colspan="3" style="text-align: center;">${block.label}<div class="text-muted">${block.dateRange}</div></th>`
          )
          .join("");

        // Add sub-header row for Major (2 columns, no label) and Minor (1 column) labels
        const subHeaderCells = referenceBlocks
          .map(() => 
            `<th colspan="2">Major</th><th>Minor</th>`
          )
          .join("");

        const tableRows = blockSchedules.map(([pgyLevel, scheduleData]) => {
          const blocks = allBlocksByPgy[pgyLevel] || [];
          
          // PGY header row - span across all columns (1 resident + 3 columns per block)
          const totalCols = 1 + (referenceBlocks.length * 3);
          const pgyHeaderRow = `
            <tr>
              <td colspan="${totalCols}" class="pgy-header">
                Post Graduate Year ${pgyLevel}
              </td>
            </tr>
          `;

          // Resident rows for this PGY level
          const residentRows = Object.entries(scheduleData.schedule || {})
            .filter(([_, residentData]) => residentData?.year?.toString() === pgyLevel)
            .map(([_, residentData]) => {
              const cells = referenceBlocks
                .map((block) => {
                  // Find matching block in this schedule's blocks by index
                  const matchingBlock = blocks.find(b => b.index === block.index);
                  if (!matchingBlock) {
                    const emptyMajorLeft = '<td class="block-cell" style="width: 33.33%;"><div class="cell-content"><div class="text-muted">—</div></div></td>';
                    const emptyMajorRight = '<td class="block-cell" style="width: 33.33%;"><div class="cell-content"><div class="text-muted">—</div></div></td>';
                    const emptyMinor = '<td class="block-cell" style="width: 33.33%;"><div class="cell-content"><div class="text-muted">—</div></div></td>';
                    return emptyMajorLeft + emptyMajorRight + emptyMinor;
                  }
                  
                  const majorGroups = summarizeRotationGroups(matchingBlock.majorWeeks, residentData);
                  const minorGroups = summarizeRotationGroups(matchingBlock.minorWeeks, residentData);
                  
                  const majorFormatted = formatMajorRotationsForPrint(majorGroups);
                  const minorFormatted = formatBlockCellHTMLForPrint([], minorGroups);
                  
                  // Build major cells (left and right)
                  let majorCells = '';
                  if (majorFormatted.leftColspan === 2) {
                    // Single 4-week rotation spans both columns
                    majorCells = `<td class="block-cell" colspan="2"><div class="cell-content">${majorFormatted.left}</div></td>`;
                  } else {
                    // Two columns for major
                    majorCells = `<td class="block-cell"><div class="cell-content">${majorFormatted.left}</div></td>`;
                    majorCells += `<td class="block-cell"><div class="cell-content">${majorFormatted.right}</div></td>`;
                  }
                  
                  const minorCell = `<td class="block-cell"><div class="cell-content">${minorFormatted}</div></td>`;
                  
                  return majorCells + minorCell;
                })
                .join("");
              
              return `
                <tr>
                  <td>${residentData.name}</td>
                  ${cells}
                </tr>
              `;
            })
            .join("");

          return pgyHeaderRow + residentRows;
        }).join("");

        blockScheduleSection = `
          <div class="print-section">
            <h2>Block Schedules</h2>
            <table class="schedule-table">
              <thead>
                <tr>
                  <th rowspan="2">Resident</th>
                  ${headerCells}
                </tr>
                <tr>
                  ${subHeaderCells}
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </div>
        `;
      }
    }

    const customStyles = `
      

      h2 {
        margin-top: 30px;
        margin-bottom: 20px;
        page-break-after: avoid;
      }
    `;

    const content = `${standardScheduleSection}${blockScheduleSection}`;

    const printContent = buildPrintDocumentUtil({
      title: 'All Rotation Schedules',
      content,
      headerTitle: 'All Rotation Schedules',
      headerSubtitle: `Generated on ${new Date().toLocaleDateString()}`,
      showPrintButton: true,
      customStyles: PRINT_STYLES + customStyles,
      pageSize: 'landscape'
    });

    openPrintWindow(printContent);
  };

  if (loading) {
    return (
      <div className="bg-main min-vh-100">
        <PageTitle title="Loading..." />
        <Navbar pageTitle="Rotation Scheduler" />

        <div className="bg-white shadow-sm">
          <Container>
            <Row>
              <Col
                lg={3}
                md={4}
                sm={4}
                className="d-flex col-4 order-first order-lg-1 order-md-1 order-sm-1 mb-2 mb-md-0 mb-sm-2 py-lg-0 py-md-3 py-3 align-items-center justify-content-start"
              >
                <h6
                  className="mb-0 fw-semibold"
                >
                Schedules
                </h6>
              </Col>
              <Col lg={9} md={12} sm={12} className="order-last order-lg-2 order-md-3 order-sm-3">
                {!props?.singleTabMode && (
                <Nav
                  variant="tabs"
                  style={{ marginBottom: "-1px" }}
                  onSelect={handleTabChange}
                >
                  <Nav.Item>
                    <Nav.Link
                      eventKey="rotations"
                      active={activeTab === "rotations"}
                    >
                      Rotations
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="weekend-call"
                      active={activeTab === "weekend-call"}
                    >
                      Weekend Call
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="daily-schedule"
                      active={activeTab === "daily-schedule"}
                    >
                      Day to Day
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                )}
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner 
              animation="border" 
              variant="primary" 
              size="lg"
              className="mb-3"
            />
            <div className="text-muted">Loading rotation schedules...</div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-main min-vh-100">
      <PageTitle title="Rotation Scheduler" description="Manage rotation schedules and weekend call assignments" />
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <Navbar pageTitle="Rotation Scheduler" />

      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <Row>
            <Col
              lg={3}
              md={4}
              sm={6}
              className="d-flex col-6 order-first order-lg-1 order-md-1 order-sm-1 py-lg-0 py-md-2 py-2 align-items-center justify-content-start gap-3"
            >
              <h6  className={`mb-0 fw-semibold ${isRotationEditMode || isEditMode ? 'py-3' : 'py-2'}`}>Schedules</h6>
              <div className="d-flex align-items-center" title="View Academic Year">
                <Form.Select
                  size="sm"
                  value={viewAcademicYear}
                  onChange={(e) => setViewAcademicYear(parseInt(e.target.value))}
                  style={{ width: 'auto' }}
                >
                  {availableAcademicYears.map((year) => (
                    <option key={`view-${year}`} value={year}>
                      {year}-{year + 1}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Col>
            <Col lg={4} md={12} sm={12} className="order-last order-lg-2 order-md-3 order-sm-3">
              {!props?.singleTabMode && !isRotationEditMode && !isEditMode && (
              <Nav
                variant="tabs"
                style={{ marginBottom: "-1px" }}
                onSelect={handleTabChange}
              >
                <Nav.Item>
                  <Nav.Link
                    eventKey="rotations"
                    active={activeTab === "rotations"}
                  >
                    Rotations
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="weekend-call"
                    active={activeTab === "weekend-call"}
                  >
                    Weekend Call
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="daily-schedule"
                    active={activeTab === "daily-schedule"}
                  >
                    Day to Day
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              )}
            </Col>
            <Col
              lg={5}
              md={8}
              sm={6}
              className="d-flex gap-2 col-6 order-lg-3 order-md-2 order-sm-2 py-lg-0 py-md-2 py-2 align-items-center justify-content-end"
            >
              {activeTab === "rotations" && (
                <>
                  {!isRotationEditMode ? (
                    
                    <div className="radio-inputs d-flex align-items-center" style={{ width: 'fit-content'}}>
                      <label className="radio">
                        <input
                          type="radio"
                          name="rotation-toolbar-actions"
                          onChange={handleAddRotation}
                          checked={false}
                        />
                        <span className="name bg-white-hover px-2">
                          <i className="bi bi-plus-lg"></i>
                          <span className="d-none d-md-inline ms-2">Rotations</span>
                        </span>
                      </label>
                      {Object.keys(schedules).length > 0 && (
                        <>
                          <label className="radio">
                            <input
                              type="radio"
                              name="rotation-toolbar-actions"
                              onChange={handleRotationEditClick}
                              checked={false}
                            />
                            <span className="name bg-white-hover px-2 d-flex flex-row align-items-center">
                            <i className="bi bi-pencil"></i> <span className="d-none d-md-block ms-2">Edit</span>
                            </span>
                          </label>
                          <label className="radio">
                            <input
                              type="radio"
                              name="rotation-toolbar-actions"
                              onChange={handleClearClick}
                              checked={false}
                            />
                            <span className="name bg-white-hover px-2 d-flex flex-row align-items-center text-danger">
                            <i className="bi bi-trash d-block d-md-none"></i> <span className="d-none d-md-block ms-1">Delete All</span>
                            </span>
                          </label>
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ margin: '6px 0' }}>
                      <div className="radio-inputs d-flex align-items-center" style={{ width: 'fit-content', minWidth: '230px' }}>
                        <label className="radio">
                          <input
                            type="radio"
                            name="rotation-toolbar-edit-actions"
                            onChange={handleRotationSaveChanges}
                            checked={false}
                          />
                          <span className="name bg-white-hover px-2 d-flex flex-row align-items-center">
                            <i className="bi bi-save me-1"></i> Save Changes
                            {(() => {
                              const scheduleType = Object.values(editedRotationSchedules)[0]?.scheduleType;
                              const isBlocks = scheduleType === 'blocks';
                              const changeCount = isBlocks ? countBlockScheduleChanges() : movedRotations.size;
                              return changeCount > 0 ? (
                                <span className="ms-2 badge bg-secondary">
                                  {changeCount} change{changeCount !== 1 ? "s" : ""}
                                </span>
                              ) : null;
                            })()}
                          </span>
                        </label>
                        <label className="radio">
                          <input
                            type="radio"
                            name="rotation-toolbar-edit-actions"
                            onChange={handleRotationCancel}
                            checked={false}
                          />
                          <span className="name bg-white-hover px-2">Cancel</span>
                        </label>
                      </div>
                    </div>
                  )}
                </>
              )}
              {activeTab === "weekend-call" && (
                <>
                  <div className="d-flex align-items-center gap-2">
                 
                    {!isEditMode ? (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setShowWeekendGenModal(true)}
                        >
                          <i className="bi bi-magic"></i>
                          <span className="d-none d-md-inline ms-1">Generate Calls</span>
                        </Button>
                        <div className="radio-inputs d-flex align-items-center" style={{ width: 'fit-content' }}>
                          <label className="radio">
                            <input
                              type="radio"
                              name="weekend-toolbar-actions"
                              onChange={handleEditClick}
                              checked={false}
                            />
                            <span className="name bg-white-hover px-2 d-flex flex-row align-items-center">
                              <i className="bi bi-pencil"></i>
                              <span className="d-none d-md-inline ms-2">Edit</span>
                            </span>
                          </label>
                          <div className="dropdown-container">
                            <Dropdown>
                              <Dropdown.Toggle
                                variant="light-solid"
                                id="export-dropdown"
                                size="sm"
                              >
                                <i className="bi bi-download"></i>
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item onClick={exportWeekendCallToCalendar}>
                                  <i className="bi bi-calendar-plus"></i> Export to Calendar
                                </Dropdown.Item>
                                <Dropdown.Item onClick={exportWeekendCallToExcel}>
                                  <i className="bi bi-file-earmark-excel"></i> Export to Excel
                                </Dropdown.Item>
                                <Dropdown.Item onClick={exportWeekendCallToPrint}>
                                  <i className="bi bi-printer"></i> Print Schedule
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div style={{ margin: '6px 0' }}>
                        <div className="radio-inputs d-flex align-items-center" style={{ width: 'fit-content' }}>
                          <label className="radio">
                            <input
                              type="checkbox"
                              checked={allowRotationMovement}
                              onChange={(e) => setAllowRotationMovement(e.target.checked)}
                            />
                            <span className="name bg-white-hover px-2 d-flex flex-row align-items-center">
                              <i className="bi bi-arrows-move me-1"></i>
                              <span className="d-none d-md-inline">Enable Movement</span>
                            </span>
                          </label>
                          <label className="radio">
                            <input
                              type="radio"
                              name="weekend-toolbar-edit-actions"
                              onChange={handleSaveChanges}
                              checked={false}
                            />
                            <span className="name bg-white-hover px-2 d-flex flex-row align-items-center">
                              <i className="bi bi-save"></i>
                              <span className="ms-1">Save Changes</span>
                              {movedResidents.size > 0 && (
                                <span className="ms-2 badge bg-light text-dark">
                                  {movedResidents.size} change{movedResidents.size !== 1 ? "s" : ""}
                                </span>
                              )}
                            </span>
                          </label>
                          <label className="radio">
                            <input
                              type="radio"
                              name="weekend-toolbar-edit-actions"
                              onChange={handleCancel}
                              checked={false}
                            />
                            <span className="name bg-white-hover px-2">Cancel</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              {activeTab === "daily-schedule" && (
                <Button
                  variant="light-solid"
                  onClick={handleShowPagerModal}
                  size="sm"
                  title="Manage pager numbers for rotations"
                >
                  <i className="bi bi-plus"></i>
                  <span className="d-none d-md-inline ms-1">Pager Numbers</span>
                </Button>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      {props?.singleTabMode && (
        <div className="bg-white border-bottom">
          <Container>
            <Row>
              <Col className="d-flex align-items-center justify-content-between py-2">
                <Button variant="link" onClick={() => navigate('/rotation-scheduler')} className="text-decoration-none">
                  <i className="bi bi-arrow-left"></i> Back to Scheduler
                </Button>
                <div className="text-muted small">
                  Viewing: {activeTab === 'weekend-call' ? 'Weekend Call' : activeTab === 'daily-schedule' ? 'Day to Day' : 'Rotations'}
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )}

      <Container fluid={isRotationEditMode && isBlockViewExpanded} className="mt-4">
        <Row>
          <Col>
            {successMessage && (
              <div className="alert alert-success mb-3" role="alert">
                <i className="bi bi-check-circle me-2"></i>
                {successMessage}
              </div>
            )}
            {error && (
              <div className="alert alert-danger mb-3" role="alert">
                {error}
              </div>
            )}
          </Col>
        </Row>
        <Tab.Content>
          <Tab.Pane eventKey="rotations" active={activeTab === "rotations"}>
            <RotationScheduleTab
              schedules={schedules}
              getCurrentRotationSchedules={getCurrentRotationSchedules}
              isRotationEditMode={isRotationEditMode}
              isBlockViewExpanded={isBlockViewExpanded}
              setIsBlockViewExpanded={setIsBlockViewExpanded}
              blockPageIndices={blockPageIndices}
              setBlockPageIndices={setBlockPageIndices}
              blockPattern={blockPattern}
              blockDuration={blockDuration}
              weekDuration={weekDuration}
              rotationColors={rotationColors}
              rotationTypes={rotationTypes}
              getRotationLabel={getRotationLabel}
              abbreviateRotation={abbreviateRotation}
              renderResidentNameWithPic={renderResidentNameWithPic}
              getRotationColor={getRotationColor}
              movedRotations={movedRotations}
              affectedRotationCells={affectedRotationCells}
              draggedRotation={draggedRotation}
              offServiceResidents={offServiceResidents}
              handleDeleteClick={handleDeleteClick}
              exportToCalendar={exportToCalendar}
              exportToExcel={exportToExcel}
              exportToPrint={exportToPrint}
              exportAllToExcel={exportAllToExcel}
              exportAllToPrint={exportAllToPrint}
              viewAcademicYear={viewAcademicYear}
              handleBlockRotationDrop={handleBlockRotationDrop}
              handleBlockRotationChange={handleBlockRotationChange}
              handleBlockRotationSwap={handleBlockRotationSwap}
              handleRotationChange={handleRotationChange}
              handleRotationDragStart={handleRotationDragStart}
              handleRotationDragOver={handleRotationDragOver}
              handleRotationDrop={handleRotationDrop}
              handleRotationTypeDragStart={handleRotationTypeDragStart}
              handleOpenOffServiceModal={handleOpenOffServiceModal}
              handleEditOffServiceResidents={handleEditOffServiceResidents}
              setEditedRotationSchedules={setEditedRotationSchedules}
            />
          </Tab.Pane>

          <Tab.Pane
            eventKey="weekend-call"
            active={activeTab === "weekend-call"}
          >
              <WeekendCallTab
                schedules={schedules}
                currentMonthIndex={currentMonthIndex}
                handleMonthNavigation={handleMonthNavigation}
                getAllMonths={getAllMonths}
                getWeekendsForMonth={getWeekendsForMonth}
                isEditMode={isEditMode}
                onEditClick={handleEditClick}
                onSaveChanges={handleSaveChanges}
                onCancel={handleCancel}
                onExportCalendar={exportWeekendCallToCalendar}
                onExportExcel={exportWeekendCallToExcel}
                onExportPrint={exportWeekendCallToPrint}
                allowRotationMovement={allowRotationMovement}
                setAllowRotationMovement={setAllowRotationMovement}
                editedWeekendSchedules={getCombinedWeekendSchedules()}
                originalWeekendSchedules={originalWeekendSchedules}
                movedResidents={movedResidents}
                draggedResident={draggedResident}
                handleDragStart={handleDragStart}
                handleDragOver={handleDragOver}
                handleDrop={handleDrop}
                rotationColors={rotationColors}
                capitalizeName={capitalizeName}
                abbreviateRotation={abbreviateRotation}
                onClearWeekendDayForRotation={handleClearWeekendDayForRotation}
                onClearMonthForRotation={handleClearMonthForRotation}
                extraUnassignedByRotationMonth={manuallyUnassignedByRotationMonth}
                approvedVacationRequests={approvedVacationRequests}
                getRotationColor={getRotationColor}
                getWeekStartForDate={getWeekStartForDate}
              />
          </Tab.Pane>

          <Tab.Pane
            eventKey="daily-schedule"
            active={activeTab === "daily-schedule"}
          >
            {Object.keys(schedules).length === 0 ? (
              <Card className="mb-4 shadow-sm border-0 py-4">
                <CardBody className="p-0">
              <div className="text-center py-4">
                <div className="d-flex flex-column align-items-center gap-2">
                  <img src="/images/undraw_schedule_6t8k.svg"  alt="No schedule" className="img-fluid" style={{height: "200px"}} />
                  <h5>No Day to Day schedules available</h5>
                  <p className="text-muted">
                    Please create a rotation schedule first
                  </p>
                </div>
              </div>
              </CardBody>
              </Card>
            ) : (
              <DailyScheduleTab
                currentDate={currentDate}
                getAllDates={getAllDatesLocal}
                handleDateNavigation={handleDateNavigation}
                getResidentsForDate={getResidentsForDate}
                rotationColors={rotationColors}
                getRotationLabel={getRotationLabel}
                pagerNumbers={pagerNumbers}
                residentPagerNumbers={residentPagerNumbers}
                formatPhoneNumber={formatPhoneNumber}
                capitalizeName={capitalizeName}
              />
            )}
          </Tab.Pane>
        </Tab.Content>
      </Container>

      <Modal
        show={showWeekendGenModal}
        onHide={() => setShowWeekendGenModal(false)}
      >
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Generate Weekend Calls</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          <div className="mb-3">
            <div className="fw-semibold mb-2">Mode</div>
            <div className="d-flex flex-row gap-2 flex-wrap">
              <Button
                variant={weekendGenMode === 'one-day-off' ? 'secondary' : 'outline-primary'}
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  setWeekendGenMode('one-day-off');
                }}
                style={{ cursor: 'pointer' }}
                className="w-100 py-3"
              >
                <div className="d-flex flex-column align-items-start justify-content-start">
                  <Form.Check
                    type="checkbox"
                    label="One day off per weekend"
                    checked={weekendGenMode === 'one-day-off'}
                    onChange={() => setWeekendGenMode('one-day-off')}
                  />
                  <span className="ms-4" style={{fontSize: "0.75rem", color: "var(--gray-600)"}}>One day off per weekend</span>
                </div>
              </Button>
              <Button
                variant={weekendGenMode === 'gold-weekend' ? 'secondary' : 'outline-primary'}
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  setWeekendGenMode('gold-weekend');
                }}
                style={{ cursor: 'pointer' }}
                className="w-100 py-3"
              >
                <div className="d-flex flex-column align-items-start justify-content-start">
                  <Form.Check
                    type="checkbox"
                    label="Gold weekend (two full weekends off)"
                    checked={weekendGenMode === 'gold-weekend'}
                    onChange={() => setWeekendGenMode('gold-weekend')}
                  />
                  <span className="ms-4" style={{fontSize: "0.75rem", color: "var(--gray-600)"}}>Gold weekend (two full weekends off)</span>
                </div>
              </Button>
            </div>
          </div>

          <div className="mb-3">
            <div className="fw-semibold mb-2">Exclude rotations</div>
            <div className="text-muted small mb-2">Residents on selected rotations will not be assigned weekend calls.</div>
            <Row className="mb-2">
              <Col xs={12} sm={8} md={6} lg={5}>
                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" size="sm" id="exclude-rotation-dropdown">
                    Select rotation to exclude…
                  </Dropdown.Toggle>
                  <Dropdown.Menu style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {rotationTypes
                      .filter((r) => !excludedRotationsForWeekend.includes(r))
                      .map((rotation) => (
                        <Dropdown.Item 
                          key={`exclude-option-${rotation}`} 
                          onClick={() => {
                            if (!excludedRotationsForWeekend.includes(rotation)) {
                              setExcludedRotationsForWeekend([...excludedRotationsForWeekend, rotation]);
                            }
                          }}
                        >
                          {rotation}
                        </Dropdown.Item>
                      ))}
                    {rotationTypes.filter((r) => !excludedRotationsForWeekend.includes(r)).length === 0 && (
                      <Dropdown.Item disabled>No rotations available</Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>
            <div>
              {excludedRotationsForWeekend.length === 0 && (
                <div className="text-muted small">No rotations excluded.</div>
              )}
              {excludedRotationsForWeekend.map((rotation) => (
                <span key={`excluded-badge-${rotation}`} className="badge rounded-2 bg-secondary me-2 mb-2">
                  {rotation}
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-2 align-middle text-danger"
                    onClick={() => setExcludedRotationsForWeekend(excludedRotationsForWeekend.filter((r) => r !== rotation))}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                </span>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button variant="light" size="sm" onClick={() => setShowWeekendGenModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={async () => {
            try {
              const weekend = generateWeekendSchedulesWithOptions(schedules, {
                mode: weekendGenMode,
                excludedRotations: excludedRotationsForWeekend,
                offServiceResidents: offServiceResidents,
              });
              await saveWeekendCallSchedules(weekend);
              await fetchSchedules(); // Refresh to show new schedules
              showSuccess('Weekend call schedules generated');
              setShowWeekendGenModal(false);
              setExcludedRotationsForWeekend([]);
            } catch (e) {
              console.error(e);
              setError('Failed to generate weekend call schedules');
            }
          }}>
            Generate
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showModal}
        backdrop="static"
        keyboard={false}
        onHide={() => {
          setShowModal(false);
          setError("");
          setNewCustomRotation("");
          setShowCustomRotationInput(false);
          setIsCustomRotationEditMode(false);
          resetScheduleConfigState();
        }}
        size="lg"
      >
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-50"> <div className="d-flex flex-column justify-content-start align-items-start">
            <span>Add Rotations</span>
            <div className="text-hl"></div>
          </div>
        </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4" style={{ position: 'relative', minHeight: '200px' }}>
          {generatingSchedule && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
            >
              <div className="text-center">
                <Spinner animation="border" variant="primary" className="mb-2" />
                <div className="text-muted">Generating schedule...</div>
              </div>
            </div>
          )}
          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}
          <Form>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <FloatingLabel controlId="floatingInput" label="Academic Year" className="mb-3">
                  <Form.Select
                    value={selectedAcademicYear}
                    onChange={(e) => setSelectedAcademicYear(parseInt(e.target.value))}
                  >
                    {availableAcademicYears.map((year) => (
                      <option key={year} value={year}>
                        {year}-{year + 1}
                      </option>
                    ))}
                  </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <FloatingLabel controlId="floatingInput" label="Schedule Type" className="mb-3">
                  <Form.Select
                    value={scheduleType}
                    onChange={(e) => {
                      setScheduleType(e.target.value);
                      if (e.target.value !== "blocks") {
                        setScheduleConfigs((prev) => prev.map((cfg) => ({ ...cfg, blockStep: 1 })));
                      }
                    }}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="blocks">Blocks</option>
                  </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <FloatingLabel controlId="floatingInput" label="Start Date" className="mb-3">
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={scheduleType === "monthly"}
                  />
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

           

            {scheduleType === "weekly" && (
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <FloatingLabel controlId="floatingInput" label="Week Duration (weeks per rotation)" className="mb-3">
                    <Form.Select
                      value={weekDuration}
                      onChange={(e) => setWeekDuration(parseInt(e.target.value))}
                    >
                      <option value={1}>1 Week</option>
                      <option value={2}>2 Weeks</option>
                      <option value={3}>3 Weeks</option>
                      <option value={4}>4 Weeks</option>
                      <option value={6}>6 Weeks</option>
                      <option value={8}>8 Weeks</option>
                    </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <FloatingLabel controlId="floatingInput" label="End Date" className="mb-3">
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {scheduleType === "blocks" && (
              <>
                <Row className="mb-3 border-bottom">
                  <Col md={4}>
                    <Form.Group>
                      <FloatingLabel controlId="floatingInput" label="Block Duration (weeks per block)" className="mb-3">
                      <Form.Select
                        value={blockDuration}
                        onChange={(e) => {
                          const newDuration = parseInt(e.target.value);
                          setBlockDuration(newDuration);
                          // Reset pattern to default when duration changes
                          const [currentMajor, currentMinor] = blockPattern.split('-').map(Number);
                          if (currentMajor + currentMinor !== newDuration) {
                            // Set default pattern (major gets more weeks if odd, split evenly if even)
                            const defaultMajor = Math.ceil(newDuration / 2);
                            const defaultMinor = newDuration - defaultMajor;
                            setBlockPattern(`${defaultMajor}-${defaultMinor}`);
                          }
                        }}
                      >
                        <option value={4}>4 Weeks</option>
                        <option value={5}>5 Weeks</option>
                        <option value={6}>6 Weeks</option>
                        <option value={8}>8 Weeks</option>
                      </Form.Select>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
             
                  <Col md={2}>
                          <FloatingLabel controlId="floatingInput" label="Major" className="mb-3">
                          <Form.Control
                            type="number"
                            min="1"
                            max={blockDuration - 1}
                            value={(() => {
                              const [major] = blockPattern.split('-').map(Number);
                              return major;
                            })()}
                            onChange={(e) => {
                              const majorWeeks = parseInt(e.target.value) || 1;
                              const minorWeeks = blockDuration - majorWeeks;
                              if (majorWeeks >= 1 && majorWeeks < blockDuration && minorWeeks >= 1) {
                                setBlockPattern(`${majorWeeks}-${minorWeeks}`);
                              }
                            }}
                          />
                          </FloatingLabel>
                        </Col>
                        <Col md={2}>
                          <FloatingLabel controlId="floatingInput" label="Minor" className="mb-3">
                          <Form.Control
                            type="number"
                            min="1"
                            max={blockDuration - 1}
                            value={(() => {
                              const [, minor] = blockPattern.split('-').map(Number);
                              return minor;
                            })()}
                            onChange={(e) => {
                              const minorWeeks = parseInt(e.target.value) || 1;
                              const majorWeeks = blockDuration - minorWeeks;
                              if (minorWeeks >= 1 && minorWeeks < blockDuration && majorWeeks >= 1) {
                                setBlockPattern(`${majorWeeks}-${minorWeeks}`);
                              }
                            }}
                          />
                          </FloatingLabel>
  
                        </Col>                  
                        <Col md={4}>
                    <Form.Group>
                      <FloatingLabel controlId="floatingInput" label="End Date" className="mb-3">
                      <Form.Control
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                      </FloatingLabel>
                    </Form.Group>
                  </Col> 
                  <Col md={12}>
                          <div className="mb-3">
                            <div className="alert alert-light mb-0" style={{ fontSize: '0.875rem' }}>
                              <h5 className="mb-2 fw-semibold">Block Pattern Configuration</h5>
                              <p className="mb-0">Configure how weeks are split within each block (must add up to {blockDuration} weeks)</p>

                              <strong>Current Pattern:</strong> {(() => {
                                const [major, minor] = blockPattern.split('-').map(Number);
                                return `${major}-${minor} (${major} weeks major + ${minor} weeks minor)`;
                              })()}
                              {(() => {
                                const [major, minor] = blockPattern.split('-').map(Number);
                                if (major + minor !== blockDuration) {
                                  return <span className="text-danger ms-2">⚠ Must equal {blockDuration} weeks</span>;
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                  </Col>
                </Row>
              </>
            )}

            {scheduleType === "monthly" && (
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <FloatingLabel controlId="floatingInput" label="End Date (optional)" className="mb-3">
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      max={getAcademicYearForYear(selectedAcademicYear).end}
                    />
                    <Form.Text className="text-muted">
                      Leave as is to use full academic year (July 1 - June 30)
                    </Form.Text>
                    </FloatingLabel>

                  </Form.Group>
                </Col>
                <Col md={6}>
                  <div className="d-flex align-items-center mt-2">
                    <div className="text-muted">
                      <span className="fw-semibold">Academic Year:</span> {getAcademicYearForYear(selectedAcademicYear).display}
                      <br />
                      <span className="fw-semibold">Duration:</span> {(() => {
                        if (!startDate || !endDate) return "0 months";
                        const months = getMonthsBetween(startDate, endDate);
                        return `${months.length} months`;
                      })()}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
 <div className="mb-3">
              <Accordion activeKey={activeScheduleConfigId} className="mb-1" onSelect={(key) => key && setActiveScheduleConfigId(key)}>
                {scheduleConfigs.map((config, index) => (
                  <Accordion.Item eventKey={config.id} key={config.id}>
                    <Accordion.Header>
                      <div className="w-100">
                        <div className="d-flex flex-md-row flex-column align-items-center justify-content-md-between justify-content-start gap-2">
                          <div className="d-flex flex-row align-items-center gap-2 flex-grow-1">
                            <span className="small fw-semibold text-muted">
                              Level {index + 1}
                            </span>
                            <Form.Select
                              size="sm"
                              style={{ width: 'fit-content' }}
                              value={config.pgyLevel}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                const value = e.target.value;
                                setActiveScheduleConfigId(config.id);
                                setScheduleConfigs((prev) =>
                                  prev.map((cfg) =>
                                    cfg.id === config.id ? { ...cfg, pgyLevel: value } : cfg
                                  )
                                );
                              }}
                            >
                              <option value="">Select PGY</option>
                              {(pgyLevelOptions.length > 0
                                ? pgyLevelOptions
                                : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))
                              ).map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Form.Select>
                            <span className="text-muted small">
                              {(config.selectedRotations || []).length} rotation{(config.selectedRotations || []).length !== 1 ? "s" : ""}
                            </span>
                          </div>
                             {config.id === activeScheduleConfigId && scheduleType === "blocks" && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <div className="d-flex align-items-center justify-content-center">
                              <div className="d-flex align-items-center" style={{ width: '100%', maxWidth: '500px' }}>
                                <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
                                  <div className="text-center">
                                    <div className={`small fw-semibold ${blockStep >= 1 ? 'text-primary' : 'text-muted'}`}>
                                      Select Rotations
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className={`${blockStep >= 2 ? 'bg-primary' : 'bg-secondary'}`}
                                  style={{ height: '2px', flex: 1, margin: '0 10px', marginTop: '10px' }}
                                />
                                <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
                                  <div className="text-center">
                                    <div className={`small fw-semibold ${blockStep >= 2 ? 'text-primary' : 'text-muted'}`}>
                                      Configure Blocks
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                          <Button
                            variant="light-lite"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveScheduleLevel(config.id);
                            }}
                            disabled={scheduleConfigs.length === 1}
                            className="me-2"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                     
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      {config.id === activeScheduleConfigId && scheduleType !== "blocks" && (
                        <>
                          {programDetails?.specialty && (
                            <div className="alert alert-info mb-3 mt-3">
                              <i className="bi bi-hospital me-2"></i>
                              <strong>{programDetails.specialty}</strong> • {rotationTypes.length} rotation types available
                            </div>
                          )}
                          <Form.Group className="mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h5>Select Rotations</h5>
                              <div className="d-flex align-items-center gap-2">
                                {customRotations.length > 0 && (
                                  <Button
                                    variant={isCustomRotationEditMode ? "danger" : "light"}
                                    size="sm"
                                    onClick={handleToggleCustomRotationEditMode}
                                  >
                                    <i className={`bi ${isCustomRotationEditMode ? "bi-x-circle" : "bi-pencil"}`}></i>
                                    <span className="d-none d-md-inline ms-1">{isCustomRotationEditMode ? "Cancel Edit" : "Edit Custom"}</span>
                                  </Button>
                                )}
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={handleShowCustomRotationInput}
                                  disabled={showCustomRotationInput}
                                >
                                  <i className="bi bi-plus"></i> <span className="d-none d-md-inline ms-1">Add Rotation</span>
                                </Button>
                              </div>
                            </div>
                            {showCustomRotationInput && (
                              <div className="border rounded p-3 mb-3 bg-light">
                                <Row>
                                  <Col md={8}>
                                    <Form.Control
                                      type="text"
                                      placeholder="Enter custom rotation name..."
                                      value={newCustomRotation}
                                      onChange={(e) => setNewCustomRotation(e.target.value)}
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          handleAddCustomRotation();
                                        }
                                      }}
                                    />
                                  </Col>
                                  <Col md={4}>
                                    <div className="d-flex gap-2">
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleAddCustomRotation}
                                        disabled={!newCustomRotation.trim() || customRotationLoading}
                                      >
                                        {customRotationLoading ? (
                                          <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Adding...
                                          </>
                                        ) : (
                                          'Add'
                                        )}
                                      </Button>
                                      <Button
                                        variant="light"
                                        size="sm"
                                        onClick={handleCancelCustomRotation}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            )}
                            <Row>
                              {rotationTypes.map((rotation) => (
                                <Col xs={12} sm={6} md={6} lg={4} key={`inline-${config.id}-${rotation}`}>
                                  <div className="position-relative" style={{ width: "100%" }}>
                                    <Button
                                      variant={selectedRotations.includes(rotation) ? "secondary mb-2" : "outline-primary mb-2"}
                                      size="sm"
                                      style={{
                                        width: "100%",
                                        padding: "10px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        backgroundColor: selectedRotations.includes(rotation) ? "#f8f9fa" : "",
                                        borderColor: selectedRotations.includes(rotation) ? "#d3d6da" : "",
                                        color: selectedRotations.includes(rotation) ? "#000" : "",
                                        borderStyle: selectedRotations.includes(rotation) ? "solid" : "dashed",
                                      }}
                                      onClick={() => handleRotationSelect(rotation)}
                                    >
                                      <Form.Check
                                        type="checkbox"
                                        id={`inline-${config.id}-${rotation}`}
                                        label={rotation}
                                        checked={selectedRotations.includes(rotation)}
                                        onChange={() => handleRotationSelect(rotation)}
                                      />
                                      {selectedRotations.includes(rotation) && (
                                        <Form.Select
                                          size="sm"
                                          className="py-1 px-2"
                                          style={{ width: "80px" }}
                                          value={rotationFrequency[rotation] || 1}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleFrequencyChange(rotation, parseInt(e.target.value, 10));
                                          }}
                                        >
                                          <option value="1">Once</option>
                                          <option value="2">Twice</option>
                                          <option value="3">Three Times</option>
                                          <option value="4">Four Times</option>
                                        </Form.Select>
                                      )}
                                    </Button>
                                    {isCustomRotationEditMode && customRotations.some(cr => cr.name === rotation) && (
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        className="position-absolute rounded-circle"
                                        style={{
                                          top: "-8px",
                                          right: "-8px",
                                          width: "28px",
                                          height: "28px",
                                          padding: "0",
                                          fontSize: "16px",
                                          fontWeight: "bold",
                                          zIndex: 10,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                          border: "2px solid white"
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveCustomRotation(rotation);
                                        }}
                                        title="Remove custom rotation"
                                      >
                                        ×
                                      </Button>
                                    )}
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </Form.Group>
                          {selectedRotations.length > 0 && (
                            <>
                              <Form.Group className="mb-3">
                                <Form.Label>Recurrent Rotations (for repeating rotations)</Form.Label>
                                <div className="text-muted mb-2">
                                  Select rotations that should be repeated if you have fewer rotations than time periods:
                                </div>
                                <Row>
                                  {selectedRotations.map((rotation) => (
                                    <Col xs={12} sm={6} md={4} key={`inline-recurrent-${config.id}-${rotation}`}>
                                      <Form.Check
                                        type="checkbox"
                                        id={`inline-recurrent-${config.id}-${rotation}`}
                                        label={rotation}
                                        checked={recurrentRotations.includes(rotation)}
                                        onChange={() => handleRecurrentRotationChange(rotation)}
                                      />
                                    </Col>
                                  ))}
                                </Row>
                                {recurrentRotations.length === 0 && selectedRotations.length > 0 && (
                                  <div className="text-info mt-2">
                                    <i className="bi bi-info-circle me-1"></i>
                                    If no recurrent rotations are selected, all selected rotations will be repeated as needed.
                                  </div>
                                )}
                              </Form.Group>
                              <div className="alert alert-info mb-0">
                                <strong>Summary:</strong><br />
                                • Selected rotations: {selectedRotations.length}<br />
                                • Total rotation count: {selectedRotations.reduce((t, rotation) => t + (rotationFrequency[rotation] || 1), 0)}<br />
                                • {scheduleType === "monthly"
                                  ? `Duration: ${startDate && endDate ? getMonthsBetween(startDate, endDate).length : 0} months`
                                  : `Duration: ${startDate && endDate ? getWeeksBetween(startDate, endDate).length : 0} weeks (${weekDuration} weeks per rotation)`}
                                <br />
                                • {recurrentRotations.length > 0
                                  ? `Recurrent rotations: ${recurrentRotations.length}`
                                  : "All rotations will be repeated as needed"}
                                {(() => {
                                  const totalRotationCount = selectedRotations.reduce((total, rotation) => {
                                    const frequency = rotationFrequency[rotation] || 1;
                                    return total + frequency;
                                  }, 0);

                                  if (scheduleType === "monthly" && startDate && endDate) {
                                    const monthCount = getMonthsBetween(startDate, endDate).length;
                                    if (totalRotationCount < monthCount) {
                                      return (
                                        <>
                                          <br />
                                          <span className="text-warning">
                                            <i className="bi bi-exclamation-triangle me-1"></i>
                                            Some rotations will be repeated
                                          </span>
                                        </>
                                      );
                                    }
                                  }

                                  if (scheduleType === "weekly" && startDate && endDate) {
                                    const weekCount = getWeeksBetween(startDate, endDate).length;
                                    const rotationSlots = Math.ceil(weekCount / weekDuration);
                                    if (totalRotationCount < rotationSlots) {
                                      return (
                                        <>
                                          <br />
                                          <span className="text-warning">
                                            <i className="bi bi-exclamation-triangle me-1"></i>
                                            Some rotations will be repeated ({weekCount} total weeks, {rotationSlots} rotation slots)
                                          </span>
                                        </>
                                      );
                                    }
                                  }
                                  return null;
                                })()}
                              </div>
                            </>
                          )}
                        </>
                      )}
                      {config.id === activeScheduleConfigId && scheduleType === "blocks" && (
                        <>
                          {blockStep === 1 && (
                            <>
                              {programDetails?.specialty && (
                                <div className="alert alert-info mb-3">
                                  <i className="bi bi-hospital me-2"></i>
                                  <strong>{programDetails.specialty}</strong> • {rotationTypes.length} rotation types available
                                </div>
                              )}
                              <Form.Group>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h5>Select Rotations</h5>
                                  <div className="d-flex align-items-center gap-2">
                                    {customRotations.length > 0 && (
                                      <Button
                                        variant={isCustomRotationEditMode ? "danger" : "light"}
                                        size="sm"
                                        onClick={handleToggleCustomRotationEditMode}
                                      >
                                        <i className={`bi ${isCustomRotationEditMode ? "bi-x-circle" : "bi-pencil"}`}></i>
                                        <span className="d-none d-md-inline ms-1">{isCustomRotationEditMode ? "Cancel Edit" : "Edit Custom"}</span>
                                      </Button>
                                    )}
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={handleShowCustomRotationInput}
                                      disabled={showCustomRotationInput}
                                    >
                                      <i className="bi bi-plus"></i> <span className="d-none d-md-inline ms-1">Add Rotation</span>
                                    </Button>
                                  </div>
                                </div>
                                {showCustomRotationInput && (
                                  <div className="border rounded p-3 mb-3 bg-light">
                                    <Row>
                                      <Col md={8}>
                                        <Form.Control
                                          type="text"
                                          placeholder="Enter custom rotation name..."
                                          value={newCustomRotation}
                                          onChange={(e) => setNewCustomRotation(e.target.value)}
                                          onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                              handleAddCustomRotation();
                                            }
                                          }}
                                        />
                                      </Col>
                                      <Col md={4}>
                                        <div className="d-flex gap-2">
                                          <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={handleAddCustomRotation}
                                            disabled={!newCustomRotation.trim() || customRotationLoading}
                                          >
                                            {customRotationLoading ? (
                                              <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Adding...
                                              </>
                                            ) : (
                                              'Add'
                                            )}
                                          </Button>
                                          <Button
                                            variant="light"
                                            size="sm"
                                            onClick={handleCancelCustomRotation}
                                          >
                                            Cancel
                                          </Button>
                                        </div>
                                      </Col>
                                    </Row>
                                  </div>
                                )}
                                <Row>
                                  {rotationTypes.map((rotation) => (
                                    <Col xs={12} sm={6} md={6} lg={4} key={`block-inline-${config.id}-${rotation}`}>
                                      <div className="position-relative" style={{ width: "100%" }}>
                                        <Button
                                          variant={selectedRotations.includes(rotation) ? "secondary mb-2" : "outline-primary mb-2"}
                                          size="sm"
                                          style={{
                                            width: "100%",
                                            padding: "10px",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            backgroundColor: selectedRotations.includes(rotation) ? "#f8f9fa" : "",
                                            borderColor: selectedRotations.includes(rotation) ? "#d3d6da" : "",
                                            color: selectedRotations.includes(rotation) ? "#000" : "",
                                            borderStyle: selectedRotations.includes(rotation) ? "solid" : "dashed",
                                          }}
                                          onClick={() => handleRotationSelect(rotation)}
                                        >
                                          <Form.Check
                                            type="checkbox"
                                            id={`block-inline-${config.id}-${rotation}`}
                                            label={rotation}
                                            checked={selectedRotations.includes(rotation)}
                                            onChange={() => handleRotationSelect(rotation)}
                                          />
                                        </Button>
                                        {isCustomRotationEditMode && customRotations.some(cr => cr.name === rotation) && (
                                          <Button
                                            variant="danger"
                                            size="sm"
                                            className="position-absolute rounded-circle"
                                            style={{
                                              top: "-8px",
                                              right: "-8px",
                                              width: "28px",
                                              height: "28px",
                                              padding: "0",
                                              fontSize: "16px",
                                              fontWeight: "bold",
                                              zIndex: 10,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                              border: "2px solid white"
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRemoveCustomRotation(rotation);
                                            }}
                                            title="Remove custom rotation"
                                          >
                                            ×
                                          </Button>
                                        )}
                                      </div>
                                    </Col>
                                  ))}
                                </Row>
                              </Form.Group>
                            </>
                          )}

                          {blockStep === 2 && (
                            <>
                              <div className="alert alert-info mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <strong>Selected Rotations:</strong> {selectedRotations.length} rotation{selectedRotations.length !== 1 ? 's' : ''}
                                    <br />
                                    <small className="text-muted">
                                      {selectedRotations.slice(0, 5).join(', ')}
                                      {selectedRotations.length > 5 && ` + ${selectedRotations.length - 5} more`}
                                    </small>
                                  </div>
                                </div>
                              </div>
                              <Form.Group className="mb-3">
                                <Form.Label className="mb-3">
                                  <strong>Configure Block Rotations</strong>
                                </Form.Label>
                                <div className="text-muted mb-3">
                                  Assign rotations to columns based on block pattern ({blockPattern}). Configure total weeks, min, and max block sizes for each rotation.
                                </div>
                                {(() => {
                                  const totalWeeksInSchedule = startDate && endDate ? getWeeksBetween(startDate, endDate).length : 0;
                                  const totalConfiguredWeeks = selectedRotations.reduce((sum, rotation) => {
                                    const config = blockRotations[rotation];
                                    return sum + (config?.totalWeeks || 0);
                                  }, 0);
                                  const remainingWeeks = totalWeeksInSchedule - totalConfiguredWeeks;
                                  const isMatch = totalConfiguredWeeks === totalWeeksInSchedule;

                                  return (
                                    <div className={`alert ${isMatch ? 'alert-success' : remainingWeeks > 0 ? 'alert-warning' : 'alert-danger'} mb-3`}>
                                      <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                          <strong>Week Configuration Summary:</strong>
                                          <br />
                                          <div className="d-flex flex-row gap-3">
                                            <div>
                                              <small className="fw-bold text-muted me-2">Total Weeks:</small> <span className="text-muted">{totalWeeksInSchedule} weeks</span>
                                            </div>
                                            <div>
                                              <small className="fw-bold text-muted me-2">Configured Weeks:</small> <span className="text-muted">{totalConfiguredWeeks} weeks</span>
                                            </div>
                                          </div>
                                        </div>
                                        <div>
                                          {isMatch ? (
                                            <span className="badge bg-success">
                                              <i className="bi bi-check-circle me-1"></i> Complete
                                            </span>
                                          ) : remainingWeeks > 0 ? (
                                            <span className="badge bg-warning text-dark">
                                              <i className="bi bi-exclamation-triangle me-1"></i> {remainingWeeks} weeks remaining
                                            </span>
                                          ) : (
                                            <span className="badge bg-danger">
                                              <i className="bi bi-x-circle me-1"></i> {Math.abs(remainingWeeks)} weeks over
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                                {(() => {
                                  const [majorWeeks, minorWeeks] = blockPattern.split('-').map(Number);
                                  return (
                                    <div className="border rounded-2">
                                      <Table responsive className="mb-0">
                                        <thead>
                                          <tr className="custom-header-bg">
                                            <th className="ps-3">Rotation</th>
                                            <th>Column</th>
                                            <th>Total Weeks</th>
                                            <th>Min Block Size</th>
                                            <th>Max Block Size</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {selectedRotations.map((rotation) => {
                                            const getDefaultConfig = (column) => {
                                              if (column === 'major') {
                                                return { column: 'major', totalWeeks: 0, minBlockSize: 2, maxBlockSize: majorWeeks };
                                              }
                                              return { column: 'minor', totalWeeks: 0, minBlockSize: minorWeeks, maxBlockSize: minorWeeks };
                                            };
                                            const config = blockRotations[rotation] || getDefaultConfig('major');
                                            return (
                                              <tr key={`block-inline-config-${rotation}`}>
                                                <td className="ps-3"><strong>{rotation}</strong></td>
                                                <td>
                                                  <Form.Select
                                                    size="sm"
                                                    value={config.column}
                                                    onChange={(e) => {
                                                      const newColumn = e.target.value;
                                                      const [majorWeeks, minorWeeks] = blockPattern.split('-').map(Number);
                                                      const newMinBlockSize = newColumn === 'major' ? 2 : minorWeeks;
                                                      const newMaxBlockSize = newColumn === 'major' ? majorWeeks : minorWeeks;
                                                      setBlockRotations({
                                                        ...blockRotations,
                                                        [rotation]: {
                                                          ...config,
                                                          column: newColumn,
                                                          minBlockSize: newMinBlockSize,
                                                          maxBlockSize: newMaxBlockSize,
                                                        }
                                                      });
                                                    }}
                                                  >
                                                    <option value="major">{majorWeeks} Week Column</option>
                                                    <option value="minor">{minorWeeks} Week Column</option>
                                                  </Form.Select>
                                                </td>
                                                <td>
                                                  <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min="0"
                                                    value={config.totalWeeks || 0}
                                                    onChange={(e) => {
                                                      setBlockRotations({
                                                        ...blockRotations,
                                                        [rotation]: {
                                                          ...config,
                                                          totalWeeks: parseInt(e.target.value, 10) || 0,
                                                        }
                                                      });
                                                    }}
                                                  />
                                                </td>
                                                <td>
                                                  <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min="1"
                                                    value={config.minBlockSize || (config.column === 'major' ? 2 : minorWeeks)}
                                                    onChange={(e) => {
                                                      setBlockRotations({
                                                        ...blockRotations,
                                                        [rotation]: {
                                                          ...config,
                                                          minBlockSize: parseInt(e.target.value, 10) || 1,
                                                        }
                                                      });
                                                    }}
                                                  />
                                                </td>
                                                <td>
                                                  <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min="1"
                                                    value={config.maxBlockSize || (config.column === 'major' ? majorWeeks : minorWeeks)}
                                                    onChange={(e) => {
                                                      setBlockRotations({
                                                        ...blockRotations,
                                                        [rotation]: {
                                                          ...config,
                                                          maxBlockSize: parseInt(e.target.value, 10) || 1,
                                                        }
                                                      });
                                                    }}
                                                  />
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </Table>
                                    </div>
                                  );
                                })()}
                              </Form.Group>
                            </>
                          )}
                          <div className="d-flex justify-content-end mt-3">
                            {blockStep === 1 ? (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                  if (selectedRotations.length > 0) {
                                    setBlockStep(2);
                                  } else {
                                    setError("Please select at least one rotation before proceeding");
                                  }
                                }}
                                disabled={selectedRotations.length === 0}
                              >
                                Next: Configure Block Rotations <i className="bi bi-arrow-right ms-2"></i>
                              </Button>
                            ) : (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setBlockStep(1)}
                              >
                                <i className="bi bi-arrow-left me-2"></i> Back to Rotation Selection
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
              <Button
                variant="link-dashed"
                size="sm"
                className="w-100 py-3"
                onClick={handleAddScheduleLevel}
                disabled={scheduleConfigs.length >= maxPGYLevel}
              >
                add new level
              </Button>
            </div>
            {/* Custom Rotation Management */}
            {/*<Form.Group className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleShowCustomRotationInput}
                    disabled={showCustomRotationInput}
                  >
                    <i className="bi bi-plus me-1"></i> Add Custom Rotation
                  </Button>
                  {customRotations.length > 0 && (
                    <Button
                      variant={isCustomRotationEditMode ? "danger" : "outline-secondary"}
                      size="sm"
                      onClick={handleToggleCustomRotationEditMode}
                    >
                      <i className={`bi ${isCustomRotationEditMode ? "bi-x-circle" : "bi-pencil"} me-1`}></i>
                      {isCustomRotationEditMode ? "Cancel Edit" : "Edit Custom"}
                    </Button>
                  )}
                </div>
                {customRotations.length > 0 && (
                  <span className="text-muted small">
                    {customRotations.length} custom rotation{customRotations.length !== 1 ? 's' : ''} added
                  </span>
                )}
              </div>
              
              {showCustomRotationInput && (
                <div className="border rounded p-3 mb-3 bg-light">
                  <Row>
                    <Col md={8}>
                      <Form.Control
                        type="text"
                        placeholder="Enter custom rotation name..."
                        value={newCustomRotation}
                        onChange={(e) => setNewCustomRotation(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddCustomRotation();
                          }
                        }}
                      />
                    </Col>
                    <Col md={4}>
                      <div className="d-flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleAddCustomRotation}
                          disabled={!newCustomRotation.trim() || customRotationLoading}
                        >
                          {customRotationLoading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Adding...
                            </>
                          ) : (
                            'Add'
                          )}
                        </Button>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={handleCancelCustomRotation}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}
            </Form.Group>*/}

            {scheduleType === "blocks" && (
              <div className="mb-4">
                {/* Stepper moved inside each PGY accordion section */}
              </div>
            )}

            {false && (scheduleType === "blocks" && blockStep === 1) && (
            <>
              {programDetails?.specialty && (
                <div className="alert alert-info mb-3">
                  <i className="bi bi-hospital me-2"></i>
                  <strong>{programDetails.specialty}</strong> • {rotationTypes.length} rotation types available
                  {customRotations.length > 0 && (
                    <span className="ms-2">
                      <i className="bi bi-plus-circle me-1"></i>
                      {customRotations.length} custom rotation{customRotations.length !== 1 ? 's' : ''} added
                    </span>
                  )}
                </div>
              )}
            <Form.Group>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Select Rotations</h5>
                
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                {customRotations.length > 0 && (
                  <span className="text-muted small">
                    {customRotations.length} custom rotation{customRotations.length !== 1 ? 's' : ''} added
                  </span>
                )}
                 {customRotations.length > 0 && (
                    <Button
                      variant={isCustomRotationEditMode ? "danger" : "light"}
                      size="sm"
                      onClick={handleToggleCustomRotationEditMode}
                    >
                        <i className={`bi ${isCustomRotationEditMode ? "bi-x-circle" : "bi-pencil"}`}></i>
                        <span className=" d-none d-md-inline ms-1">{isCustomRotationEditMode ? "Cancel Edit" : "Edit Custom"}</span>
                    </Button>
                  )}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleShowCustomRotationInput}
                    disabled={showCustomRotationInput}
                  >
                    <i className="bi bi-plus"></i>  <span className=" d-none d-md-inline ms-1">Add Rotation</span>
                  </Button>

                </div>
        
              </div></div>
              {showCustomRotationInput && (
                <div className="border rounded p-3 mb-3 bg-light">
              <Row>
                    <Col md={8}>
                      <Form.Control
                        type="text"
                        placeholder="Enter custom rotation name..."
                        value={newCustomRotation}
                        onChange={(e) => setNewCustomRotation(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddCustomRotation();
                          }
                        }}
                      />
                    </Col>
                    <Col md={4}>
                      <div className="d-flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleAddCustomRotation}
                          disabled={!newCustomRotation.trim() || customRotationLoading}
                        >
                          {customRotationLoading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Adding...
                            </>
                          ) : (
                            'Add'
                          )}
                        </Button>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={handleCancelCustomRotation}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

             
              <Row>
                {rotationTypes.map((rotation) => (
                  <Col xs={12} sm={6} md={6} lg={4} key={rotation}>
                    <div className="position-relative" style={{ width: "100%" }}>
                      <Button
                        variant={
                          selectedRotations.includes(rotation)
                            ? "secondary mb-2"
                            : "outline-primary mb-2"
                        }
                        size="sm"
                        style={{
                          width: "100%",
                          padding: "10px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          backgroundColor: selectedRotations.includes(rotation)
                            ? "#f8f9fa"
                            : "", // A light grey
                          borderColor: selectedRotations.includes(rotation)
                            ? "#d3d6da"
                            : "",
                          color: selectedRotations.includes(rotation)
                            ? "#000"
                            : "",
                          borderStyle: selectedRotations.includes(rotation)
                            ? "solid"
                            : "dashed", // Adjust text color for contrast
                        }}
                        onClick={() => handleRotationSelect(rotation)}
                      >
                        <div className="d-flex align-items-center">
                        <Form.Check
                          type="checkbox"
                          id={rotation}
                          label={rotation}
                          checked={selectedRotations.includes(rotation)}
                          onChange={(e) => {
                            handleRotationSelect(rotation);
                          }}
                          style={{
                            height: "31px",
                            paddingTop: "5px",
                          }}
                        />
                          {isCustomRotationEditMode && customRotations.some(cr => cr.name === rotation) && (
                            <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.7rem' }}>
                              Custom
                            </span>
                          )}
                        </div>

                        {selectedRotations.includes(rotation) && scheduleType !== "blocks" && (
                          <Form.Select
                            size="sm"
                            className="py-1 px-2"
                            style={{ width: "80px" }}
                            value={rotationFrequency[rotation] || 1}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleFrequencyChange(
                                rotation,
                                parseInt(e.target.value)
                              );
                            }}
                          >
                            <option value="1">Once</option>
                            <option value="2">Twice</option>
                            <option value="3">Three Times</option>
                            <option value="4">Four Times</option>
                          </Form.Select>
                        )}
                      </Button>
                      
                      {/* X button for custom rotations in edit mode */}
                      {isCustomRotationEditMode && customRotations.some(cr => cr.name === rotation) && (
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute rounded-circle"
                          style={{
                            top: "-8px",
                            right: "-8px",
                            width: "28px",
                            height: "28px",
                            borderRadius: "50% !important",
                            padding: "0",
                            fontSize: "16px",
                            fontWeight: "bold",
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            border: "2px solid white"
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveCustomRotation(rotation);
                          }}
                          title="Remove custom rotation"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </Form.Group>
            </>
            )}

            {false && scheduleType === "blocks" && blockStep === 2 && (
              <>
                <div className="alert alert-info mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>Selected Rotations:</strong> {selectedRotations.length} rotation{selectedRotations.length !== 1 ? 's' : ''}
                      <br />
                      <small className="text-muted">
                        {selectedRotations.slice(0, 5).join(', ')}
                        {selectedRotations.length > 5 && ` + ${selectedRotations.length - 5} more`}
                      </small>
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setBlockStep(1)}
                    >
                      <i className="bi bi-arrow-left me-2"></i> Change Rotations
                    </Button>
                  </div>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-3">
                    <strong>Configure Block Rotations</strong>
                  </Form.Label>
                  <div className="text-muted mb-3">
                    Assign rotations to columns based on block pattern ({blockPattern}). Configure total weeks, min, and max block sizes for each rotation.
                  </div>
                  {(() => {
                    const totalWeeksInSchedule = startDate && endDate ? getWeeksBetween(startDate, endDate).length : 0;
                    const totalConfiguredWeeks = selectedRotations.reduce((sum, rotation) => {
                      const config = blockRotations[rotation];
                      return sum + (config?.totalWeeks || 0);
                    }, 0);
                    const remainingWeeks = totalWeeksInSchedule - totalConfiguredWeeks;
                    const isMatch = totalConfiguredWeeks === totalWeeksInSchedule;
                    
                    return (
                      <div className={`alert ${isMatch ? 'alert-success' : remainingWeeks > 0 ? 'alert-warning' : 'alert-danger'} mb-3`}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>Week Configuration Summary:</strong>
                            <br />
                            <div className="d-flex flex-row gap-3">
                            <div >
                              <small className="fw-bold text-muted me-2">Total Weeks:</small> <span className="text-muted">{totalWeeksInSchedule} weeks</span>
                            </div>
                            <div >
                              <small className="fw-bold text-muted me-2">Configured Weeks:</small> <span className="text-muted">{totalConfiguredWeeks} weeks</span>
                            </div>
                            </div>
                            {/*{!isMatch && (
                              <div className="d-flex flex-row gap-3">
                                <small className="fw-bold text-muted me-2">Remaining:</small> {remainingWeeks > 0 ? `${remainingWeeks} weeks to configure` : `${Math.abs(remainingWeeks)} weeks over configured`}
                              </div>
                            )}*/}
                          </div>
                          <div>
                            {isMatch ? (
                              <span className="badge bg-success">
                                <i className="bi bi-check-circle me-1"></i> Complete
                              </span>
                            ) : remainingWeeks > 0 ? (
                              <span className="badge bg-warning text-dark">
                                <i className="bi bi-exclamation-triangle me-1"></i> {remainingWeeks} weeks remaining
                              </span>
                            ) : (
                              <span className="badge bg-danger">
                                <i className="bi bi-x-circle me-1"></i> {Math.abs(remainingWeeks)} weeks over
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                {(() => {
                  const [majorWeeks, minorWeeks] = blockPattern.split('-').map(Number);
                  return (
                    <div className="border rounded-2">
                      <Table responsive className="mb-0">
                        <thead>
                          <tr className="custom-header-bg">
                            <th className="ps-3">Rotation</th>
                            <th>Column</th>
                            <th>Total Weeks</th>
                            <th>Min Block Size</th>
                            <th>Max Block Size</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedRotations.map((rotation) => {
                            // Calculate default min/max based on column
                            const getDefaultConfig = (column) => {
                              if (column === 'major') {
                                // Major: min = 2, max = majorWeeks (e.g., 4 for 4-1 pattern)
                                return { column: 'major', totalWeeks: 0, minBlockSize: 2, maxBlockSize: majorWeeks };
                              } else {
                                // Minor: min = minorWeeks, max = minorWeeks (e.g., 1 for 4-1, 2 for 4-2)
                                return { column: 'minor', totalWeeks: 0, minBlockSize: minorWeeks, maxBlockSize: minorWeeks };
                              }
                            };
                            const config = blockRotations[rotation] || getDefaultConfig('major');
                            return (
                              <tr key={`block-${rotation}`}>
                                <td className="ps-3">
                                  <strong>{rotation}</strong>
                                </td>
                                <td>
                                  <Form.Select
                                    size="sm"
                                    value={config.column}
                                    onChange={(e) => {
                                      const newColumn = e.target.value;
                                      const [majorWeeks, minorWeeks] = blockPattern.split('-').map(Number);
                                      // Major: min = 2, max = majorWeeks
                                      // Minor: min = minorWeeks, max = minorWeeks
                                      const newMinBlockSize = newColumn === 'major' ? 2 : minorWeeks;
                                      const newMaxBlockSize = newColumn === 'major' ? majorWeeks : minorWeeks;
                                      setBlockRotations({
                                        ...blockRotations,
                                        [rotation]: {
                                          ...config,
                                          column: newColumn,
                                          minBlockSize: newMinBlockSize,
                                          maxBlockSize: newMaxBlockSize,
                                        }
                                      });
                                    }}
                                  >
                                    <option value="major">{majorWeeks} Week Column</option>
                                    <option value="minor">{minorWeeks} Week Column</option>
                                  </Form.Select>
                                </td>
                                <td>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    min="0"
                                    value={config.totalWeeks || 0}
                                    onChange={(e) => {
                                      setBlockRotations({
                                        ...blockRotations,
                                        [rotation]: {
                                          ...config,
                                          totalWeeks: parseInt(e.target.value) || 0,
                                        }
                                      });
                                    }}
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    min="1"
                                    value={config.minBlockSize || (config.column === 'major' ? 2 : minorWeeks)}
                                    onChange={(e) => {
                                      setBlockRotations({
                                        ...blockRotations,
                                        [rotation]: {
                                          ...config,
                                          minBlockSize: parseInt(e.target.value) || 1,
                                        }
                                      });
                                    }}
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    min="1"
                                    value={config.maxBlockSize || (config.column === 'major' ? majorWeeks : minorWeeks)}
                                    onChange={(e) => {
                                      setBlockRotations({
                                        ...blockRotations,
                                        [rotation]: {
                                          ...config,
                                          maxBlockSize: parseInt(e.target.value) || 1,
                                        }
                                      });
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  );
                })()}
                </Form.Group>
              </>
            )}

          </Form>
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button
            variant="light"
            size="sm"
            onClick={() => {
              setShowModal(false);
              setError("");
              resetScheduleConfigState();
            }}
            disabled={generatingSchedule}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            size="sm"
            onClick={(e) => {
              console.log('[Generate Schedule] Button clicked', { scheduleType, blockStep, generatingSchedule });
              e.preventDefault();
              e.stopPropagation();
              generateSchedule();
            }} 
            disabled={generatingSchedule}
          >
          {generatingSchedule ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Generating...
            </>
          ) : (
            'Generate Schedule'
          )}
        </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteConfirmModal}
        onHide={() => setShowDeleteConfirmModal(false)}
      >
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-50"> <div className="d-flex flex-column justify-content-start align-items-start">
            <span>Confirm Delete</span>
            <div className="text-hl-danger"></div>
          </div>
        </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          Are you sure you want to delete the PGY-{scheduleToDelete} schedule?
          This will also remove all associated calendar events.
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button
            variant="light"
            size="sm"
            onClick={() => setShowDeleteConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showClearConfirmModal}
        onHide={() => setShowClearConfirmModal(false)}
      >
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-50"> <div className="d-flex flex-column justify-content-start align-items-start">
            <span>Confirm Clear All</span>
            <div className="text-hl-danger"></div>
          </div>
        </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          Are you sure you want to clear all schedules? This will remove all
          rotation schedules and their associated calendar events.
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button
            variant="light"
            size="sm"
            onClick={() => setShowClearConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={handleConfirmClear}>
            Delete All
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Navigation Warning Modal */}
      <NavigationWarningModal
        show={showNavigationWarning}
        onHide={handleCancelNavigation}
        onSaveAndLeave={handleSaveAndLeave}
        onLeaveWithoutSaving={handleLeaveWithoutSaving}
        isTimed={false}
        timeLeft={0}
        saveButtonLabel="Save Changes"
        message={`You are in ${isRotationEditMode ? 'rotation schedule' : 'weekend call schedule'} edit mode. Navigating away will exit edit mode.`}
        showSaveButton={false}
      />

      {/* Weekend Call Clear Confirmation Modal */}
      <Modal
        show={showWeekendClearConfirmModal}
        onHide={() => {
          setShowWeekendClearConfirmModal(false);
          setClearConfirmData(null);
        }}
      >
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-50">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Confirm Clear</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          {clearConfirmData?.type === 'day' ? (
            <p>
              Are you sure you want to clear all residents from this weekend day? 
              This will remove all assignments for {clearConfirmData.rotation} on this date.
            </p>
          ) : clearConfirmData?.type === 'month' ? (
            <p>
              Are you sure you want to clear all weekend call assignments for {clearConfirmData.rotation} 
              for the entire month? This will remove all assignments for this rotation.
            </p>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button
            variant="light"
            size="sm"
            onClick={() => {
              setShowWeekendClearConfirmModal(false);
              setClearConfirmData(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            size="sm"
            onClick={() => {
              if (clearConfirmData?.type === 'day') {
                confirmClearWeekendDay();
              } else if (clearConfirmData?.type === 'month') {
                confirmClearMonth();
              }
            }}
          >
            Clear
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pager Numbers Modal */}
      <Modal
        show={showPagerModal}
        onHide={handleCancelPagerModal}
        size="lg"
      >
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-50">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Manage Pager Numbers</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}
          <div className="mb-3">
            <Alert className="alert-info">
              Add pager numbers for each rotation. These will be displayed in the daily schedule view.
            </Alert>
          </div>
          <Row>
            {Object.keys(pagerNumbers).map((rotation) => (
              <Col xs={12} sm={6} md={4} key={rotation} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-semibold d-flex align-items-center gap-1">
                    {rotation}
                    {pagerNumbers[rotation] && /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(pagerNumbers[rotation]) && (
                      <i className="bi bi-check-circle-fill text-success ms-1" title="Valid phone number"></i>
                    )}
                  </Form.Label>

                                     <Form.Control
                     type="text"
                     placeholder="Phone Number"
                     value={pagerNumbers[rotation] || ""}
                     onChange={(e) => handlePagerNumberChange(rotation, e.target.value)}
                     maxLength={14}
                     className={pagerNumbers[rotation] && !/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(pagerNumbers[rotation]) ? "is-invalid" : ""}
                   />
                </Form.Group>
              </Col>
            ))}
          </Row>
          {Object.keys(pagerNumbers).length === 0 && (
            <div className="text-center py-4">
              <div className="d-flex flex-column align-items-center gap-2">
                <i className="bi bi-telephone-x text-muted" style={{ fontSize: '3rem' }}></i>
                <h6>No rotations available</h6>
                <p className="text-muted">
                  Create rotation schedules first to add pager numbers
                </p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button
            variant="light"
            size="sm"
            onClick={handleCancelPagerModal}
            disabled={pagerModalLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSavePagerNumbers}
            disabled={pagerModalLoading}
          >
            {pagerModalLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Pager Numbers'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Off-Service Residents Modal */}
      <Modal
        show={showOffServiceModal}
        onHide={() => {
          setShowOffServiceModal(false);
          const defaultEntry = {
            residentName: '',
            month: '',
            rotationName: '',
            weekBlock: '',
            blockIndex: '',
            rotations: { major: '', minor: '' }
          };
          setOffServiceEntries([defaultEntry]);
          setSelectedPgyForOffService(null);
          setEditingOffServiceResident(null);
          setError("");
        }}
        size="lg"
      >
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-50">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>{editingOffServiceResident === 'all' ? 'Edit Off Service Residents' : 'Add Off Service Residents'}</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}
          {(() => {
            const scheduleData = schedules[selectedPgyForOffService];
            const scheduleType = scheduleData?.scheduleType || 'monthly';
            const availableMonths = selectedPgyForOffService && schedules[selectedPgyForOffService]
              ? getMonthsBetween(schedules[selectedPgyForOffService].startDate, schedules[selectedPgyForOffService].endDate)
              : [];
            
            // Get weekly groups for weekly schedules
            const weeklyGroups = scheduleType === 'weekly' && scheduleData
              ? getWeeklyGroups(scheduleData.startDate, scheduleData.endDate, scheduleData.weekDuration || weekDuration || 1)
              : [];
            
            // Get blocks for block schedules
            const blockPatternValue = scheduleType === 'blocks' && scheduleData
              ? (scheduleData.blockPattern || blockPattern || '4-1')
              : '4-1';
            const [majorWeeks, minorWeeks] = blockPatternValue.split('-').map(Number);
            const blockDurationValue = scheduleType === 'blocks' && scheduleData
              ? (scheduleData.blockDuration || blockDuration || 5)
              : 5;
            const weeks = scheduleType === 'blocks' && scheduleData
              ? getWeeksBetween(scheduleData.startDate, scheduleData.endDate)
              : [];
            const totalBlocks = scheduleType === 'blocks' ? Math.ceil(weeks.length / blockDurationValue) : 0;
            const allBlocks = [];
            if (scheduleType === 'blocks') {
              for (let i = 0; i < weeks.length; i += blockDurationValue) {
                const blockWeeks = weeks.slice(i, i + blockDurationValue);
                if (blockWeeks.length > 0) {
                  const weekStart = Math.floor((i / blockDurationValue) * blockDurationValue) + 1;
                  const weekEnd = Math.min(weekStart + blockDurationValue - 1, weeks.length);
                  allBlocks.push({
                    index: Math.floor(i / blockDurationValue) + 1,
                    start: blockWeeks[0].start,
                    end: blockWeeks[blockWeeks.length - 1].end,
                    label: `Block ${Math.floor(i / blockDurationValue) + 1} (Weeks ${weekStart}-${weekEnd})`
                  });
                }
              }
            }
            
            // Get block rotations configuration
            const blockRotationsConfig = scheduleType === 'blocks' && scheduleData?.blockRotations
              ? scheduleData.blockRotations
              : {};
            const majorRotations = Object.keys(blockRotationsConfig).filter(rot => blockRotationsConfig[rot]?.column === 'major');
            const minorRotations = Object.keys(blockRotationsConfig).filter(rot => blockRotationsConfig[rot]?.column === 'minor');
            
            return (
              <>
          <div className="mb-3">
            <Alert className="alert-info">
                    {editingOffServiceResident === 'all'
                      ? `Edit off-service residents for PGY-${selectedPgyForOffService}. Remove entries to delete residents. You can remove all entries to delete all off-service residents for this PGY level.`
                      : scheduleType === 'monthly'
                        ? `Add off-service residents for PGY-${selectedPgyForOffService}. These residents will be displayed in the schedule.Multiple residents with the same month and rotation will be grouped together.`
                        : scheduleType === 'weekly'
                          ? `Add off-service residents for PGY-${selectedPgyForOffService}. Select a week block and rotation. Multiple residents with the same week block and rotation will be grouped together.`
                          : `Add off-service residents for PGY-${selectedPgyForOffService}. Select a block and rotations (major and/or minor). Multiple residents with the same block and rotations will be grouped together.`
              }
            </Alert>
          </div>
          
                {offServiceEntries.map((entry, index) => (
              <div key={entry.id || index} className="mb-3 p-3 border rounded-2 bg-lighter">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0 fw-bold">Off Service Resident {index + 1}</h6>
                      {(offServiceEntries.length > 1 || editingOffServiceResident === 'all') && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="py-0 px-1"
                      onClick={() => {
                        setOffServiceEntries(prev => prev.filter((_, i) => i !== index));
                      }}
                    >
                      <i className="bi bi-trash"></i> Remove
                    </Button>
                  )}
                </div>
                <Row>
                      <Col md={scheduleType === 'blocks' ? 6 : 4}>
                    <Form.Group>
                      <FloatingLabel label="Resident Name">
                      <Form.Control
                        type="text"
                        placeholder="Enter resident name"
                              value={entry.residentName || ''}
                        onChange={(e) => {
                          const newEntries = [...offServiceEntries];
                          newEntries[index].residentName = e.target.value;
                          setOffServiceEntries(newEntries);
                        }}
                      />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                      
                      {scheduleType === 'monthly' && (
                        <>
                  <Col md={4}>
                    <Form.Group>
                      <FloatingLabel label="Month">
                      <Form.Select
                                  value={entry.month || ''}
                        onChange={(e) => {
                          const newEntries = [...offServiceEntries];
                          newEntries[index].month = e.target.value;
                          setOffServiceEntries(newEntries);
                        }}
                      >
                        <option value="">Select month</option>
                        {availableMonths.map((monthStr) => {
                          const [year, monthNum] = monthStr.split("-");
                          const date = new Date(year, parseInt(monthNum) - 1);
                          const monthName = date.toLocaleString("default", { month: "long" });
                          return (
                            <option key={monthStr} value={monthStr}>
                              {monthName} {year}
                            </option>
                          );
                        })}
                      </Form.Select>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <FloatingLabel label="Rotation">
                      <Form.Select
                                  value={entry.rotationName || ''}
                        onChange={(e) => {
                          const newEntries = [...offServiceEntries];
                          newEntries[index].rotationName = e.target.value;
                          setOffServiceEntries(newEntries);
                        }}
                                >
                                  <option value="">Select rotation</option>
                                  {rotationTypes.map((rotation) => (
                                    <option key={rotation} value={rotation}>
                                      {rotation}
                                    </option>
                                  ))}
                                </Form.Select>
                              </FloatingLabel>
                            </Form.Group>
                          </Col>
                        </>
                      )}
                      
                      {scheduleType === 'weekly' && (
                        <>
                          <Col md={4}>
                            <Form.Group>
                              <FloatingLabel label="Week Block">
                                <Form.Select
                                  value={entry.weekBlock || ''}
                                  onChange={(e) => {
                                    const newEntries = [...offServiceEntries];
                                    newEntries[index].weekBlock = e.target.value;
                                    setOffServiceEntries(newEntries);
                                  }}
                                >
                                  <option value="">Select week block</option>
                                  {weeklyGroups.map((group) => {
                                    const startDateObj = new Date(group.start + 'T00:00:00');
                                    const endDateObj = new Date(group.end + 'T00:00:00');
                                    const label = `${startDateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${endDateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
                                    return (
                                      <option key={group.start} value={group.start}>
                                        {label}
                                      </option>
                                    );
                                  })}
                                </Form.Select>
                              </FloatingLabel>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <FloatingLabel label="Rotation">
                                <Form.Select
                                  value={entry.rotationName || ''}
                                  onChange={(e) => {
                                    const newEntries = [...offServiceEntries];
                                    newEntries[index].rotationName = e.target.value;
                                    setOffServiceEntries(newEntries);
                                  }}
                      >
                        <option value="">Select rotation</option>
                        {rotationTypes.map((rotation) => (
                          <option key={rotation} value={rotation}>
                            {rotation}
                          </option>
                        ))}
                      </Form.Select>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                        </>
                      )}
                      
                      {scheduleType === 'blocks' && (
                        <>
                          <Col md={6}>
                            <Form.Group>
                              <FloatingLabel label="Block">
                                <Form.Select
                                  value={entry.blockIndex || ''}
                                  onChange={(e) => {
                                    const newEntries = [...offServiceEntries];
                                    newEntries[index].blockIndex = e.target.value;
                                    setOffServiceEntries(newEntries);
                                  }}
                                >
                                  <option value="">Select block</option>
                                  {allBlocks.map((block) => (
                                    <option key={block.index} value={block.index.toString()}>
                                      {block.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </FloatingLabel>
                            </Form.Group>
                          </Col>
                          {majorRotations.length > 0 && (
                            <Col md={6}>
                              <Form.Group>
                                <FloatingLabel label={`Major Rotation (${majorWeeks} weeks)`}>
                                  <Form.Select
                                    value={entry.rotations?.major || ''}
                                    onChange={(e) => {
                                      const newEntries = [...offServiceEntries];
                                      if (!newEntries[index].rotations) {
                                        newEntries[index].rotations = { major: '', minor: '' };
                                      }
                                      newEntries[index].rotations.major = e.target.value;
                                      setOffServiceEntries(newEntries);
                                    }}
                                  >
                                    <option value="">Select major rotation (optional)</option>
                                    {majorRotations.map((rotation) => (
                                      <option key={rotation} value={rotation}>
                                        {rotation}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </FloatingLabel>
                              </Form.Group>
                            </Col>
                          )}
                          {minorRotations.length > 0 && (
                            <Col md={6}>
                              <Form.Group>
                                <FloatingLabel label={`Minor Rotation (${minorWeeks} week${minorWeeks !== 1 ? 's' : ''})`}>
                                  <Form.Select
                                    value={entry.rotations?.minor || ''}
                                    onChange={(e) => {
                                      const newEntries = [...offServiceEntries];
                                      if (!newEntries[index].rotations) {
                                        newEntries[index].rotations = { major: '', minor: '' };
                                      }
                                      newEntries[index].rotations.minor = e.target.value;
                                      setOffServiceEntries(newEntries);
                                    }}
                                  >
                                    <option value="">Select minor rotation (optional)</option>
                                    {minorRotations.map((rotation) => (
                                      <option key={rotation} value={rotation}>
                                        {rotation}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </FloatingLabel>
                              </Form.Group>
                            </Col>
                          )}
                        </>
                      )}
                </Row>
              </div>
                ))}
              </>
            );
          })()}
          
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              const defaultEntry = {
                residentName: '',
                month: '',
                rotationName: '',
                weekBlock: '',
                blockIndex: '',
                rotations: { major: '', minor: '' }
              };
              setOffServiceEntries([...offServiceEntries, defaultEntry]);
            }}
          >
            <i className="bi bi-plus-lg me-1"></i> Add Another Entry
          </Button>
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button
            variant="light"
            size="sm"
            onClick={() => {
              setShowOffServiceModal(false);
              const defaultEntry = {
                residentName: '',
                month: '',
                rotationName: '',
                weekBlock: '',
                blockIndex: '',
                rotations: { major: '', minor: '' }
              };
              setOffServiceEntries([defaultEntry]);
              setSelectedPgyForOffService(null);
              setEditingOffServiceResident(null);
              setError("");
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            size="sm"
            onClick={editingOffServiceResident === 'all' ? handleSaveAllOffServiceResidents : handleSaveOffServiceResidents}
          >
            {editingOffServiceResident === 'all' ? 'Update All' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RotationSchedulerPage;
